export default {
  cvHealth: {
    title: "CV Health",
    heart: {
      a11y: "CV Health, score {{score}} out of 100. Open review.",
      a11yUnavailable: "CV Health unavailable. Open review.",
    },
    areas: {
      human: {
        label: "Recruiters",
        purpose: "Can someone quickly understand your experience?",
        allClear: "Recruiters can quickly spot your role and key skills.",
        tab: "Recruiters",
      },
      system: {
        label: "Hiring systems",
        purpose: "Can software read the text and links in your file?",
        allClear: "Clear headings and contact links help hiring systems find your details.",
        tab: "Systems",
      },
      ai_bot: {
        label: "AI tools",
        purpose: "Is your experience clear enough for AI tools to interpret?",
        allClear: "Full skill names and examples help AI tools understand your experience.",
        tab: "AI",
      },
    },
    stages: {
      reading: "Reading your CV…",
      sections_links: "Checking sections and links…",
      skills_experience: "Checking skills and clarity…",
      preparing: "Preparing your results…",
      ready: "Your CV Health is ready",
      failed: "We couldn't review this CV.",
      writing: "Writing your CV…",
      fixing: "Fixing your CV…",
    },
    heading: {
      suggestions_one: "1 thing to improve",
      suggestions_other: "{{count}} things to improve",
      looksGood: "Your CV looks good",
      partial: "Some checks couldn't finish.",
      unreadable: "We couldn't read enough of this CV.",
      unreadableBody: "Try another file, or continue and build it from what we have.",
      areaUnavailable: "We couldn't finish these checks this time.",
      fixed_one: "Fixed {{count}} thing",
      fixed_other: "Fixed {{count}} things",
      scoreUpFrom: "Up from {{score}}",
      scoreDownFrom: "Down from {{score}}",
      skippedFixes_one: "{{count}} suggestion could not be applied because your CV changed.",
      skippedFixes_other: "{{count}} suggestions could not be applied because your CV changed.",
      fixFailed: "We couldn't apply these changes. Try again.",
      undoFailed: "We couldn't undo these changes. Try again.",
    },
    badges: {
      suggestions_one: "{{count}} suggestion",
      suggestions_other: "{{count}} suggestions",
      looksGood: "Looks good",
      checkDetail: "Check a detail",
      unavailable: "Couldn't check",
      checking: "Checking…",
      cvChanged: "CV changed",
      optional: "Optional",
    },
    rings: {
      score:
        "CV Health score {{score}} out of 100. Recruiters {{humanPassed}} of {{humanEligible}}, Hiring systems {{systemPassed}} of {{systemEligible}}, AI tools {{aiPassed}} of {{aiEligible}}.",
      progress: "Checking your CV, {{percent}} percent complete.",
      unavailable: "CV Health score not available.",
    },
    checks: {
      human_headline_present: {
        name: "Role at the top",
        title: "Add your role at the top",
        why: "A clear role under your name tells a recruiter what you do before they read on.",
        change: "We'll ask which role you want to be known for and place it under your name.",
        agent: "Let's put your role right under your name.",
        plain: "Your CV doesn't say what your job is at the top.",
        plainFix: "We'll add your job title under your name.",
      },
      human_summary_present: {
        name: "Short summary",
        title: "Add a short summary",
        why: "A few lines at the top help a recruiter see your focus and strengths quickly.",
        change: "We'll suggest a short summary built only from the experience already in your CV.",
        agent: "Let's add a short summary so your focus is clear.",
        plain: "Your CV has no short intro.",
        plainFix: "We'll write a short intro from your experience.",
      },
      human_summary_specific: {
        name: "Specific summary",
        title: "Make your summary more specific",
        why: "A summary that names your role and real skills is easier to remember than general praise.",
        change: "The new summary keeps your facts and names your role and key skills.",
        agent: "Let's make your summary say what you actually do.",
        plain: "Your intro is generic. It doesn't say what you actually do.",
        plainFix: "We'll rewrite it around your real role and skills.",
      },
      human_recent_role_detail: {
        name: "Latest role described",
        title: "Describe your latest role",
        why: "Your most recent role is usually read first, so a few lines about it go a long way.",
        change: "We'll ask what you did in this role and add it as a short description.",
        agent: "Let's add a few lines about your latest role.",
        plain: "Your latest job has no description.",
        plainFix: "Add two or three lines about what you did there.",
      },
      human_action_openers: {
        name: "Bullets start with actions",
        title: "Start this bullet with what you did",
        why: "Bullets that open with an action are quicker to scan and easier to follow.",
        change: "The new bullet keeps the same facts and opens with what you did.",
        agent: "Let's start this bullet with what you did.",
        plain: "One bullet starts with 'Responsible for' instead of what you did.",
        plainFix: "We'll rewrite it to start with the action.",
      },
      human_bullets_concise: {
        name: "Easy-to-scan bullets",
        title: "Make this bullet easier to scan",
        why: "Shorter bullets let a reader pick out your work without rereading.",
        change: "The new bullet says the same thing in fewer words.",
        agent: "Let's tighten this bullet so it reads at a glance.",
        plain: "One bullet is too long to read quickly.",
        plainFix: "We'll shorten it, same facts.",
      },
      human_chronology_consistent: {
        name: "Latest role first",
        title: "Put your latest role first",
        why: "Readers expect your most recent work at the top of your experience.",
        change: "We'll ask you to confirm which role is the most recent before anything moves.",
        agent: "Let's make sure your latest role comes first.",
        plain: "Your jobs aren't in order, newest first.",
        plainFix: "Move your latest job to the top.",
      },
      human_personal_details: {
        name: "Personal details",
        title: "Keep your CV focused on your work",
        why: "Details such as date of birth or marital status aren't needed to understand your experience.",
        change: "You decide whether to leave these details out of your new CV. Nothing is removed on its own.",
        agent: "Let's check which personal details you want to share.",
        plain: "Your CV shows personal details employers don't need, like a date of birth.",
        plainFix: "You choose whether to leave them out.",
      },
      human_impact_clarity: {
        name: "Results of your work",
        title: "Show the result of your work",
        why: "A result shows what changed because of your work, which makes it easier to value.",
        change: "Add what changed because of it: a number, a saving, a deadline met or a decision it led to. Only you know the result, so this is never rewritten for you.",
        agent: "Let's make the result of this work easier to see.",
        plain: "One bullet says what you did, but not the result.",
        plainFix: "Add the result only you know.",
      },
      system_email_valid: {
        name: "Email address",
        title: "Add an email address",
        why: "Hiring systems look for an email in your contact details so employers can reply.",
        change: "We'll ask for the email address you want employers to use.",
        agent: "Let's add an email address employers can reach you on.",
        plain: "Your CV has no valid email address.",
        plainFix: "Add an email so employers can reply.",
      },
      system_phone_valid: {
        name: "Phone format",
        title: "Use an international phone format",
        why: "A country code helps software store your number correctly and lets employers abroad call you.",
        change: "The new number keeps your digits and adds the country code.",
        agent: "Let's write your phone number with its country code.",
        plain: "Your phone number isn't in international format.",
        plainFix: "Write it like +971 50 123 4567.",
      },
      system_role_fields_complete: {
        name: "Title and company",
        title: "Add the missing title or company",
        why: "Hiring systems file each role by its title and company, so a missing one leaves a gap.",
        change: "We'll ask for the missing title or company for this role.",
        agent: "Let's fill in the missing title or company.",
        plain: "One job is missing its title or company.",
        plainFix: "Fill in the missing part.",
      },
      system_role_dates_complete: {
        name: "Role dates",
        title: "Add dates to this role",
        why: "Dates let software work out how long you held each role.",
        change: "We'll ask when you started and, if it has ended, when you left.",
        agent: "Let's add dates to this role.",
        plain: "One job is missing its dates.",
        plainFix: "Add when you started and finished.",
      },
      system_dates_consistent: {
        name: "Consistent dates",
        title: "Check one date",
        why: "An end date before a start date can make software misread your timeline.",
        change: "We'll show the date as written and ask you to confirm or correct it.",
        agent: "Let's double check one date.",
        plain: "One date doesn't add up.",
        plainFix: "Check the start and end dates.",
      },
      system_skills_discrete: {
        name: "Separate skills",
        title: "Split a long skill entry",
        why: "Software matches skills one at a time, so a long combined entry can be missed.",
        change: "The long entry becomes separate skills that keep your wording.",
        agent: "Let's split this entry into separate skills.",
        plain: "One skill entry is a whole sentence, so software can't match it.",
        plainFix: "We'll split it into separate skills.",
      },
      system_links_valid: {
        name: "Working links",
        title: "Fix one link",
        why: "A link with a typo can't be opened by software or by the person reading your CV.",
        change: "The fixed link keeps the same address with the typo corrected.",
        agent: "Let's fix this link so it opens.",
        plain: "One of your links is broken.",
        plainFix: "We'll fix the link.",
      },
      system_source_parse_complete: {
        name: "File fully read",
        title: "Part of your file may not have been read",
        why: "Some content in your file couldn't be read as text, so hiring systems may miss it too.",
        change: "Nothing changes on its own. You can check the missing part and add it in the editor.",
        agent: "Let's check whether anything from your file is missing.",
        plain: "Part of your file couldn't be read.",
        plainFix: "Check that every section below is complete.",
      },
      ai_bot_skills_present: {
        name: "Key skills listed",
        title: "List your key skills",
        why: "A clear skills list helps AI tools understand what you can do.",
        change: "We'll suggest skills already mentioned in your experience for you to confirm.",
        agent: "Let's add your key skills.",
        plain: "Your CV lists fewer than three skills.",
        plainFix: "We'll add skills from your experience.",
      },
      ai_bot_skills_evidenced: {
        name: "Skills shown in your work",
        title: "Show where you used your skills",
        why: "AI tools give more weight to skills that also appear in your experience.",
        change: "The new bullet names a listed skill where you already used it.",
        agent: "Let's show where you used your skills.",
        plain: "Some skills you list never appear in your experience.",
        plainFix: "Add an example of where you used them.",
      },
      ai_bot_role_identity_consistent: {
        name: "Headline matches latest role",
        title: "Match your headline to your latest role",
        why: "When your headline and latest role agree, AI tools read your profile as one clear story.",
        change: "We'll ask which title you want to lead with and use it in your headline.",
        agent: "Let's make your headline and latest role agree.",
        plain: "Your headline and your latest job title don't match.",
        plainFix: "We'll use your latest title.",
      },
      ai_bot_experience_span_consistent: {
        name: "Years of experience",
        title: "Check the years of experience in your summary",
        why: "If your summary and your role dates suggest different totals, AI tools may pick the wrong one.",
        change: "We'll show both figures and ask which one is right.",
        agent: "Let's check the years of experience in your summary.",
        plain: "Your intro says a different number of years than your jobs add up to.",
        plainFix: "We'll correct the number.",
      },
      ai_bot_claims_supported: {
        name: "Claims backed by experience",
        title: "Back up one claim in your summary",
        why: "A claim that your experience supports is easier for AI tools and people to trust.",
        change: "The new summary keeps the claim and ties it to work already in your CV.",
        agent: "Let's back up this claim with your experience.",
        plain: "One claim in your intro isn't backed by your experience.",
        plainFix: "Check it, or remove it.",
      },
      ai_bot_professional_proof: {
        name: "Professional link",
        title: "Add a professional link",
        why: "A LinkedIn profile, portfolio or GitHub page gives readers more of your work to look at.",
        change: "We'll add the link you choose to your contact details.",
        agent: "Let's add a professional link, if you have one.",
        plain: "No LinkedIn, portfolio or GitHub link yet.",
        plainFix: "Add one if you have it. Optional.",
      },
      status: {
        passed: "Passed",
        failed: "Needs attention",
        needs_review: "Check a detail",
        not_applicable: "Not applicable",
        unavailable: "Couldn't check",
      },
    },
    simple: {
      scoreHeading: "Your CV scores {{score}} out of 100",
      fixesSub_one: "{{count}} quick fix will make it stronger.",
      fixesSub_other: "{{count}} quick fixes will make it stronger.",
      noFixes: "Nothing to fix. Nice work.",
      // A row "Fix my CV" will not apply (it needs the user's OK or detail):
      // never a "We'll…" promise.
      yourCall: "Your call. Review it anytime in your CV.",
      fixButton: "Fix my CV with AI",
      continueWithoutFixing: "Continue without fixing",
      fixingLine: "Fixing your CV…",
      doneHeading: "Done. Your CV went from {{before}} to {{after}}.",
      doneSub_one: "We fixed {{count}} thing. You can undo this.",
      doneSub_other: "We fixed {{count}} things. You can undo this.",
      fixFailed: "We couldn't fix your CV this time.",
      nowLabel: "Now",
      afterLabel: "After the fix",
      weAskLabel: "What we'll ask you",
      undo: "Undo",
      checkingHeading: "Checking your CV",
      editorHeading: "Your CV scores {{score}} out of 100",
      editorFixesSub_one: "{{count}} quick fix will make it stronger.",
      editorFixesSub_other: "{{count}} quick fixes will make it stronger.",
      editorNoFixes: "Nothing to fix right now.",
    },
    report: {
      gaugeLabel: "CV Health score",
      gaugeValue: "{{score}} out of 100",
      allPassed: "All checks passed",
      reviewSuggestions_one: "Review {{count}} suggestion",
      reviewSuggestions_other: "Review {{count}} suggestions",
      resumeReview: "Continue reviewing",
      viewSuggestions: "View individual suggestions",
      whatWeChecked: "What we checked",
      looksGood: "Looks good",
      improvements_one: "{{count}} improvement",
      improvements_other: "{{count}} improvements",
      couldntCheck: "Couldn't check",
      basedOn_one: "Based on {{count}} check of your CV's text and links. Not a hiring prediction.",
      basedOn_other: "Based on {{count}} checks of your CV's text and links. Not a hiring prediction.",
      overview: "Overview",
      areasLabel: "CV Health areas",
      checksInArea: "Checks in this area",
      coverage: "Based on your CV's text and links. File layout, scanned pages and job matching aren't included. This score isn't a hiring prediction.",
      previewChanges_one: "Preview change",
      previewChanges_other: "Preview {{count}} changes",
      optional: "Optional",
      proofTitle: "Show examples of your work",
      proofBody: "GitHub, LinkedIn or a portfolio can help. One relevant link is enough.",
      proofAction: "Add a work link",
      proofNote: "Your score is unchanged if you skip this.",
      changesSaved_one: "{{count}} change saved",
      changesSaved_other: "{{count}} changes saved",
      undone: "Changes undone",
      checkingChanges: "Checking your changes…",
      recheckFailed_one: "{{count}} change saved. We couldn't check your CV again.",
      recheckFailed_other: "{{count}} changes saved. We couldn't check your CV again.",
      undoSkipped_one: "{{count}} change couldn't be undone because your CV changed since.",
      undoSkipped_other: "{{count}} changes couldn't be undone because your CV changed since.",
    },
    review: {
      heading: "Your changes are ready",
      cvUpdated: "Your CV was rewritten, so these are the changes for the new version. Keep the ones you like.",
      lead: "Keep the changes you like. Edit anything.",
      compare: "Compare & edit",
      original: "Original",
      suggested: "Suggested",
      selected_one: "{{count}} selected · Original kept",
      selected_other: "{{count}} selected · Original kept",
      save_one: "Save {{count}} change",
      save_other: "Save {{count}} changes",
      useInCv: "Use in my new CV",
      keepOriginal: "Keep original",
      saving: "Saving your changes…",
      saveFailed: "We couldn't save these changes. Try again.",
      invalidLink: "One of the links doesn't look right. Check it and try again.",
      empty: "Write something, or untick this change.",
      others_one: "{{count}} more suggestion needs details only you have.",
      others_other: "{{count}} more suggestions need details only you have.",
      seeOthers: "Show in the list",
    },
    checkSteps: {
      reading: "Writing and experience",
      sections_links: "Sections and contact details",
      skills_experience: "Skills and clarity",
      preparing: "Your results",
      progressLabel: "Checks completed",
      unchanged: "Your original CV stays unchanged.",
      cancel: "Cancel check",
      slowHint: "The last checks can take a little longer.",
      failedLead: "Your CV is unchanged. Try the check again.",
      cancelledTitle: "Check cancelled",
      cancelledLead: "Your CV is unchanged. Start again when you're ready.",
      checkMyCv: "Check my CV",
    },
    about: {
      title: "About these checks",
    },
    notChecked: {
      title: "Not checked in this version",
      layout: "Page layout and columns",
      fonts: "Fonts and sizes",
      contrast: "Colour contrast",
      ocr: "Scanned or image-only pages",
      spelling: "Spelling",
      job_match: "Match to a specific job",
    },
    actions: {
      fixWithAi_one: "Fix {{count}} thing with AI",
      fixWithAi_other: "Fix {{count}} things with AI",
      continueWithoutFixing: "Continue without fixing",
      undo: "Undo",
      previewChange: "Preview change",
      seeChange: "See the change",
      leaveUnchanged: "Leave this unchanged",
      skipForNow: "Skip for now",
      looksRight: "Looks right to me",
      useSuggestion: "Use suggestion",
      leaveOut: "Leave out of new CV",
      keepIt: "Keep it",
      addProfessionalLink: "Add a professional link",
      noProfessionalLink: "I don't have one",
      useFixedLink: "Use the fixed link",
      saveChange: "Save change",
      keepOriginal: "Keep original",
      original: "Original",
      suggested: "Suggested",
      nothingYet: "Nothing yet",
      editHint: "You can edit this before saving.",
      inYourCv: "In your CV",
      sectionLabels: {
        header: "Header",
        contact: "Contact details",
        summary: "Summary",
        experience: "Experience",
        skills: "Skills",
        education: "Education",
        projects: "Projects",
        certifications: "Certifications",
        languages: "Languages",
        careerHighlights: "Career highlights",
      },
    },
  },
  photoCrop: {
    accountTitle: "Adjust account photo",
    cvTitle: "Adjust CV photo",
    instructions: "Drag to position. Pinch or use the slider to zoom.",
    webInstructions: "Drag to position and adjust the zoom. You can also use the arrow keys.",
    preview: "Photo crop preview",
    accessibilityHint: "Use actions to move the photo, and the zoom slider to resize it.",
    zoom: "Zoom",
    reset: "Reset",
    cancel: "Cancel",
    usePhoto: "Use photo",
    failed: "We couldn't prepare this photo. Please try again or choose another image.",
    left: "Move photo left",
    right: "Move photo right",
    up: "Move photo up",
    down: "Move photo down",
  },
  accountPhoto: {
    "title": "Account photo",
    "edit": "Change account photo",
    "choose": "Choose photo",
    "remove": "Remove account photo",
    "saveFailed": "Could not save your photo. Please try again.",
    "removeCv": "Remove CV photo"
  },
  supportDesk: {
  "resumeDraft": "Continue your draft",
  "eyebrow": "A little help, whenever you need it",
  "title": "Help & support",
  "intro": "Find an answer or talk to the Tabbio team. Keep the whole conversation here.",
  "back": "Back",
  "quickAnswers": "Quick answers",
  "allAnswers": "Browse the help center",
  "contactTitle": "How can we help?",
  "contactDescription": "Tell us what you were trying to do and what happened. You can follow the reply here.",
  "newRequest": "Contact support",
  "history": "Your requests",
  "empty": "No requests yet. We are here when you need a hand.",
  "loading": "Loading your requests…",
  "loadError": "We could not load this conversation. Please try again.",
  "retry": "Try again",
  "more": "More requests",
  "older": "Earlier messages",
  "conversation": "Support conversation",
  "you": "You",
  "team": "Tabbio Support",
  "topic": "What is this about?",
  "message": "Tell us what happened",
  "reply": "Your reply",
  "placeholder": "What were you trying to do? What happened instead?",
  "draftSaved": "Draft saved on this device",
  "draftFailed": "Draft could not be saved on this device. Keep this page open.",
  "sendError": "We could not confirm delivery. Try again to safely resend the same message. Your draft is still here.",
  "sent": "Your message is saved. You can follow the conversation here.",
  "sending": "Sending…",
  "send": "Send request",
  "sendReply": "Send reply",
  "discard": "Clear draft",
  "privacy": "Your message and basic app details are shared with our support team. Please leave out passwords and payment card details.",
  "readOnly": "This view-only session can read requests but cannot send messages.",
  "status": {
    "open": "Open",
    "closed": "Resolved"
  },
  "categories": {
    "account": "Account",
    "cv": "CV & downloads",
    "billing": "Plan & billing",
    "jobs": "Jobs & applications",
    "other": "Something else"
  },
  "answers": {
    "cv": {
      "title": "Help with a CV download",
      "body": "Open the CV you want to download and check its content in the editor. If the downloaded file looks different, tell us which format you used and what changed."
    },
    "billing": {
      "title": "Check your plan or credits",
      "body": "Open Settings and choose Plan & billing to see your current plan and credits. For a payment issue, include the purchase date and the store you used."
    },
    "account": {
      "title": "Update your account details",
      "body": "Open Settings and choose Account to manage your name, email and sign-in details. If you cannot make a change, send us the error you see."
    }
  }
},
  errorBoundary: {
    title: "Something went wrong",
    message: "The app ran into an unexpected error. You can try again.",
    retry: "Try again",
    goHome: "Go to home",
  },
  appName: "Tabbio",
  appTagline: "Your digital CV.",
  ai: "AI",
  status: {
    loading: "Loading",
  },
  attachTitle: "Add attachment",
  attachCamera: "Camera",
  attachLibrary: "Photo Library",
  attachDocument: "Document",
  attachFailedTitle: "Could not attach file",
  attachFailedMessage: "Try again or pick a different file.",
  attachUnsupportedMessage:
    "That file type is not supported. Upload a PDF, DOCX, TXT, or image file.",
  localeSwitch: {
    switching: "Switching to {{language}}",
  },
  dirtyGuard: {
    title: "Unsaved changes",
    message: "You have unsaved changes. What would you like to do?",
    stay: "Stay",
    discard: "Discard",
    save: "Save",
  },
  supportSession: {
    title: "Support session",
    checkingTitle: "Checking support link",
    checkingDescription:
      "Tabbio is validating this one-time, view-only session.",
    viewOnly: "View-only support access",
    readyTitle: "View {{name}}'s account",
    readyDescription:
      "You are entering {{email}} as {{actor}} for support investigation.",
    restriction:
      "Changes, submissions, messages, purchases, and other write actions are blocked in this session.",
    expires: "This session expires at {{time}} and cannot be refreshed.",
    continue: "Continue in view-only mode",
    exit: "Exit session",
    failedTitle: "Support link unavailable",
    failedDescription:
      "This link has expired, was already used, or is no longer valid. Create a new link from the admin console.",
    goHome: "Go to Tabbio",
    bannerTitle: "Viewing {{name}} as support",
    bannerDescription: "View-only session: all changes are blocked",
  },
  meta: {
    titles: {
      appEntry: "Tabbio: AI Career Agent for CV, Job Search & Auto-Apply",
      onboarding: "Onboarding",
      services: "Services",
      library: "Library",
      shortlists: "Shortlists",
      upgrade: "Upgrade",
      createCompany: "Create Company",
      autoApply: "Automation",
      subscription: "Subscription",
      share: "Shared Page",
    },
    descriptions: {
      appEntry:
        "Build a CV that hiring systems can read, find matched jobs across the UAE and GCC, tailor every application, and auto-apply with Tabbio, your all-in-one AI career agent.",
      default:
        "AI-powered career conversations, CV profiles, jobs, and hiring tools.",
      auth: "Sign in to Tabbio and access your AI-powered career workspace on web.",
      onboarding: "Finish setting up your Tabbio profile and workspace on web.",
      chat: "Continue your AI-powered career conversation in Tabbio.",
      jobs: "Review opportunities and hiring workflows in your Tabbio workspace.",
      messages: "Review recruiter and employer conversations in Tabbio.",
      cv: "Manage your CV, profile assets, and public presence in Tabbio.",
      profile: "Review and update your Tabbio profile on web.",
      candidates:
        "Browse candidates and hiring activity in your Tabbio workspace.",
      company: "Manage your company profile, team, and career page in Tabbio.",
      analytics: "Track views and performance across your Tabbio workspace.",
      services: "Connect Tabbio to external apps and automation tools.",
      library: "Browse and organize your talent library in Tabbio.",
      shortlists:
        "Review shortlisted talent and shared candidate flows in Tabbio.",
      notifications:
        "Stay up to date with activity across your Tabbio workspace.",
      settings: "Manage account, billing, and workspace preferences in Tabbio.",
      upgrade: "Review plans and upgrade your Tabbio workspace.",
      uaePass: "View your UAE PASS status and verification in Tabbio.",
      share: "View shared Tabbio content and public shortlist pages.",
      autoApply:
        "Review scheduled automations and the signals Tabbio uses for your job search.",
    },
  },

  // Navigation
  nav: {
    home: "Home",
    chat: "Chat",
    cv: "CV",
    jobs: "Jobs",
    messages: "Messages",
    profile: "Profile",
    candidates: "Candidates",
    company: "Company Page",
    tabbio: "Tabbio",
    publicProfile: "Profile",
    webCv: "CV",
    sharedCvs: "Shared CVs",
    sites: "Sites",
    skills: "Skills",
    connectors: "Connectors",
    analytics: "Views",
    findJobs: "Find Jobs",
    myCv: "My CV",
    autoMatch: "Automation",
    tasks: "Tasks",
    postJob: "Post Job",
    apps: "Apps",
    memory: "Memory",
    search: "Search",
    careerPage: "Career Page",
    findTalent: "Find Talent",
    applications: "Applications",
    notifications: "Notifications",
    workspaceSoon: "Workspace",
    soon: "Soon",
  },
  // The Sites list, its settings page and Create (plan 7.10).
  sites: {
    title: "Sites",
    create: "Create",
    refresh: "Refresh",
    search: "Search sites",
    tabs: {
      owned: "My sites",
      shared: "Shared with me",
    },
    columns: {
      site: "Site",
      sharedWith: "Shared with",
    },
    audience: {
      everyone: "Everyone",
      onlyYou: "Only you",
    },
    share: "Share",
    shareLabel: "Share {{title}}",
    more: "More actions for {{title}}",
    menu: {
      share: "Share",
      edit: "Edit",
      analytics: "Analytics",
      settings: "Settings",
    },
    empty: "No sites yet",
    emptyShared: "No sites shared with you",
    noMatches: "No sites match {{query}}",
    loadFailed: "Couldn't load your sites.",
    rowMeta: "{{age}} · {{host}}",
    draft: "Draft",
    noAddress: "Not published yet",
    age: {
      now: "now",
      minutes: "{{count}}m",
      hours: "{{count}}h",
      days: "{{count}}d",
      weeks: "{{count}}w",
    },
    composer: {
      chip: "Sites",
      removeChip: "Remove Sites",
      placeholder: "Create a website that …",
    },
    settings: {
      breadcrumb: "Sites",
      breadcrumbLabel: "Breadcrumb",
      edit: "Edit",
      tabs: {
        settings: "Settings",
        analytics: "Analytics",
      },
      general: {
        title: "General",
        subtitle: "The name and address of your site, and who can see it",
      },
      name: {
        label: "Name",
        hint: "Name for your site",
        saved: "Name saved",
        failed: "Couldn't save the name. Try again.",
      },
      url: {
        label: "URL",
        hint: "Web address",
        change: "Change",
        none: "Publish the site to give it an address",
      },
      domain: {
        label: "Domain",
        hint: "URL for your site",
      },
      sharing: {
        label: "Sharing",
        hint: "Who can view your site",
        everyone: "Visible to everyone",
        onlyYou: "Only you",
        manage: "Manage",
      },
      danger: {
        title: "Danger zone",
        label: "Delete site",
        hint: "Permanently delete this site",
        cta: "Delete site",
      },
      delete: {
        title: "Delete this site?",
        body: "The address stops working right away. You can keep the page in your files.",
        keep: "Keep the page in my files",
        confirm: "Delete site",
        keepAndDelete: "Delete site, keep the page",
        deleteAll: "Delete site and page",
        deleting: "Deleting…",
        deleted: "Site deleted",
        failed: "Couldn't delete the site. Try again.",
      },
      notFound: "This site is no longer available.",
      loadFailed: "Couldn't load this site.",
    },
    analytics: {
      views7: "Views, last 7 days",
      views30: "Views, last 30 days",
      visitors7: "Visitors, last 7 days",
      visitors30: "Visitors, last 30 days",
      daily: "Views per day (UTC)",
      empty: "No views yet. Share the link to get your first visitors.",
      notLive: "Publish the site to start counting views.",
      dayViews_one: "{{day}}: {{count}} view",
      dayViews_other: "{{day}}: {{count}} views",
      loadFailed: "Couldn't load the numbers.",
    },
  },

  // Skills Hub
  skills: {
    title: "Skills",
    subtitle: "Tap-to-run agent skills that read and improve your CV.",
    nativeComingSoon:
      "The Skills Hub is available on the web app. A native experience is coming soon.",
    searchPlaceholder: "Search skills",
    shelves: {
      default: "Default",
      community: "Community",
      own: "My Skills",
    },
    tiers: {
      free: "Free",
      plus: "Plus",
      pro: "Pro",
    },
    filters: {
      title: "Filters",
      clear: "Clear filters",
      sort: "Sort",
      country: "Country",
      level: "Level",
      language: "Language",
    },
    tabs: {
      explore: "Explore",
      mine: "My skills",
    },
    sections: {
      cv: "CV skills",
      other: "Other skills",
    },
    directoryGroups: {
      uae: "United Arab Emirates",
      generalCv: "General CV skills",
      apply: "Job applications",
      interview: "Interview preparation",
      message: "Writing & networking",
      general: "Career support",
    },
    // tabbio/v2 catalog: other-skill categories (chip row + labels)
    categories: {
      all: "All",
      apply: "Apply",
      interview: "Interview",
      message: "Message",
      general: "General",
    },
    // tabbio/v2 catalog: CV-skill countries (filter + detail row)
    countries: {
      uae: "UAE",
      ksa: "Saudi Arabia",
      kwt: "Kuwait",
      qat: "Qatar",
      omn: "Oman",
      bhr: "Bahrain",
      gulf: "Gulf (GCC)",
    },
    // tabbio/v2 catalog: experience levels
    levels: {
      intern: "Intern",
      fresh: "Fresh grad",
      mid: "Mid-career",
      exec: "Executive",
      all: "All levels",
    },
    // Bilingual mini-pill on hub rows (language codes, same in Arabic)
    langBadgeBi: "EN·AR",
    // tabbio/v2: what a skill reads before running ("This skill reads" row)
    contextSources: {
      primary_cv: "Primary CV",
      profile: "Profile",
      memory: "Career memory",
    },
    // tabbio/v2: CV-skill layout templates (detail "Style" row)
    layoutTemplates: {
      modern: "Modern",
      classic: "Classic",
      executive: "Executive",
      creative: "Creative",
    },
    filterBar: {
      allPlans: "All plans",
      allLevels: "All levels",
      allCountries: "All countries",
      anyLanguage: "Any language",
      trending: "Trending",
      langEn: "English",
      langAr: "العربية",
      langBi: "Bilingual",
    },
    newSkill: "New skill",
    addSkill: "Add skill",
    open: "Open",
    use: "Use",
    useSkill: "Use skill",
    add: "Add",
    added: "Added",
    install: "Install",
    installed: "Installed",
    installUpgrade: "Upgrade to install",
    uninstall: "Uninstall",
    installCount: "{{value}} installs",
    installCount_one: "{{value}} install",
    installCount_other: "{{value}} installs",
    // Runs, not installs: a skill is used straight from the catalog row and
    // from chat, neither of which installs anything, so the install counter
    // stays at ~0 while the skill is used every day. `usageCount` is
    // incremented on every run (skills.service.ts) and is the honest signal.
    usageCount: "{{value}} uses",
    usageCount_one: "{{value}} use",
    usageCount_other: "{{value}} uses",
    installFailed: "Couldn't update this skill",
    verified: "Tabbio curated",
    useInChat: "Use in Agent Chat",
    slashMenuTitle: "All skills",
    slashMenuEmpty: "No matching skills found.",
    chatStarters: {
      title: "Get started with these skills",
      browse: "Browse all",
      browseHint: "Discover and manage more Agent Skills",
      viewAll: "View all",
      run: "Run in chat",
      view: "View and install",
      runLabel: "Run {{name}} in Agent Chat",
      openLabel: "View and install {{name}}",
    },
    handoff: {
      opening: "Opening your skill…",
      signInRequired: "Sign in to apply this skill to your CV.",
      signInToUse: "Sign in to use this career tool.",
    },
    toast: {
      addedToMySkills: "Added to My skills",
      removedFromMySkills: "Removed from My skills",
      installed: "Installed in My skills",
      uninstalled: "Removed from My skills",
    },
    count_one: "{{count}} skill",
    count_other: "{{count}} skills",
    newFlow: {
      chooser: {
        cvTitle: "Build a CV skill",
        anyTitle: "Build a skill",
      },
      describe: {
        label: "Description",
        placeholder:
          "e.g. One-page marketing CV for a UAE fresh grad. English, plain formatting, no photo.",
        writeWith: "Write it with",
        buildManually: "Build manually",
        generate: "Generate skill",
      },
      any: {
        title: "Build a skill",
        tabs: { write: "Write with AI", paste: "Paste", import: "Import" },
        writePlaceholder:
          "e.g. Turn a job post into 5 tailored interview questions with model answers.",
        pastePlaceholder: "Paste SKILL.md contents or plain instructions.",
        save: "Save skill",
        dropTitle: "Drop a SKILL.md file",
        dropBody: "or choose one from your device",
        choose: "Choose file",
      },
      // Write with AI, two steps: describe the idea, then review the draft.
      write: {
        describeLabel: "Describe what this skill should do",
        describePlaceholder:
          "e.g. Rewrite my LinkedIn About section for Gulf recruiters in under 200 words.",
        reviewNotice:
          "Drafted by AI from your description. Edit anything before creating.",
        regenerate: "Regenerate",
        tagsLabel: "Tags",
        modelSuggested: "Suggested: {{model}}",
        tryAgain: "Try again",
        writeManually: "Write manually",
      },
      basics: {
        nameCvPlaceholder: "Name your CV skill",
        iconLabel: "Choose icon",
        upload: "Upload image",
        recommendedModel: "Recommended model",
        recommendedModelHint: "for whoever runs this skill",
        subtitle: "Name it, pick an icon, and tell us who it's for.",
      },
      regions: {
        UAE: "UAE",
        KSA: "Saudi Arabia",
        Kuwait: "Kuwait",
        Qatar: "Qatar",
        Bahrain: "Bahrain",
        Oman: "Oman",
        GCC: "GCC",
      },
      levels: {
        intern: "Intern",
        "fresh-grad": "Fresh grad",
        "mid-career": "Mid-career",
        executive: "Executive",
      },
      langs: { en: "English", ar: "Arabic", bi: "Bilingual" },
      models: {
        "gemini-flash-lite": { name: "Gemini 3.5 Flash-Lite", note: "Fastest" },
        "gemini-flash": { name: "Gemini 3.6 Flash", note: "Fast" },
        "gpt-4o-mini": { name: "GPT-4o mini", note: "Free" },
        "claude-sonnet": { name: "Claude Sonnet 5", note: "Sharper writing" },
        "gemini-pro": { name: "Gemini 3.7 Flash", note: "Strong reasoning" },
        "claude-opus": { name: "Claude Opus 5", note: "Best writing" },
      },
      upgrade: {
        title: "{{model}} is a Pro model",
        body: "Unlock every model plus unlimited AI drafts.",
        notNow: "Not now",
        upgrade: "Upgrade",
      },
      sectionDefaults: {
        summary:
          "Three tight lines. Open with role and years, name one signature strength, close with a measurable win. No clichés, no invented numbers.",
        careerHighlights:
          "Three to four highlights, each a single line with a measurable outcome (%, AED, or time saved). Lead with the biggest.",
        skills:
          "Group by theme (Design, Tools, Languages). Within each, lead with the most job-relevant. Drop anything generic.",
        experience:
          "Reverse-chronological. One line of context per role, then bullets that start with a verb and end in a result.",
        education:
          "Lead with the degree, institution, and year. Add coursework or honours only when they strengthen the target role.",
        certifications:
          "List the certification, issuer, and year. Keep only current, relevant credentials.",
        languages: "List each language with an honest proficiency level.",
      },
    },
    run: "Run",
    by: "by {{author}}",
    byTabbio: "by Tabbio",
    byTabbioName: "Tabbio",
    sort: { top: "Top", new: "New", az: "A–Z", popular: "Most installed" },
    board: { skill: "Skill", activity: "Activity", runs: "Runs" },
    detail: {
      run: "Run skill",
      runTune: "Run & tune",
      result: "Result",
      summary: "Summary",
      howItWorks: "How it works",
      showContent: "Show content",
      hideContent: "Hide content",
      contentCopied: "Skill content copied",
      copyFailed: "Couldn't copy skill content",
      linkCopied: "Skill link copied",
      shareFailed: "Couldn't share this skill",
      needsLabel: "What it needs",
      givesLabel: "What you get",
      author: "Author",
      usage: "Usage",
      runs: "runs",
      rating: "Rating",
      category: "Category",
      tier: "Plan",
      languages: "Languages",
      version: "Version",
      added: "Added",
      // tabbio/v2 catalog meta rows
      recommendedModel: "Recommended model",
      reads: "This skill reads",
      country: "Country",
      level: "Level",
      style: "Style",
      photoOn: "Photo on",
      photoOff: "Photo off",
      actions: {
        export: "Export .md",
        copyMd: "Copy SKILL.md",
        edit: "Edit",
        delete: "Delete",
      },
      exportCopied: "SKILL.md copied to clipboard",
      exportDownloaded: "SKILL.md downloaded",
      exportFailed: "Couldn't export this skill",
      deleteTitle: "Delete skill",
      deleteMessage:
        "This removes “{{name}}” from your skills. This can't be undone.",
      deleteConfirm: "Delete",
      deleteCancel: "Cancel",
      deleted: "Skill deleted",
      deleteFailed: "Couldn't delete the skill",
      // Share on an own private skill publishes it first (P2-c).
      publish: {
        confirmTitle: "Make this skill public?",
        confirmBody:
          "Anyone with the link can view and run it at {{link}}. You can make it private again later.",
        confirmAction: "Make public & share",
        published: "Your skill is now public",
        publishFailed: "Couldn't publish this skill",
        unpublishTitle: "Make this skill private?",
        unpublishBody:
          "The public link stops working right away. You can publish it again whenever you like.",
        unpublishAction: "Make private",
        unpublished: "Your skill is now private",
        unpublishFailed: "Couldn't make this skill private",
        publicBadge: "Public",
        privateBadge: "Private",
        makePrivate: "Make private",
      },
      needs: {
        cv: "Your CV",
        cv_jd: "Your CV + a job post",
        job: "A job description",
        topic: "A topic",
        text: "A short description",
        profile: "Nothing: runs on your profile",
        none: "Nothing",
      },
      gives: {
        cvEdit: "An updated CV",
        letter: "A cover letter",
        post: "A ready-to-post draft",
        message: "An outreach message",
        search: "Search results & links",
        plan: "A step-by-step plan",
        prep: "Interview prep material",
        doc: "A ready-to-use document",
      },
    },
    createSkill: "Create skill",
    loadMore: "Load more",
    loadFailed: "Could not load skills",
    empty: {
      defaultTitle: "No skills yet",
      defaultBody: "Tabbio's built-in skills will appear here.",
      communityTitle: "Community skills are coming",
      communityBody:
        "Skills shared by the community will show up here once they launch.",
      ownTitle: "No installed skills yet",
      ownBody: "Install a skill from Explore or create your own.",
      retryBody: "Something went wrong. Try again.",
      createdTitle: "You haven't created a skill yet",
      createdBody: "Skills you create show up here.",
    },
    menu: {
      publish: "Publish",
      publishConfirmTitle: "Publish skill",
      publishConfirmMessage: "Make this skill available to run. Continue?",
      published: "Skill published",
      publishFailed: "Could not publish skill",
    },
    runDialog: {
      previewSubtitle: "Review what {{name}} will change before applying.",
      loading: "Preparing changes…",
      noChanges: "This skill didn't suggest any changes.",
      changes: "Changes",
      nextActions: "Next steps",
      titleField: "Title",
      headlineField: "Headline",
      summaryField: "Summary",
      skillsField: "Skills",
      apply: "Apply changes",
      applying: "Applying…",
      cancel: "Cancel",
      done: "Skill applied",
      failed: "Could not run skill",
      blocked:
        "This skill is blocked. Update your Agent access settings to run it.",
      configureSubtitle: "See what it does, then apply it to a CV.",
      configureTextSubtitle: "Add what the skill needs, then run it.",
      resultSubtitle: "Here's what changed: keep it or undo.",
      resultTextSubtitle: "Your result is ready to use.",
      whatItDoes: "What it does",
      applyTo: "Apply to",
      allowanceTitle: "Skill runs",
      allowanceChecking: "Checking your Skill allowance…",
      allowanceUnavailable:
        "Usage information is temporarily unavailable. Your run will still be checked before it starts.",
      allowanceSummary:
        "{{included}} included · {{used}} used · {{remaining}} remaining",
      allowanceResets: "Resets {{date}}",
      allowanceIncluded: "Included with your current plan",
      allowanceUpgrade: "See Plus",
      run: "Run skill",
      running: "Applying {{name}}…",
      runningText: "Running {{name}}…",
      runningHint: "This usually takes a few seconds.",
      appliedTo: "Applied to {{cv}}",
      yourCv: "your CV",
      viewCv: "View updated CV",
      undo: "Undo",
      undoing: "Undoing…",
      undone: "Reverted: your CV is unchanged.",
      undoFailed: "Couldn't undo. Please try again.",
      noChangesTitle: "No changes needed",
      safeNote: "Runs on a copy: your original CV stays untouched.",
      // B09: the CV changed (or the write would differ) after the review.
      reviewAgain:
        "Your CV, or how this run saves it, changed after the run was reviewed, so nothing was changed and no credit was used. Run the skill again to review the latest version.",
      loadingCvs: "Loading your CVs…",
      untitledCv: "Untitled CV",
      noCvBody: "You don't have a CV yet. Create one, then run skills on it.",
      createCv: "Create a CV",
      importCv: "Import a CV",
      importCvFromDevice: "Import a CV from your device",
      importing: "Importing your CV…",
      importFailed: "Could not import that CV. Try another file.",
      tryAgain: "Try again",
      doneButton: "Done",
      regenerate: "Regenerate",
      copy: "Copy",
      copied: "Copied to clipboard",
      copyFailed: "Couldn't copy",
      applyMode: "How to save",
      modeUpdate: "Update this CV",
      modeNewVersion: "Save as new version",
      versionNamePlaceholder: "Name this version (e.g. Tailored: Product Lead)",
      updateNote: "Changes are applied to this CV. You can undo afterwards.",
      updatedCv: "Updated {{cv}}",
      jobLabel: "Job post or description",
      jobPlaceholder: "Paste the job post, description, or a link…",
      topicLabel: "What's it about?",
      topicPlaceholder: "e.g. lessons from shipping my first feature",
      freeTextLabel: "Tell Tabbio a bit more",
      freeTextPlaceholder: "Describe your situation, role, or what you need…",
      profileNote: "This runs on your profile: no input needed.",
      textReady: "Ready to use",
      noOutput: "No output was produced.",
      selectPlaceholder: "Choose…",
    },
    // CV spine B05: an undo that would replace edits made after the run.
    undoConflict: {
      title: "You edited this CV after the skill ran",
      message:
        "Undoing now would replace your newer edits to: {{fields}}. Restore what the skill changed anyway, or keep your edits?",
      unknownMessage:
        "This run is from before undo could tell your edits apart from the skill's. Restoring puts back the CV as it was before the run and replaces: {{fields}}.",
      restoreAnyway: "Restore anyway",
      keepEdits: "Keep my edits",
      copyTitle: "Copy kept",
      copyMessage:
        "You edited the copy this skill made, so it wasn't deleted. You can delete it from your CVs if you no longer need it.",
      unavailableTitle: "Can't undo this run",
      unavailableMessage:
        "This run didn't keep your CV from before it ran, so it can't be undone. Nothing was changed.",
      ok: "OK",
      separator: ", ",
      fields: {
        title: "CV name",
        summary: "Summary",
        headline: "Headline",
        contact: "Contact details",
        skills: "Skills",
        experience: "Experience",
        education: "Education",
        certifications: "Certifications",
        highlights: "Highlights",
        projects: "Projects",
        languages: "Languages",
        design: "Design",
        layout: "Section order",
        content: "CV content",
        other: "Other details",
      },
    },
    create: {
      title: "Create a skill",
      nameLabel: "Name",
      namePlaceholder: "e.g. Tighten my summary",
      descriptionLabel: "What it does",
      descriptionPlaceholder: "Load when… / Use to… (shown on the card)",
      instructionsLabel: "Instructions",
      instructionsPlaceholder: "Describe the method the skill should follow.",
      submit: "Create skill",
      creating: "Creating…",
      created: "Skill created",
      failed: "Could not create skill",
      nameRequired: "Add a name.",
      descriptionRequired: "Add a one-line description.",
    },
    // Two-way skills: 5-step builder wizard (web owns these keys; native reuses)
    builder: {
      title: "Create a skill",
      editTitle: "Edit skill",
      drafting: "Drafting\u2026",
      generateFailed: "We couldn't draft this one. You can still build it yourself.",
      generateRetry: "Build manually",
      stepLabel: "Step {{current}} of {{total}}",
      steps: {
        basics: "Basics",
        type: "Type",
        style: "Style",
        sections: "Sections",
        review: "Review",
      },
      back: "Back",
      next: "Next",
      create: "Create skill",
      save: "Save changes",
      saving: "Saving…",
      created: "Skill created",
      saved: "Skill updated",
      failed: "Could not save the skill",
      basics: {
        nameLabel: "Name",
        namePlaceholder: "e.g. Fresh Grad Kuwait CV",
        descriptionLabel: "What it does",
        descriptionPlaceholder:
          "One or two lines shown on the card: when to use this skill.",
        nameRequired: "Add a name.",
        descriptionRequired: "Add a short description.",
      },
      type: {
        label: "What kind of skill is this?",
        portableTitle: "Portable instructions",
        portableBody:
          "A standard SKILL.md: instructions the agent follows. Export and share it anywhere.",
        customTitle: "Custom CV skill",
        customBody:
          "Pick a CV style, accent and sections, then guide each section with prompts. Applies in one tap.",
        instructionsLabel: "Instructions",
        instructionsPlaceholder:
          "Describe the method the skill should follow, step by step…",
        instructionsHint:
          "This is the skill's method: what the agent reads when someone runs it.",
        charCount: "{{used}} / {{max}}",
      },
      style: {
        layoutLabel: "Layout",
        colorLabel: "Accent color",
        layouts: {
          MODERN: "Modern",
          CLASSIC: "Classic",
          EXECUTIVE: "Executive",
          CREATIVE: "Creative",
        },
        colors: {
          BLACK: "Black",
          BLUE: "Blue",
          PURPLE: "Purple",
          EMERALD: "Emerald",
          RED: "Red",
          ORANGE: "Orange",
        },
      },
      sectionsStep: {
        hint: "Choose which sections this CV includes and their order. Add a prompt to guide how each one is written.",
        needOne: "Include at least one section.",
        moveUp: "Move up",
        moveDown: "Move down",
        dragHint: "Drag to reorder",
        addSection: "Add {{name}}",
        suggest: "Suggest with AI",
        regenerate: "Regenerate",
        suggestFailed: "Couldn\u2019t suggest a prompt. Showing the default instead.",
        suggesting: "Suggesting…",
        promptCount: "{{used}} / {{max}}",
        names: {
          summary: "Summary",
          careerHighlights: "Career highlights",
          skills: "Skills",
          experience: "Experience",
          education: "Education",
          projects: "Projects",
          certifications: "Certifications",
          languages: "Languages",
        },
        prompts: {
          summary:
            "e.g. 3 lines max, energetic, no clichés, no invented numbers.",
          careerHighlights:
            "e.g. 3–4 highlights, each with a measurable outcome.",
          skills:
            "e.g. Group by theme; lead with the most job-relevant skills.",
          experience:
            "e.g. Frame internships as impact. Volunteer work counts.",
          education: "e.g. Lead with the degree. Coursework only if relevant.",
          projects: "Describe the work using only the source project facts.",
          certifications: "e.g. Most recent first; drop expired ones.",
          languages: "e.g. Only languages you can work in; include the level.",
        },
      },
      review: {
        name: "Name",
        description: "Description",
        type: "Type",
        typePortable: "Portable instructions",
        typeCustom: "Custom CV skill",
        style: "Style",
        color: "Accent",
        sections: "Sections",
        withPrompt: "with prompt",
        instructions: "Instructions",
        noInstructions: "No instructions added.",
        privateNote:
          "Your skill stays private to you. You can export or publish it later.",
      },
    },
    // Import a SKILL.md (paste or upload): hub entry next to Create
    import: {
      entry: "Import skill",
      title: "Import a skill",
      subtitle:
        "Paste a SKILL.md or upload the file: Tabbio skills and standard portable skills both work.",
      pasteLabel: "SKILL.md contents",
      pastePlaceholder: "---\nname: …\ndescription: …\n---\n…",
      uploadLabel: "Upload .md file",
      fileTooLarge: "That file is too large. Max 200 KB.",
      readFailed: "Couldn't read that file.",
      required: "Paste the SKILL.md contents or upload a file.",
      errorLabel: "Problem with this SKILL.md",
      submit: "Import skill",
      importing: "Importing…",
      imported: "Skill imported",
      failed: "Couldn't import that skill",
    },
    // Shared discard-draft confirmation (builder wizard + import dialog)
    discard: {
      title: "Discard changes?",
      message:
        "You have unsaved changes to this skill. Close and discard them?",
      confirm: "Discard",
    },
    picker: {
      title: "Apply a skill",
      subtitle: "Pick a skill to run on this CV.",
      empty: "No skills available to run.",
    },
    // Native confirmation sheet (full-CV writes)
    confirm: {
      title: "Confirm full-CV update",
      message:
        "This skill rewrites your whole CV. Review the preview, then confirm to apply.",
      confirmCta: "Confirm & apply",
    },
    // Native chat tool-cards
    card: {
      label: "Skill",
      runTitle: "Run skill",
      publishTitle: "Publish skill",
      created: "Created skill",
      published: "Published skill",
      previewBadge: "Preview",
      listLabel: "Skills",
      listEmpty: "No skills matched.",
      openHint: "Open this skill to run it",
      selectHint: "Select this skill in Agent Chat",
      appliedBadge: "Applied",
      count_one: "{{count}} skill",
      count_other: "{{count}} skills",
      showAll_one: "Show the skill",
      showAll_other: "Show all {{count}} skills",
      forYou: "Skills for you",
    },
  },

  // Connectors (M3.3): two-rail Tools + AI integrations surface
  connectors: {
    simple: {
      setupFailed: "Couldn't load connection details. Please try again.",
      aiDescription: "Use Tabbio with {{name}}.",
      available: "Available apps",
      back: "Back to connectors",
      disconnectName: "Disconnect {{name}}",
      actionFailed: "We couldn't update this connection. Please try again.",
    },
    title: "Connectors",
    subtitle: "Connect Tabbio to the apps and AI tools you already use.",
    nativeSubtitle: "Apps that can read your CV.",
    searchPlaceholder: "Search connectors",
    sections: {
      connected: "Connected",
      tools: "Tools",
      ai: "AI assistants",
    },
    endpoint: {
      title: "Your Tabbio endpoint",
      hint: "Point your AI tools at this address.",
      copy: "Copy",
      copied: "Copied",
      copyFailed: "Couldn't copy to clipboard",
      loading: "Loading endpoint…",
    },
    soon: "Soon",
    connect: "Connect",
    connectName: "Connect {{name}}",
    toast: {
      connected: "Connected",
      disconnected: "Disconnected",
    },
    consent: {
      willBeAbleTo: "It will be able to",
      willNot: "It will not",
      canEditCv: "Edit your CV",
      canReadCv: "View your CV and skills",
      runYourSkills: "Run your skills",
      canTailorCv: "Tailor your CV",
      canFindJobs: "Find jobs",
      canDownloadResume: "Download your resume in any format",
      cannotAccessAccount:
        "Access your account settings or delete your account",
      disconnectAnytime: "Disconnect anytime.",
      finishTitle: "Finish in {{name}}",
      finishHint:
        "Copy the endpoint and token into {{name}} to complete the connection.",
    },
    connecting: "Connecting…",
    manage: "Manage",
    manageSubtitle: "Review access and disconnect",
    revoke: "Disconnect",
    revoking: "Disconnecting…",
    revokeTitle: "Disconnect {{name}}?",
    revokeMessage:
      "Tabbio will lose access to this connector. You can reconnect at any time.",
    grantedAccess: "Granted access",
    noScopes: "No specific permissions were shared.",
    loadingScopes: "Loading permissions…",
    loading: "Loading connectors…",
    loadFailed: "Couldn't load connectors",
    loadFailedBody: "Something went wrong. Please try again.",
    empty: {
      title: "No connectors found",
      body: "Try a different search term.",
    },
    status: {
      connected: "Connected",
      notConnected: "Not connected",
      comingSoon: "Coming soon",
    },
    aiHint:
      "Connect Tabbio to your AI assistant over MCP to use your career data anywhere.",
    aiSetupNote: "Set up below",
    setup: {
      subtitle: "Connect {{name}} to Tabbio in a few steps.",
      comingSoonSubtitle: "Coming soon",
      comingSoonBody:
        "We're putting the finishing touches on this. You'll be able to connect {{name}} shortly.",
      introFallback:
        "Connect {{name}} to Tabbio over MCP so it can use your CV, skills and career data.",
      stepsLabel: "How to connect",
      configLabel: "Configuration",
      commandLabel: "Command",
      serverUrl: "Server URL",
      token: "Access token",
      generateToken: "Generate token",
      generating: "Generating…",
      regenerate: "Generate new",
      revoke: "Revoke",
      revokeTitle: "Revoke token?",
      revokeBody:
        "Any AI assistant using this token will lose access until you set it up again.",
      revoked: "Token revoked",
      tokenActive: "Token active",
      oneTimeWarning: "Copy this token now: we won't show it again.",
      needTokenForCode:
        "Generate a token above and it fills in here automatically.",
      personalAccess:
        "Grants access to your personal profile, CV and applications only.",
      copy: "Copy",
      copied: "Copied",
      copyFailed: "Couldn't copy to clipboard",
      tokenFailed: "Couldn't update the token",
      openDocs: "Setup guide",
      done: "Done",
      chatgpt: {
        intro:
          "Use Tabbio inside ChatGPT: it can read your CV and career data to help you apply.",
        steps: [
          "In ChatGPT, open Settings → Connectors (Developer Mode must be on: Plus, Pro, Business, Enterprise or Edu).",
          "Click “Create”, name it “Tabbio”, and paste the Server URL below.",
          "When asked to authenticate, paste the token below as a Bearer token.",
          "Save. In a chat, open + → Developer mode and pick Tabbio.",
        ],
      },
      claude: {
        intro:
          "Add Tabbio as a custom connector in Claude Desktop, claude.ai or Claude for Work.",
        steps: [
          "In Claude, click the + in the message box → Connectors → Manage connectors.",
          "Choose “Add custom connector”.",
          "Paste the Server URL below. Under Advanced settings, add the token as a Bearer header.",
          "Enable the Tabbio connector and start chatting.",
        ],
      },
      gemini: {
        intro: "Add Tabbio to Gemini CLI as an MCP server.",
        steps: [
          "Open your Gemini CLI settings.json file.",
          "Add the configuration below under mcpServers.",
          "Save, restart Gemini CLI, then run /mcp to confirm Tabbio is connected.",
        ],
      },
      grok: {
        intro: "Add Tabbio to Grok as a custom MCP connector.",
        steps: [
          "Open grok.com/connectors.",
          "Click New Connector, then choose Custom.",
          "Paste the Server URL below and complete the required authentication.",
          "Grok will discover Tabbio tools and make them available in conversations.",
        ],
      },
      perplexity: {
        intro:
          "Perplexity supports local MCP on Mac today. Remote MCP is coming soon, so Tabbio will be available here when Perplexity enables remote connectors.",
        steps: [
          "Open Perplexity for Mac and go to Account settings → Connectors.",
          "Install the PerplexityXPC helper if prompted.",
          "Remote MCP is not yet generally available. Check the setup guide for rollout details.",
        ],
      },
    },
    // Chat tool-cards for the M3.2 connector tools
    card: {
      connectedLabel: "Connected services",
      emailsLabel: "Recent emails",
      messagesLabel: "Recent messages",
      listEmpty: "Nothing to show yet.",
      more: "+{{count}} more",
      actionLabel: "Connector action",
      sendEmailTitle: "Send email",
      postSlackTitle: "Send Slack message",
      zapierTitle: "Run Zapier workflow",
      makeTitle: "Run Make scenario",
      sentEmail: "Email sent",
      postedSlack: "Message sent",
      triggered: "Automation triggered",
      confirm: "Run this connector action?",
      fieldTo: "To",
      fieldSubject: "Subject",
      fieldChannel: "Channel",
      fieldMessage: "Message",
      fieldWorkflow: "Workflow",
      fieldScenario: "Scenario",
    },
  },
  nova: {
    title: "Automation",
    subtitle:
      "Schedule tasks that run for you. You approve every draft before anything goes out.",
    proBadge: "Pro",
    lockedTitle: "Meet your Career agent",
    lockedSubtitle:
      "Your job-search autopilot. Your Career agent scans jobs, finds matches, and prepares drafts on a schedule. You approve before anything is sent.",
    locked: {
      benefits: {
        scan: {
          title: "Works while you're away",
          body: "Your Career agent scans new jobs on your schedule, even when the app is closed.",
        },
        drafts: {
          title: "Drafts, never auto-sent",
          body: "Applications and replies are prepared as drafts you review and approve first.",
        },
        results: {
          title: "Results wherever you are",
          body: "Matches arrive as notifications and email digests, with every run visible on your board.",
        },
      },
    },
    board: {
      newAutomation: "New automation",
      loading: "Loading your automations…",
      loadFailed: "Couldn't load your automations",
      loadFailedBody: "Something went wrong. Please try again.",
      empty: {
        title: "No automations yet",
        body: "Start from a template, or describe the task in your own words.",
        cta: "New automation",
      },
      nextRun: "Next run {{when}}",
      noSchedule: "No fixed schedule",
      viewHistory: "View run history",
      yourAutomations: "Your automations",
      activeMeter: "{{active}} of {{max}} active",
      lastRan: "ran {{when}}",
      getStarted: "Get started",
      finishSetup: "Finish setup",
      draftNeedsSetup: "Draft · Finish setup",
      draftProRequired: "Draft · Pro required to activate",
      filter: {
        label: "Filter",
        all: "All",
        active: "Active",
        paused: "Paused",
      },
      filteredEmpty: {
        active: "No active automations",
        paused: "No paused automations",
      },
    },
    composer: {
      placeholder: "Create a task",
      send: "Send",
      add: "More options",
      discard: "Discard",
      controls: "Research and result controls",
      controlsTitle: "Research and results",
      controlsClose: "Close",
    },
    cadence: {
      daily: "Daily",
      weekdays: "Weekdays",
      weekly: "Weekly",
      monthly: "Monthly",
      custom: "Custom",
    },
    timeOfDay: {
      morning: "Morning",
      afternoon: "Afternoon",
      evening: "Evening",
      night: "Night",
    },
    approvals: {
      title: "Needs your approval",
      review: "Review",
      draftReady: "Draft ready to review",
      jobMatches_one: "{{count}} job match",
      jobMatches_other: "{{count}} job matches",
    },
    limit: {
      title: "Automation limit reached",
      body: "You can keep up to {{max}} automations active. Pause one, or upgrade for more.",
    },
    rateLimit: {
      automation: {
        title: "Already ran today",
        body: "This automation runs once a day. It will run again tomorrow.",
      },
      user: {
        title: "Daily automation limit reached",
        body: "You've hit today's limit across all your automations. It resets tomorrow.",
      },
      concurrency: {
        title: "A run is already in progress",
        body: "Give the current run a moment to finish, then try again.",
      },
    },
    status: {
      draft: "Draft",
      active: "Active",
      paused: "Paused",
      archived: "Archived",
    },
    actions: {
      pause: "Pause",
      resume: "Resume",
      edit: "Edit",
      delete: "Delete",
      runNow: "Run now",
    },
    delete: {
      confirmTitle: "Delete this automation?",
      confirmMessage:
        "Your Career agent will stop running it. This can't be undone.",
      failed: "Couldn't delete this automation",
    },
    lifecycle: {
      paused: "Automation paused",
      resumed: "Automation resumed",
      failed: "Couldn't update this automation",
      runStarted: "Run started",
      runFailed: "Couldn't start a run",
      rateLimited: "You've reached today's automation limit. Try again later.",
    },
    templates: {
      label: "Templates",
      use: "Use template",
      add: "Add",
      enabled: "Added to your board",
      draftCreated: "Draft added. Finish setup to activate",
      enableFailed: "Couldn't add this template",
    },
    builder: {
      title: "New automation",
      editTitle: "Refine automation",
      taskLabel: "What should your Career agent do?",
      taskPlaceholder:
        "e.g. Every Monday at 9am, find new jobs that match my CV and draft applications for me to review.",
      scheduleLabel: "Schedule (optional)",
      schedulePlaceholder: "e.g. every weekday at 8am",
      scheduleHelp:
        "Leave blank and your Career agent will infer a schedule from your description.",
      compile: "Build plan",
      compiling: "Building your plan…",
      compilingHint:
        "Your Career agent is turning your words into a step-by-step plan. This usually takes a few seconds.",
      recompile: "Rebuild plan",
      compileFailed: "Your Career agent couldn't build a plan",
      compileFailedBody: "Try describing the task a little differently.",
      emptyTask: "Describe what your Career agent should do first.",
      planTitle: "Career agent plan",
      summaryLabel: "Summary",
      whenLabel: "When it runs",
      sentenceLabel: "In plain English",
      cronLabel: "Schedule expression",
      triggerEventLabel: "Trigger",
      stepsLabel: "Steps",
      outputsLabel: "Results",
      showDetails: "Show details",
      hideDetails: "Hide details",
      stepDryRun: "Draft only",
      stepApproval: "Needs your approval",
      stepLive: "Runs automatically",
      trustLabel: "I trust this, apply automatically",
      trustHelp:
        "Coming soon. For now your Career agent always drafts and waits for your approval before anything is sent.",
      safetyNote:
        "Your Career agent only prepares drafts. Nothing is sent or submitted until you review and approve it.",
      approve: "Approve and activate",
      activating: "Activating…",
      activated: "Automation activated",
      activatedWithRun: "Activated. First run {{when}}",
      activatedHint: "You'll be notified when there's a result to review.",
      activateFailed: "Couldn't activate this automation",
      validationFailed: "Fix the highlighted issues before activating.",
      connectTitle: "Connect an app first",
      connectBody:
        "Your Career agent needs access to {{apps}} to run this automation.",
      connectCta: "Connect apps",
      rateLimited:
        "You've reached today's automation limit. Try again in {{seconds}}s.",
      controlsLabel: "Research and results",
      controlsHint:
        "Choose whether this task searches the web, adds an image, and what it sends you.",
    },
    runs: {
      title: "Run history",
      subtitle:
        "Everything your Career agent has done, with the drafts it produced.",
      empty: {
        title: "No runs yet",
        body: "When this automation runs, you'll see what it did here.",
      },
      loadFailed: "Couldn't load run history",
      filterStatus: "Status",
      filterAll: "All",
      allAutomations: "All automations",
      startedAt: "Started {{when}}",
      scheduledFor: "Scheduled for {{when}}",
      stepsLabel: "Steps",
      outputsLabel: "Produced",
      draftLabel: "Draft",
      approve: "Approve",
      reject: "Reject",
      approved: "Approved",
      rejected: "Rejected",
      approveFailed: "Couldn't update this run",
      viewRun: "View run",
      runStatus: {
        queued: "Queued",
        running: "Running",
        succeeded: "Done",
        failed: "Failed",
        skipped: "Skipped",
        killed: "Stopped",
        awaiting_approval: "Awaiting approval",
      },
      skipReason: {
        entitlement: "Skipped: Pro is required",
        inactive: "Skipped: this task was not active",
        no_version: "Skipped: this task has no saved plan",
        rate_automation: "Skipped: today's run limit was reached",
        rate_user: "Skipped: today's limit for all your tasks was reached",
        rate_concurrency: "Skipped: a previous run was still in progress",
        kill_switch: "Skipped: tasks are paused for a moment",
      },
      failReason: {
        research_no_sources:
          "No current sources were found this time. The next run will try again.",
        research_failed:
          "The research service did not answer. The next run will try again.",
        research_limit: "This task reached the research limit for one run.",
        sources_unreadable:
          "None of the sources could be read. Check the link or file.",
        capability_unavailable:
          "Research or documents are not available for your account right now.",
        credits_exhausted:
          "You're out of credits for today. The next run will try again.",
        report_failed: "The report could not be written this time.",
        render_failed: "The document could not be created this time.",
        invalid_plan: "This task needs to be rebuilt. Edit its plan.",
      },
    },
    runPanel: {
      title: "Automation run",
      liveTitle: "Your Career agent is on it…",
      liveHint: "Results appear as each step finishes.",
      awaitingTitle: "Needs your approval",
      awaitingHint:
        "Your Career agent prepared drafts. Nothing is sent until you approve.",
      failedTitle: "This run didn't finish",
      failedHint: "Nothing was sent. Try running it again in a moment.",
      doneWithMatches_one: "Done. {{count}} match found",
      doneWithMatches_other: "Done. {{count}} matches found",
      doneHint: "Tap a match to view the job.",
      doneGeneric: "Done. Your result is ready",
      doneGenericHint: "Review the complete result below.",
      noResults: "No new matches this time",
      noResultsHint:
        "Your Career agent will keep scanning on schedule and notify you when something lands.",
      resultsLabel: "What it found",
      detailsLabel: "Result details",
      loadFailed: "Couldn't load this run",
      notFoundTitle: "This result is no longer available",
      notFoundHint: "It may have been deleted or belongs to another account.",
      backToTasks: "Back to tasks",
      retry: "Retry",
      nothingNewHint:
        "Your Career agent checked again and found nothing new, so nothing was sent.",
    },
    results: {
      matchesFound_one: "Found {{count}} match",
      matchesFound_other: "Found {{count}} matches",
      digestReady_one: "Digest ready with {{count}} match",
      digestReady_other: "Digest ready with {{count}} matches",
      draftAwaiting: "Draft ready for your approval",
      scoreLabel: "{{score}}% match",
      sourcesFound_one: "Found {{count}} source",
      sourcesFound_other: "Found {{count}} sources",
      sourcesRead_one: "Read {{count}} source",
      sourcesRead_other: "Read {{count}} sources",
      reportReady: "Report written",
      nothingNew: "Nothing new since the last run",
      documentReady: "Document ready",
      documentRendering: "Creating the document",
      delivered: "Delivered",
    },
    steps: {
      search_jobs: "Find matching jobs",
      evaluate_matches: "Pick the best matches",
      compose_digest: "Build your digest",
      summarize: "Summarize the results",
      draft_applications: "Draft applications",
      draft_engagement: "Draft outreach messages",
      draft_follow_up: "Draft follow-ups",
      draft_prep: "Prepare interview notes",
      draft_reply: "Draft replies",
      fetch_cv: "Read your CV",
      fetch_profile: "Read your profile",
      fetch_applications: "Read your applications",
      get_analytics: "Check analytics",
      get_views: "Check profile views",
      connectorAction: "Send via connector",
      draft: "Draft",
      research_web: "Research the web",
      read_sources: "Read your sources",
      compose_report: "Write the report",
      render_document: "Create the files",
      deliver: "Deliver the result",
    },
    report: {
      title: "What it reads and sends",
      sourcesLabel: "Sources",
      resultLabel: "Result",
      imageLabel: "Image",
      deliveryLabel: "Delivery",
      creditsLabel: "Estimated credits per run",
      separator: ", ",
      sources: {
        web: "Web search: {{topic}}",
        webRegion: "Web search: {{topic}} in {{region}}",
        links: "Links: {{list}}",
        pdf: "Your PDFs: {{number}}",
        github: "GitHub repository: {{repo}}",
        skill: "One of your skills",
      },
      window: {
        day: "last 24 hours",
        week: "last 7 days",
        month: "last 30 days",
      },
      format: {
        summary: "A short summary",
        document: "Document ({{files}})",
        slides: "Slides ({{files}})",
      },
      image: {
        included: "Included",
        notIncluded: "Not included",
      },
      channels: {
        in_app: "In the app",
        email: "Email",
      },
    },
    templateInputs: {
      title: "Set up {{name}}",
      subtitle: "Tell your Career agent what to read. You can review the plan before it runs.",
      topicLabel: "Topic",
      topicPlaceholder: "e.g. product design",
      topicHint: "Leave it empty to use your job preferences.",
      regionLabel: "Region (optional)",
      regionPlaceholder: "e.g. UAE",
      urlLabel: "Link to watch",
      urlPlaceholder: "https://",
      urlHint: "A public page. Your Career agent reads it on each run.",
      urlInvalid: "Use a link that starts with http or https.",
      pdfLabel: "Your PDF",
      pdfEmpty: "No PDFs yet. Attach a PDF in a chat first, then come back.",
      pdfLoading: "Loading your PDFs…",
      pdfLoadFailed: "Couldn't load your PDFs.",
      pdfRequired: "Pick one of your PDFs.",
      required: "This field is required.",
      submit: "Add task",
      submitting: "Adding…",
      cancel: "Cancel",
    },
    card: {
      planLabel: "Career agent plan",
      listLabel: "Automations",
      listEmpty: "No automations yet.",
      count_one: "{{count}} automation",
      count_other: "{{count}} automations",
      runsLabel: "Run history",
      runsEmpty: "No runs yet.",
      runsCount_one: "{{count}} run",
      runsCount_other: "{{count}} runs",
      activateTitle: "Activate automation",
      runNowTitle: "Run automation now",
      activated: "Automation activated",
      ranNow: "Run started",
      confirmActivate: "Approve and activate this automation?",
      confirmRunNow: "Run this automation now?",
      whenLabel: "When",
      stepsLabel: "Steps",
      draftBadge: "Draft only",
    },
  },

  // Shared select/dropdown chrome (DropdownField, native + web)
  dropdown: {
    title: "Select option",
    searchPlaceholder: "Search",
    empty: "No options",
    noResults: "No matches",
  },

  // Common actions
  actions: {
    save: "Save",
    add: "Add",
    remove: "Remove",
    enable: "Enable",
    disable: "Disable",
    copy: "Copy",
    share: "Share",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    done: "Done",
    selectDate: "Select date",
    close: "Close",
    back: "Back",
    moveUp: "Move up",
    moveDown: "Move down",
    next: "Next",
    submit: "Submit",
    retry: "Retry",
    download: "Download",
    dismiss: "Dismiss",
    open: "Open",
    signOut: "Sign Out",
    goHome: "Go home",
    getStarted: "Get Started",
    newChat: "+ New",
    clear: "Clear",
    more: "More",
    openMenu: "Open menu",
    continueOnboarding: "Continue onboarding",
  },

  // Common labels
  labels: {
    yourChats: "Chats",
    today: "Today",
    yesterday: "Yesterday",
    previous7Days: "Previous 7 Days",
    previous30Days: "Previous 30 Days",
    loading: "Loading...",
    noResults: "No results found",
    noConversations: "Your conversations will appear here",
    personal: "Personal",
    employer: "Employer",
    switchWorkspace: "Switch Workspace",
    addWorkspace: "Add workspace",
    countryCode: "Country code",
    searchCountry: "Search country...",
  },

  location: {
    title: "Location",
    search: "Search city or country...",
    searching: "Searching…",
    noMatches: "No matches. Try a different city.",
    selectLocation: "Select location",
    select: "Select",
    regions: {
      remote: "Remote",
      gcc: "GCC",
      middleEast: "Middle East",
      southAsia: "South Asia",
      southeastAsia: "Southeast Asia",
      eastAsia: "East Asia",
      europe: "Europe",
      americas: "Americas",
      africa: "Africa",
      oceania: "Oceania",
    },
  },

  startup: {
    openingWorkspace: "Opening your workspace",
    loadingSettings: "Opening settings",
    loadingUpgrade: "Opening plans",
    openingConversation: "Opening conversation",
  },

  resumeImport: {
    checking: "Preparing your resume import",
    claiming: "Reading your resume",
    saving: "Updating your CV",
    missingError:
      "This resume handoff is missing or expired. Please upload your resume again.",
    genericClaimError: "Could not process your resume. Please upload it again.",
    emptyError: "We could not find enough CV details to update your profile.",
    saveFailed: "Could not update your CV. Please try again.",
    toastTitle: "CV updated",
    toastMessage: "Your resume details are ready to review.",
    kicker: "Resume import",
    importTitle: "Let's build your CV",
    creating: "Creating your new CV",
    errorTitle: "Resume import needs a new upload",
    retryTitle: "Resume import paused",
    errorBody:
      "This handoff expired. Return to the landing page and upload your resume again.",
    importedResume: "Imported resume",
    summaryMeta:
      "{{roles}} roles · {{education}} education · {{skills}} skills",
    backToTabbio: "Back to Tabbio",
    retryAction: "Try again",
    // The choice an already-onboarded importer gets. Replacing the main CV
    // used to be the only outcome, which lost the CV people already had.
    chooseTitle: "Where should this resume go?",
    chooseBody:
      "We read your resume. Nothing has changed yet, so pick what happens to it.",
    chooseUpdate: "Update my main CV",
    chooseUpdateHint:
      "Replaces your main CV with this resume. You can edit it afterwards.",
    chooseCopy: "Save it as a new CV",
    chooseCopyHint:
      "Adds it alongside your other CVs. Your main CV stays exactly as it is.",
    copyToastTitle: "New CV created",
    copyToastMessage: "Your resume was saved as a new CV.",
    copyFailed:
      "Could not save this resume as a new CV. Please try again.",
    confirmTitle: "Replace your existing CV?",
    confirmBody:
      "This will replace the matching sections in your current CV with the imported resume. This action only continues after you confirm.",
    confirmAction: "Replace existing CV",
  },

  // Errors
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "Network error. Check your connection.",
    notFound: "Page not found",
  },

  feedback: {
    floatingLabel: "Feedback",
    title: "Share feedback",
    categoryLabel: "What is this about?",
    ratingLabel: "How does it feel?",
    messageLabel: "What should we know?",
    messagePlaceholder: "Tell us what happened or what would make this better.",
    messageHelper:
      "Minimum 10 characters. Product feedback goes to the Tabbio admin team.",
    placeholders: {
      bug: "Steps to reproduce, expected result, and what actually happened.",
      idea: "Describe your idea and the problem it would solve.",
      confusing: "What were you trying to do, and what got in the way?",
      performance: "Where did it feel slow? Which screen and which action?",
      other: "Tell us what happened or what would make this better.",
    },
    helpers: {
      bug: "Minimum 10 characters. Include steps, expected result, and actual result.",
      idea: "Minimum 10 characters. Tell us the use case your idea solves.",
      confusing: "Minimum 10 characters. Describe what was unclear and where.",
      performance:
        "Minimum 10 characters. Include the screen, action, and how slow it felt.",
      other:
        "Minimum 10 characters. Product feedback goes to the Tabbio admin team.",
    },
    submit: "Send feedback",
    submitting: "Sending...",
    successTitle: "Feedback sent",
    successMessage: "Thanks. We will review it in admin.",
    errorTitle: "Could not send feedback",
    errorMessage: "Please try again in a moment.",
    screenshotLabel: "Screenshot (optional)",
    addScreenshot: "Add screenshot",
    removeScreenshot: "Remove screenshot",
    screenshotUploading: "Uploading...",
    screenshotError: "Could not attach the screenshot. Please try again.",
    screenshotErrors: {
      unsupportedType:
        "That image type isn't supported. Use JPG, PNG, or WebP.",
      tooLarge: "That image is too large. Maximum size is {{size}}MB.",
    },
    charCount: "{{count}} / {{max}}",
    ratingOption: "{{value}} out of 5",
    validation: {
      messageMin: "Please add at least 10 characters.",
      messageMax: "You've reached the {{max}} character limit.",
    },
    categories: {
      bug: "Bug",
      idea: "Idea",
      confusing: "Confusing",
      performance: "Performance",
      other: "Other",
    },
  },

  // Settings
  // Tabbio Developer Platform: the "Sign in with Tabbio" consent screen and the
  // scope catalog it shares with Settings > Connected apps. Scope ids carry a
  // colon (cv:read), which i18next reads as a namespace separator, so they are
  // keyed by the flattened form (cvRead) that `scopeCopyKey` produces.
  oauth: {
    consent: {
      loading: "Loading",
      redirecting: "Taking you back",
      unavailableTitle:
        "Sign in with Tabbio is not available",
      unavailableBody:
        "Sign in with Tabbio is not available in this build. Go back to the app that sent you here and try again later.",
      errorTitle: "This sign-in request cannot be completed",
      errorBody:
        "The app that sent you here is not set up correctly. Go back to it and try again, or contact its support team.",
      goToTabbio: "Go to Tabbio",
      title: "Sign in to {{app}} with Tabbio",
      titleExisting: "{{app}} is already connected",
      subtitle:
        "{{app}} will be able to read the information you allow below, and it stays up to date until you disconnect.",
      signedInAs: "Signed in as",
      permissions: "What {{app}} will receive",
      required: "Required",
      includePhoto: "Include profile photo",
      cvRow: "CV to share",
      cvNone: "No CV selected",
      change: "Change",
      continue: "Continue",
      cancel: "Cancel",
      legal:
        "By continuing, you allow Tabbio to share the items above with {{app}}.",
      privacyPolicy: "Privacy policy",
      terms: "Terms",
      disconnectNote:
        "You can disconnect anytime in Settings, under Connected apps.",
      presets: {
        label: "How much to share",
        signin: "Sign in only",
        cv: "Share my CV",
        full: "Share everything",
      },
    },
    scopes: {
      profile: {
        title: "Name, username and profile photo",
        description:
          "Your name, username, headline, language and public profile link.",
      },
      email: {
        title: "Email address",
        description: "The verified email address on your Tabbio account.",
      },
      cvRead: {
        title: "Your CV",
        description:
          "The CV you choose, kept up to date automatically, as structured data and as a PDF.",
      },
      cvContact: {
        title: "Private contact details",
        description:
          "Phone number, full address, date of birth, nationality and similar personal details from your CV.",
      },
    },
  },
  settings: {
    groups: { account: "Account", activity: "Activity", access: "Access and connections", help: "Help and information" },
    mobileAbout: "Tabbio for {{platform}} · {{version}}",
    feedbackDescription: "Share an idea or tell us about a problem.",
    title: "Settings",
    editProfile: "Edit profile",
    name: "Name",
    categories: {
      account: "Account",
      billing: "Billing",
      preferences: "Preferences",
      notifications: "Notifications",
      agentAccess: "Agent access",
      usage: "Usage & credits",
    },
    usageScreen: {
      limitSection: "Usage limit",
      limitInfoLabel: "About usage limits",
      limitInfo:
        "Credits are shared across agent chat, CV writing, skills and Nova automations. Every action draws from the same pool.",
      monthlyLimit: "Monthly limit",
      dailyLimit: "Daily limit",
      percentRemaining: "{{percent}} remaining",
      unlimited: "Unlimited",
      resetsToday: "Resets today at {{time}}",
      resetsTomorrow: "Resets tomorrow at {{time}}",
      resetsOnWeekday: "Resets {{date}} at {{time}}",
      resetsOn: "Resets {{date}} at {{time}}",
      creditsSection: "Credits",
      creditsRemaining: "Credits remaining",
      creditCount_one: "{{value}} credit",
      creditCount_other: "{{value}} credits",
      footnoteFree: "Free credits reset every day.",
      footnotePaid:
        "Credits reset with your plan each month. The daily limit paces how many you can use in a day.",
      upgradeFree: "Upgrade for a monthly pool",
      upgradePlus: "Upgrade to Pro for a bigger pool",
    },
    agentAccess: {
      permissions: "Permissions",
      activityAndBlocks: "Activity and blocked access",
      runsLoadFailed: "Could not load recent runs",
      undoTitle: "Undo this skill run?",
      undoMessage: "Undo the CV changes made by {{skill}}. This restores an edited CV or removes the new version created by this run.",
      subtitle: "Control who can run skills on your CV and review recent runs.",
      whoCanRun: "Who can run skills",
      who: {
        any: "Any skill",
        verified: "Tabbio-curated skills only",
        default_only: "Tabbio's default skills only",
        nobody: "No skills",
      },
      communityToggle: "Community skills",
      communityToggleDescription:
        "Allow skills shared by the community to run.",
      confirmFullCv: "Confirm before full-CV rewrites",
      confirmFullCvDescription: "Ask before a skill rewrites your whole CV.",
      runLog: "Recent runs",
      runLogEmpty: "No skill runs yet.",
      dryRunBadge: "Preview",
      undo: "Undo",
      undone: "Run undone",
      undoFailed: "Could not undo run",
      blockedSkills: "Blocked skills",
      blockedCreators: "Blocked creators",
      noBlocked: "Nothing blocked.",
      unblock: "Unblock",
      loadFailed: "Could not load agent access settings",
      saveFailed: "Could not save settings",
      runStatus: {
        reverted: "Undone",
        pending: "Pending",
        running: "Running",
        completed: "Completed",
        failed: "Failed",
        blocked: "Blocked",
      },
    },
    accountEdit: {
      title: "Edit account",
      subtitle: "Update your username, email, and password.",
      usernameTitle: "Username",
      usernameDescription: "This is your public handle on Tabbio.",
      usernamePlaceholder: "username",
      usernameHint: "Pick a username that people can find easily.",
      usernameRequired: "Username is required",
      usernameMin: "Username must be at least 3 characters",
      usernameChecking: "Checking availability...",
      usernameAvailable: "Username is available",
      usernameCurrent: "This is your current username",
      usernameConfirmTitle: "Change your username?",
      usernameConfirmMessage:
        "Your link will change from {{oldLink}} to {{newLink}}. Your old link, and any CV links you've already shared, will stop working.",
      usernameConfirmAction: "Change username",
      usernameConfirmCancel: "Cancel",
      usernameInvalid: "Username format is invalid",
      usernameReserved: "This username is reserved",
      usernameReservedWithSuggestion:
        "This username is reserved. Try {{suggestion}}",
      usernameTaken: "This username is already taken",
      usernameTakenWithSuggestion: "This username is taken. Try {{suggestion}}",
      usernameCannotSave: "Choose an available username before saving",
      usernameUpdated: "Username updated successfully",
      saveUsername: "Save username",
      emailTitle: "Email",
      emailDescription:
        "We will send a one-time code to verify your new email.",
      currentEmail: "Current email",
      newEmailLabel: "New email",
      newEmailPlaceholder: "you@example.com",
      sendOtp: "Send verification code",
      otpLabel: "Verification code",
      otpPlaceholder: "Enter code",
      verifyEmail: "Verify and update email",
      emailOtpSent: "Verification code sent",
      emailUpdated: "Email updated successfully",
      emailInvalid: "Enter a valid email address",
      emailSameAsCurrent: "New email must be different from your current email",
      otpRequired: "Verification code is required",
      passwordTitle: "Password",
      passwordModeChecking: "Checking password settings...",
      passwordDescriptionChange: "Change your existing password.",
      passwordDescriptionSet: "Set a password for your account.",
      currentPasswordLabel: "Current password",
      currentPasswordPlaceholder: "Current password",
      newPasswordLabel: "New password",
      newPasswordPlaceholder: "New password",
      confirmPasswordLabel: "Confirm password",
      confirmPasswordPlaceholder: "Confirm new password",
      currentPasswordRequired: "Current password is required",
      currentPasswordIncorrect: "Current password is incorrect",
      passwordMismatch: "Passwords do not match",
      passwordChanged: "Password changed successfully",
      passwordSet: "Password set successfully",
      changePasswordAction: "Change password",
      setPasswordAction: "Set password",
      nameTitle: "Name",
      nameDescription: "This is the display name shown on your profile.",
      namePlaceholder: "Your name",
      nameRequired: "Name is required",
      nameUpdated: "Name updated successfully",
      saveName: "Save name",
      uaePassLocked:
        "This field is locked because the account is linked with UAE PASS.",
      errorGeneric: "Something went wrong. Please try again.",
    },
    signInMethods: {
      title: "Sign-in methods",
      connected: "Connected",
      notConnected: "Not connected",
      connect: "Connect",
      disconnect: "Disconnect",
      working: "Working…",
      passwordSet: "Set",
      passwordNotSet: "Not set. You sign in with a connected account",
      setPassword: "Set password",
      connectedToast: "{{provider}} connected.",
      disconnectedToast: "{{provider}} disconnected.",
      disconnectTitle: "Disconnect {{provider}}?",
      disconnectMessage:
        "You will no longer be able to sign in with {{provider}}. Your other sign-in methods keep working.",
      providers: {
        password: "Password",
        google: "Google",
        apple: "Apple",
      },
      errors: {
        generic: "We could not connect that account. Please try again.",
        emailMismatch:
          "That account uses a different email address than your Tabbio account, so we cannot connect the two.",
        emailMissing:
          "That account did not share an email address, so we cannot match it to your Tabbio account.",
        alreadyLinkedElsewhere:
          "That account is already connected to another Tabbio account.",
        disconnect: "We could not disconnect that account. Please try again.",
      },
    },
    manageCredentials: {
      title: "Manage account",
      description: "Change name, email, password, username, or delete account.",
      changeName: "Change name",
      changeNameDescription: "Update the display name shown on your profile.",
      changeUsername: "Change username",
      changeUsernameDescription: "Update your public handle on Tabbio.",
      changeEmail: "Change email",
      changeEmailDescription: "Update your account email address.",
      changePassword: "Change password",
      changePasswordDescription: "Update or set your account password.",
      deleteAccount: "Delete account",
      deleteAccountDescription: "Permanently remove your account and all data.",
    },
    uaePass: {
      title: "UAE PASS",
      statusLinked: "Verified",
      statusUnlinked: "Not linked",
      linkedDescription:
        "Your account is connected to UAE PASS. Verified identity fields are read-only.",
      unlinkedDescription: "UAE PASS is not connected to this account.",
      unavailable: "Unable to load UAE PASS status right now.",
      linkAction: "Link with UAE PASS",
    },
    mcpAccess: {
      title: "MCP Access",
      enabled: "Token active",
      notCreated: "No token for this scope",
      scope: "Scope",
      scopeHelp: "Choose what this one token can access.",
      personalScope: "Personal profile",
      personalScopeDescription:
        "Profile, CV, applications, and personal knowledge.",
      companyScopes: "Company workspaces",
      companyScopeDescription:
        "Company profile, jobs, candidates, outreach, and approvals.",
      noCompanies: "No company workspaces available for this account.",
      selectScopeRequired: "Select personal access or at least one company.",
      serverUrl: "Server URL",
      token: "Token",
      lastUsed: "Last used {{date}}",
      neverUsed: "Never used",
      oneTimeToken: "One-time token",
      oneTimeWarning:
        "Copy this token now. Tabbio will not show it again after you leave Settings.",
      copied: "Copied",
      copyFailed: "Could not copy",
      urlCopied: "MCP URL copied",
      secretCopied: "MCP token copied",
      create: "Create token",
      created: "MCP token created",
      createFailed: "Could not create MCP token",
      rotate: "Regenerate",
      rotateTitle: "Regenerate MCP token",
      rotateMessage:
        "The current token will stop working immediately. Continue?",
      rotated: "MCP token regenerated",
      rotateFailed: "Could not regenerate MCP token",
      revoke: "Revoke",
      revokeTitle: "Revoke MCP token",
      revokeMessage: "This token will stop working immediately. Continue?",
      revoked: "MCP token revoked",
      revokeFailed: "Could not revoke MCP token",
      pendingApprovals: "Pending approvals",
      approve: "Approve",
      reject: "Reject",
      approved: "MCP action approved",
      rejected: "MCP action rejected",
      approvalFailed: "Could not update MCP approval",
      loadFailed: "Could not load MCP access",
    },
    connectedApps: {
      title: "Connected apps",
      detailTitle: "Connected app",
      subtitle:
        "Apps you signed in to with Tabbio. Change what each one can read, or disconnect it.",
      loading: "Loading",
      loadFailed: "Could not load connected apps",
      emptyTitle: "No connected apps yet",
      emptyBody:
        "When you use Continue with Tabbio on another site, it appears here and you stay in control of what it reads.",
      connectedOn: "Connected {{date}}",
      lastUsed: "Last used {{date}}",
      neverUsed: "Never used",
      notFound: "This connection is no longer available.",
      accessTitle: "Access",
      photoDescription: "Share your profile photo with this app.",
      cvRow: "CV to share",
      cvNone: "No CV selected",
      cvUnavailable:
        "The CV you shared was deleted. Choose a CV so this app can read one again.",
      cvUnavailableShort: "The CV you shared was deleted. Choose a CV",
      chooseCv: "Choose a CV",
      updated: "Access updated",
      updateFailed: "Could not update access",
      disconnect: "Disconnect",
      disconnectApp: "Disconnect {{app}}",
      disconnectTitle: "Disconnect {{app}}?",
      disconnectMessage:
        "{{app}} will lose access to your Tabbio account immediately. You can connect it again later.",
      disconnected: "App disconnected",
      disconnectFailed: "Could not disconnect this app",
    },
    account: "Account",
    email: "Email",
    phone: "Phone number",
    subscription: "Subscription",
    manageSubscription: "Manage subscription",
    manageBilling: "Manage billing",
    freePlan: "Free Plan",
    upgradePro: "Upgrade to Tabbio Pro",
    restorePurchases: "Restore purchases",
    syncSubscription: "Sync subscription",
    refreshSubscription: "Refresh subscription",
    checkingSubscription: "Checking subscription",
    billingUnavailable: "Billing is unavailable right now",
    restoreFailed: "Could not restore purchases",
    restoreFailedMessage:
      "Please try again in a moment. If the problem persists, contact support.",
    syncFailed: "Could not sync subscription",
    syncFailedMessage:
      "Please try again in a moment. If the problem persists, contact support.",
    onboardingCheckoutWrongUser:
      "This checkout return belongs to a different signed-in user. Please start checkout again from onboarding.",
    onboardingCheckoutNotPaid:
      "We could not confirm the paid onboarding checkout yet. Please retry sync or continue with Free.",
    subscriptionScopeInvalid:
      "Unable to determine the subscription scope for this return link. Please reopen the purchase confirmation or sync from Settings.",
    subscriptionSynced: "Subscription synced",
    subscriptionSyncedFromStore: "Subscription synced from {{store}}.",
    subscriptionSyncing: "Syncing subscription",
    subscriptionSyncingDescription: "Checking your billing state now.",
    storeAppStore: "App Store",
    storeGooglePlay: "Google Play",
    storeBillingPortal: "Billing Portal",
    storeUnknown: "Original Store",
    managedInAppStore:
      "Billed through the App Store. Manage or cancel it in Apple subscriptions, including at account.apple.com.",
    managedInGooglePlay:
      "Billed through Google Play. Manage or cancel it in Google Play subscriptions.",
    managedInBillingPortal: "Billed through the Tabbio billing portal.",
    managedInUnknownStore:
      "Billing is managed in the original store for this subscription.",
    subscriptionActive: "Active",
    subscriptionTrial: "Trialing",
    subscriptionEnding: "Renewal off",
    subscriptionExpired: "Expired",
    subscriptionBillingIssue: "Billing issue",
    subscriptionRenewsOn: "Renews on {{date}}.",
    subscriptionTrialEndsOn: "Trial ends on {{date}}.",
    subscriptionAccessUntil: "Access continues until {{date}}.",
    subscriptionExpiredOn: "Access ended on {{date}}.",
    subscriptionBillingIssueDescription:
      "There is a billing issue on this subscription. Sync again after updating the payment method.",
    plansCreditUsage: "Plan & credits",
    plansCreditUsageDescription:
      "See your plan, credit balance, and subscription controls.",
    creditUsageUnavailable: "Action allowances are shown for individual plans.",
    subscriptionPlans: {
      planSets: {
        individual: "Individual",
        employer: "Employer",
      },
      tiers: {
        free: "Free",
        plus: "Plus",
        pro: "Pro",
      },
      descriptions: {
        individual: {
          free: "Build your profile and CV, chat with the career agent on daily free credits, and preview what Plus and Pro unlock.",
          plus: "10,000 credits a month for smarter AI, Skills, exports, analytics, and connected apps.",
          pro: "Everything in Plus, with 100,000 monthly credits, Career agent automation, and advanced career intelligence.",
        },
        employer: {
          free: "Unlimited free members, one hiring page, one job post, shared basic AI pool.",
          plus: "50 AI actions/day per seat",
          pro: "200 AI actions/day per seat",
        },
      },
      upgradeCtas: {
        individual: {
          plus: "Upgrade to Plus",
          pro: "Upgrade to Pro",
        },
        employer: {
          plus: "Add Plus seats",
          pro: "Add Pro seats",
        },
      },
    },
    monthlyCredits: "Monthly credits",
    dailyCredits: "Daily credits",
    dailyCreditsTopUp: "Daily top-up",
    creditsLeft: "{{value}} remaining",
    creditCount: "{{value}} used",
    creditsUsed: "Included use",
    creditsResetOn: "Resets on {{date}}",
    resetsDaily: "Resets daily",
    resetsMonthly: "Resets monthly",
    needMoreCredits: "Want to do more?",
    upgradeForMoreCredits:
      "Upgrade for more AI use and additional career tools.",
    needMoreAccess: "Want to do more?",
    upgradeForMoreAccess:
      "Upgrade to Plus for stronger applications, or Pro for Career agent automation.",
    allowances: {
      careerChat: "Career chat replies",
      skillRuns: "Skill runs",
      novaPreviews: "Career agent plan previews",
      cvPreviews: "CV section previews",
      cvFitReports: "CV fit reports",
      aiActions: "AI actions",
      remaining: "{{remaining}} of {{limit}} remaining",
      fairUse:
        "Your plan includes generous use protected by fair-use safeguards. Usage is shown as clear product actions.",
      resets: {
        day: "Resets daily",
        week: "Resets weekly",
        month: "Resets monthly",
      },
    },
    upgradePlan: "Upgrade plan",
    usage: "Usage",
    thisMonth: "This month",
    unlimitedCredits: "Unlimited",
    notAvailable: "Not available",
    preferences: "Preferences",
    theme: "Theme",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    language: "Language",
    controls: {
      publicPageOn: "Public page on",
      publicPageOff: "Public page off",
      "audience": "Who can see my main CV?",
      "audienceHint": "Choose whether your profile is public, visible to employers, or private.",
      "contact": "Photo and contact details",
      "contactHint": "Choose who can see your photo, email, and phone number.",
      "sharing": "Links and people",
      "sharingHint": "Review private links and invitations for your main CV.",
      "audienceOptions": {
        "public": "Anyone can view your main CV. It may appear in search engines.",
        "employers": "Recruiters can discover you. Your public pages are hidden.",
        "private": "Your public pages and recruiter discovery are hidden."
      },
      "cvSharingHint": "Public pages, private links, and invitations have separate controls.",
      "cvData": "Your CVs",
      "mainDataHint": "Edit or remove personal details, experience, and skills.",
      "savedCvHint": "Delete this version and keep your other CVs.",
      "identityConnections": "Identity and connections",
      "uaePassHint": "Review your verified identity or disconnect UAE PASS.",
      "unavailable": "Unavailable",
      "connectionsHint": "Manage services and apps that can access your account.",
      "memoryAccount": "Memory and account",
      "memoryHint": "Review or clear what the assistant remembers.",
      "accountHint": "Permanently remove your account and the data it owns.",
      "reviewMemory": "Review saved information and memory settings.",
      "chooseSection": "Choose a section",
      "appPrivacy": "App privacy",
      "appConnections": "App and connections",
      "settingsHint": "Manage your account, privacy, and the services you use.",
      "allSettings": "All Settings"
    },
    cvPrivacy: {
      description:
        "Review who can see your CVs, contact details, and shared links.",
      guide: "How to manage CV privacy",
      mainCv: "Main CV and profile",
      mainDescription:
        "Public makes your main CV and profile available on the web and eligible for search indexing. Employers only keeps you discoverable to recruiters. Private hides public pages and recruiter discovery.",
      audience: "Who can see my main CV?",
      photo: "Profile photo",
      phone: "Phone number",
      visibility: {
        public: "Public",
        employers: "Employers only",
        private: "Private",
        hidden: "Hidden",
      },
      publishTitle: "Make your main CV public?",
      publishBody:
        "Your main CV and profile will be accessible without signing in. Search engines may index them. Review your visible details before continuing.",
      publishAction: "Make public",
      independentAccess:
        "Private links, invited people, and tailored CV public pages have separate access controls. Changing your main CV visibility does not revoke them. Copies already downloaded or sent to employers are outside these controls.",
      otherCvs: "Your other CVs",
      noCvs: "You have no other saved CVs.",
      publicPageOn: "Public page is on. Anyone with its address can open it.",
      publicPageOff:
        "Public page is off. Review shared links and invited people below for any remaining access.",
      unpublish: "Turn off public page",
      unpublishTitle: "Turn off this public page?",
      unpublishBody:
        "The public page for “{{title}}” will stop opening. Private links and invited people keep their separate access until you revoke it below.",
      reviewAccess: "Review links and people",
      hideAccess: "Hide access details",
      shareLinks: "Private share links",
      noLinks: "No private share links.",
      shareLink: "Share link",
      anyoneWithLink: "Anyone with this link",
      expired: "Expired",
      downloadsOn: "Downloads allowed",
      downloadsOff: "Downloads disabled",
      people: "Invited people",
      noPeople: "No active or pending invitations.",
      sharedAccess: "Shared access",
      pending: "Invitation pending",
      active: "Access active",
      revoke: "Revoke access",
      revokeTitle: "Revoke this access?",
      revokeBody:
        "This link or invitation will stop granting access. This does not remove copies someone has already downloaded or received.",
    },
    employerAccess: {
      title: "Company access to future profile changes",
      description:
        "Applications share the saved CV you submitted. A separate permission lets a named company read the current career profile shown below, including future changes. Revoking blocks future reads; copies already saved by a company are unaffected.",
      loading: "Loading disclosure…",
      unavailable:
        "The disclosure preview is unavailable. Try again before applying.",
      showPreview: "Review what the company receives",
      hidePreview: "Hide disclosure preview",
      empty: "No eligible recipient companies yet.",
      active: "Live access enabled",
      inactive: "Saved submissions only",
      revoke: "Revoke live access",
      review: "Review live-profile permission",
      consent:
        "Allow {{company}} to read these profile fields and their future updates. This does not change any saved application CV.",
      grant: "Allow continuing profile access",
      cancel: "Cancel",
      retryReview:
        "Your profile or CV may have changed. Review the refreshed disclosure and try again.",
      fields: {
        additional: "Additional field",
        name: "Name",
        title: "Title",
        subtitle: "Details",
        summary: "Summary",
        bio: "Biography",
        about: "About",
        location: "Location",
        email: "Email",
        contactEmail: "Contact email",
        phone: "Phone",
        image: "Photo",
        avatarUrl: "Photo",
        photo: "Photo included",
        company: "Company",
        school: "School",
        degree: "Degree",
        fieldOfStudy: "Field of study",
        grade: "Grade",
        description: "Description",
        startDate: "Start date",
        endDate: "End date",
        current: "Current",
        isCurrent: "Current",
        skills: "Skills",
        interests: "Interests",
        achievements: "Achievements",
        issuer: "Issuer",
        issueDate: "Issue date",
        expirationDate: "Expiry date",
        credentialId: "Credential number",
        credentialUrl: "Credential link",
        mediaUrl: "Media link",
        mediaType: "Media type",
        link: "Link",
        date: "Date",
        technologies: "Technologies",
        tags: "Tags",
        locationType: "Work location type",
        employmentType: "Employment type",
        url: "Link",
        level: "Level",
        language: "Language",
        proficiency: "Proficiency",
      },
    },
    subjectReview: {
      title: "Review withheld account content",
      description:
        "Some older records lack reliable subject context. Request an independent review of records about you. Your statement alone does not establish ownership.",
      unavailable:
        "Review is currently unavailable. Unverified content remains withheld from exports.",
      kind: "Record type",
      empty: "No eligible records on this page.",
      record: "{{kind}} · {{id}}",
      more: "Load more",
      statement: "Explain why this record is about you (10–1000 characters)",
      request: "Request independent review",
      failed:
        "The review could not be loaded or sent. Your draft is retained. Try again.",
      refresh: "Refresh review status",
      downloadAgain:
        "Approval applies to the exact reviewed content. Download your account export again to include eligible content. Changed records require a fresh review.",
      rerunExport: "Download account export again",
      states: {
        pending: "Awaiting review",
        needs_evidence: "Independent evidence needed",
        approved_personal: "Approved for your exact content",
        denied: "Not approved",
        stale: "Content or scope changed; request again",
      },
      classes: {
        NovaAutomation: "Automation",
        NovaAutomationVersion: "Automation version",
        NovaTrigger: "Automation trigger",
        NovaStep: "Automation step",
        NovaRun: "Automation run",
        NovaAuditLog: "Automation change summary",
        NovaWebhookSubscription: "Automation event mapping",
        Episode: "Career memory",
        SemanticFact: "Career fact",
        GraphNode: "Career knowledge item",
        GraphEdge: "Career knowledge relationship",
        KnowledgeAsset: "Knowledge document",
        AgentArtifact: "Agent document",
        ArtifactVersion: "Document version",
        PublishedSite: "Published site description",
        JobApplication: "Job application CV snapshot",
        TalentNetworkSubmission: "Talent submission CV snapshot",
        SubmissionReceipt: "Submission receipt CV snapshot",
        ApplicationStudioProject: "Career tools project",
        ApplicationStudioArtifact: "Career tools document",
        MastraThreads: "Legacy conversation title",
        MastraMessages: "Legacy conversation text",
        MastraWorkingMemory: "Shared career memory",
        CV: "Legacy CV content",
        CVLanguageVariant: "CV language edition",
        CvRevisionContent: "Frozen CV revision document",
        CvImportEvidence: "CV import source and parse evidence",
      },
    },
    dataManagement: {
      privacyAndData: "Privacy and data",
      title: "Data management",
      description:
        "Choose the CVs, saved information, and connections you want to keep or remove.",
      guide: "How to manage and delete your data",
      loadFailed:
        "We could not load this information. Try again before making changes.",
      retry: "Try again",
      mainCvDescription:
        "Your main CV uses your profile information. Choose a section to edit or remove specific details. Other CVs may reuse these details; saved copies can keep their own information.",
      manageMainCv: "Choose main CV data to manage",
      sections: {
        header: "Name, photo, and contact details",
        summary: "Summary",
        experience: "Work experience",
        education: "Education",
        certifications: "Certifications",
        highlights: "Career highlights",
        skills: "Skills",
        languages: "Languages",
      },
      savedCvs: "Delete a saved CV",
      savedCvsDescription:
        "Delete individual CVs while keeping your account and other CVs.",
      deleteCv: "Delete this CV",
      deleteCvTitle: "Delete “{{title}}”?",
      deleteCvBody:
        "This permanently deletes “{{title}}” and its CV sharing links and invitations. It cannot be undone. Your main profile and other CVs remain. Copies already sent or downloaded are not recalled.",
      autoApplyWarning:
        "This CV is selected for Auto Match. Deleting it clears that selection; choose another CV before using it again.",
      removed: "Data removed",
      actionFailed: "We could not complete this change. Please try again.",
      disconnect: "Disconnect UAE PASS",
      disconnectTitle: "Disconnect UAE PASS from Tabbio?",
      disconnectBody:
        "This removes the UAE PASS connection, verification badge, and stored UAE PASS identity record from Tabbio. Your Tabbio account, name, email, and CV details remain. It does not delete your UAE PASS account. You can continue signing in with your Tabbio email.",
      disconnected: "UAE PASS disconnected",
      memoryTitle: "AI memory",
      clearMemory: "Clear AI memory",
      clearMemoryTitle: "Clear saved AI memory?",
      clearMemoryBody:
        "Remove the information saved in AI working memory. Your chats, CVs, and account remain. Future conversations can save new memories.",
      connectionsTitle: "Connected services",
      connectionsDescription:
        "Review connected services and app access. Disconnect a service or revoke a token when you no longer want it to have access.",
      accountTitle: "Delete account and account data",
      accountDescription:
        "Permanently delete your Tabbio account and the data it owns. Read the deletion steps and confirm only when you are ready. This cannot be undone.",
    },
    privacy: {
      title: "Privacy",
      viewCvsPrivately: "View CVs privately",
      viewCvsPrivatelyDescription:
        "When you open someone's CV while signed in, they see an anonymous visitor instead of your name and photo.",
      description:
        "Choose whether Tabbio may collect optional product analytics.",
      consentTitle: "Your privacy choices",
      consentBody:
        "Optional analytics help us improve Tabbio, and optional marketing lets the ad platforms we use measure our campaigns. The app works normally without either, and you can change this anytime in Settings.",
      marketing: "Marketing",
      marketingDescription:
        "Lets the advertising platforms we use (such as Google and Meta) measure whether our campaigns brought you here and show more relevant ads. Off until you allow it. Your CV, chat content and contact details are never shared with them.",
      productAnalytics: "Product analytics",
      productAnalyticsDescription:
        "Share limited product usage and screen activity so we can understand and improve Tabbio. This is product analytics only, not your personal CV or chat data. CV content, chat messages, and prompts are not included.",
      allowAnalytics: "Allow",
      continueWithoutAnalytics: "Decline",
      analyticsOn: "Analytics on",
      analyticsOff: "Analytics off",
      manage: "Manage privacy choices",
      save: "Save choices",
      saved: "Privacy choices saved",
      updateFailed: "Could not update privacy choices",
      yourData: "Your data",
      downloadData: "Download my data",
      downloadDataDescription:
        "A copy of your account, profile, CVs, applications, shares and the messages you sent, as a JSON file.",
      downloadDataPreparing: "Preparing your file…",
      downloadDataReady: "Your data file is ready.",
      downloadDataFailed:
        "Could not prepare your data. Please try again in a moment.",
    },
    notifications: "Notifications",
    dataControls: "Data controls",
    notificationPreferences: {
      marketingEmails: "Marketing emails",
      marketingEmailsDescription:
        "Product updates, offers, and tips from Tabbio.",
      systemEmails: "Product and lifecycle emails",
      systemEmailsDescription:
        "Optional product guidance, recruiter messages, and application updates.",
      pushNotifications: "Push notifications",
      pushNotificationsDescription:
        "Mobile alerts for messages, job matches, and account activity.",
      loadFailed: "Could not load notification preferences",
      updateFailed: "Could not update notification preference",
      saving: "Saving notification preferences",
    },
    about: "About",
    helpCenter: "Help Center",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    accountDeletion: "Delete account",
    accountDeletionDescription:
      "Manage privacy, notifications, and account deletion options.",
    accountDeletionFlow: {
      manageAccount: "Manage account",
      accountControls: "Manage account",
      accountControlsDescription:
        "Privacy, notifications, and account deletion controls.",
      dangerZone: "Account deletion",
      deleteAccountTitle: "Delete account",
      deleteAccountDescription:
        "Permanently delete your account and associated data. This cannot be undone.",
      beforeYouGo: "Before deletion",
      beforeYouGoBody:
        "We can often fix the reason you want to leave without deleting your account.",
      reasonStepTitle: "Why are you deleting your account?",
      reasonStepBody:
        "Choose the closest reason. It is used only while processing this deletion and is not retained afterward.",
      reasons: {
        too_many_notifications: {
          label: "Too many notifications",
          description:
            "Emails, push alerts, or job match notifications feel noisy.",
        },
        privacy_concern: {
          label: "Privacy concern",
          description:
            "You are worried about profile, CV, or account visibility.",
        },
        not_useful: {
          label: "Not useful for me",
          description: "Tabbio is not helping with your current workflow.",
        },
        found_job: {
          label: "I found a job",
          description: "You no longer need job search or profile tools.",
        },
        cost: {
          label: "Plan or billing",
          description: "Pricing, renewal, or billing is the main reason.",
        },
        other: {
          label: "Something else",
          description: "Your reason is not listed here.",
        },
      },
      protectionTitle: "Your account is protected",
      protectionBody:
        "Your profile, CV, applications, and workspace data stay behind your authenticated account controls.",
      notificationsTitle: "Too many notifications?",
      notificationsBody:
        "Use Settings > Notifications to turn off marketing emails, system emails, or push alerts.",
      jobsTitle: "Job match noise",
      jobsBody:
        "Job match alerts come from your jobs and Automation preferences. You can adjust those instead of deleting the account.",
      billingTitle: "Billing and plan control",
      billingBody:
        "Plans and store subscriptions can be managed from Settings > Billing before you close the account.",
      retention: {
        too_many_notifications: {
          title: "You can quiet notifications without deleting your account",
          body: "Normal emails and push alerts are in Settings > Notifications. Job match and Automation alerts can be adjusted from your job preferences.",
        },
        privacy_concern: {
          title: "Your account visibility is controlled",
          body: "Your profile, CV, and workspace data stay protected behind your authenticated account. Public publishing can stay disabled.",
        },
        not_useful: {
          title: "You can keep the account paused",
          body: "You can sign out, reduce alerts, and keep your CV and history available for when you need it again.",
        },
        found_job: {
          title: "You can keep Tabbio for future career updates",
          body: "Your CV, conversations, and profile can stay available for future roles without active job alerts.",
        },
        cost: {
          title: "Billing can be managed before deletion",
          body: "Plans and store subscriptions are controlled from Settings > Billing. You can cancel billing without deleting your account.",
        },
        other: {
          title: "Your account can stay protected while you decide",
          body: "You can adjust notifications, billing, and visibility first. Continue only if you still want the account deleted.",
        },
      },
      finalWarningTitle: "Your account will be deleted",
      finalWarningBody:
        "Continuing permanently deletes your account, revokes active sessions and connections, and removes its associated data. This cannot be undone.",
      keepAccount: "Keep my account",
      continueDeletion: "Continue deletion",
      finalStepTitle: "Final confirmation",
      finalStepBody:
        "Your account will be deleted now. If you later sign up with the same email, Tabbio creates a new account and does not restore this data.",
      searchResultsTitle: "About Google search results",
      searchResultsBody:
        "Tabbio removes or blocks its source page as part of deletion. Google may temporarily keep an outdated result while its index refreshes.",
      searchResultsHelper:
        "Only if a Tabbio result still appears after its source page is gone, you can ask Google to refresh it. Google handles the request and may take a few days.",
      searchResultsAction: "Open Google’s refresh tool",
      reasonTitle: "Reason",
      reasonHelp: "Tell us why you want to leave.",
      reasonPlaceholder: "What made you decide to delete your account?",
      detailsLabel: "Anything else? (optional)",
      detailsPlaceholder:
        "Add optional details to use only while processing this deletion.",
      otherReasonLabel: "Tell us the reason",
      otherReasonPlaceholder:
        "Please describe why you want to delete your account.",
      otherReasonRequired: "Please add a short reason.",
      reasonCounter: "{{count}}/{{max}}",
      acknowledge: "I understand this will delete my account.",
      acknowledgeDescription:
        "This permanently deletes the account and its associated data, revokes active sessions and connections, and signs you out.",
      requestAction: "Delete account permanently",
      requesting: "Deleting account...",
      deleteAction: "Delete account",
      deleting: "Deleting account...",
      successTitle: "Account deleted",
      successMessage:
        "Your account and associated data were deleted. Signing up later with the same email creates a new account.",
      deletedTitle: "Account deleted",
      deletedMessage:
        "Your account and associated data were deleted. The same email can now be used for a fresh signup.",
      deletedMessageWithReceipt:
        "Your account and associated data were deleted. Your deletion receipt: {{receiptId}}",
      errorTitle: "Could not delete account",
      errorMessage: "Please try again in a moment.",
    },
    subscriptionValue: {
      title: "Your plan",
      subtitle: "Your benefits and usage in one place.",
      activity: "Your recent activity",
      benefits: "Included in {{plan}}",
      allowances: "Your available actions",
      remaining: "{{remaining}} of {{limit}} left",
      resets: "Resets {{date}}",
      usageUnavailable:
        "Your plan details are unavailable. Try syncing your subscription.",
      period: {
        day: "Today (UTC)",
        week: "This week (UTC)",
        month: "This month (UTC)",
      },
      actions: {
        chat: "Career chat replies",
        skills: "Skill runs",
        cvPreviews: "CV section previews",
        fitReports: "CV fit reports",
        taskPreviews: "Task previews",
      },
      benefit: {
        cv: {
          title: "A CV for the role",
          body: "Tailor your CV to a job before you apply.",
        },
        automation: {
          title: "Keep tasks moving",
          body: "Set up recurring tasks with Nova.",
        },
        interview: {
          title: "Go in prepared",
          body: "Practice interview questions for your target role.",
        },
        letters: {
          title: "A stronger introduction",
          body: "Draft a cover letter for the job you want.",
        },
        analytics: {
          title: "Understand your audience",
          body: "See where visits to your public CV come from.",
        },
        ranking: {
          title: "Find relevant candidates",
          body: "Rank candidates against your role requirements.",
        },
        screening: {
          title: "Review candidates together",
          body: "Screen a group of candidates against a role.",
        },
        outreach: {
          title: "Start the conversation",
          body: "Reach out to candidates from your workspace.",
        },
        team: {
          title: "See your team’s activity",
          body: "Use team analytics to review hiring activity.",
        },
      },
    },
    cancelFlow: {
      cancelPlan: "Cancel subscription",
      sheetTitle: "Your subscription",
      title: "Before you leave {{plan}}",
      body: "Here’s what you can still do with your plan.",
      keepPlan: "Keep {{plan}}",
      reasonTitle: "What could be better?",
      reasonOptional: "Optional",
      noReason: "Prefer not to say",
      reasons: {
        not_needed_now: "I don’t need it right now",
        too_expensive: "It costs too much",
        hard_to_use: "I’m not sure how to use it",
        missing_feature: "A feature is missing",
        quality_issue: "The results need work",
        switching: "I’m using another tool",
        other: "Something else",
      },
      response: {
        not_needed_now:
          "Between opportunities? You can still update your CV and prepare for your next move.",
        too_expensive:
          "You can review available plans with your billing provider. The price and billing terms are shown there before any change.",
        hard_to_use:
          "Try one specific task in Skills, or ask the agent to help with a CV, a job, or an interview.",
        missing_feature:
          "You can share the feature you need in Settings → Feedback. You can continue cancelling without sending feedback.",
        quality_issue:
          "You can report a specific result in Settings → Feedback. You can continue cancelling without sending feedback.",
        switching:
          "Thanks for giving Tabbio a try. You can come back whenever you need it.",
        other:
          "Thanks for giving Tabbio a try. You can come back whenever you need it.",
      },
      employerResponse:
        "Use your workspace to review candidates, prepare a role, or reach out to your next hire.",
      accessUntil: "If you cancel, your current access ends on {{date}}.",
      accessUnknown: "Your billing provider will confirm when your access ends.",
      trialAccess:
        "Your billing provider will confirm when trial access ends. It may end as soon as you cancel.",
      freeAfter:
        "After that, Free plan limits apply. Cancelling your subscription does not delete your account or CVs.",
      employerAfter:
        "After that, Free workspace limits apply. Cancelling your subscription does not delete your account.",
      providerNote: {
        app_store: "Cancellation is completed in Apple’s subscription settings.",
        play_store: "Cancellation is completed in Google Play.",
        portal: "Cancellation is completed in your billing portal.",
      },
      continueCta: {
        app_store: "Continue to Apple subscriptions",
        play_store: "Continue to Google Play",
        portal: "Continue to billing portal",
      },
      opening: "Opening…",
      handoffError: "We couldn’t open subscription settings. Please try again.",
    },
    signOutTitle: "Sign Out",
    signOutMessage: "Are you sure you want to sign out?",
    aiMemory: "AI & Memory",
    viewMemory: "View AI Memory",
    editMemory: "Edit AI Memory",
    clearMemory: "Clear AI Memory",
    clearMemoryConfirm:
      "This will erase everything the AI has learned about you. Continue?",
    memoryCleared: "AI memory cleared",
    memorySaved: "AI memory updated",
    noMemory:
      "The AI hasn't learned anything about you yet. Start a conversation first.",
    memoryGraph: {
      title: "Memory graph",
      subtitle:
        "{{count}} saved memory fields connected to your CV, MCP access, and app connections.",
      user: "Memory",
      cv: "CV",
      mcp: "MCP",
      apps: "Apps",
      webSubtitle:
        "Everything Tabbio remembers about you: your CV, skills, jobs, connectors, and agents: as a connected graph.",
      listTitle: "Memory",
      listSubtitle:
        "What Tabbio remembers about you: your CV, skills, jobs, connections, and preferences. Open an item to review it or forget it.",
      nativeSubtitle: "{{count}} things Tabbio remembers, grouped by type.",
      loading: "Loading your memory graph…",
      loadError: "Couldn't load your memory graph.",
      empty: "Nothing in your memory graph yet. Chat with Tabbio to build it.",
      rebuild: "Refresh memory graph",
      rebuildSuccess: "Memory graph refreshed",
      rebuildError: "Couldn't refresh your memory graph.",
      searchPlaceholder: "Search memory…",
      viewGraph: "Graph view",
      viewList: "List view",
      clearFilters: "Clear filters",
      window: {
        all: "All time",
        d7: "7 days",
        d30: "30 days",
        d365: "1 year",
      },
      connections: "{{count}} connections",
      noConnections: "No connections",
      showAll: "Show all {{count}}",
      showLess: "Show less",
      deleteNode: "Forget this",
      deleteTitle: "Forget from memory?",
      deleteMessage:
        'Tabbio will forget "{{name}}". You can undo this for 30 days.',
      deleteError: "Couldn't forget this node.",
      deletedToast: 'Forgot "{{name}}"',
      restoreError: "Couldn't restore this node.",
      revokeTitle: "Revoke access?",
      revokeMessage: "{{name}} will lose access to your memory.",
      revokeConfirm: "Revoke",
      revokeError: "Couldn't revoke access.",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      zoomReset: "Reset view",
      capTitle: "Showing {{shown}} of {{total}} memories",
      capSubtitle:
        "Upgrade to Pro to keep 1 year of memory and up to 100,000 nodes.",
      capCta: "Upgrade to Pro",
      advancedTitle: "Advanced",
      advancedRowSubtitle: "Raw working memory the agent keeps between chats",
      advancedDescription:
        "Raw working-memory JSON the agent keeps between chats. Structured JSON only.",
      advancedInvalidJsonError:
        "This isn't valid JSON. Check for missing commas, quotes, or brackets, then try again.",
      advancedInvalidStructureError:
        "Working memory must be a JSON object. Put the fields inside { } and try again.",
      advancedValidationError:
        "Some memory fields use an unsupported value. Check the field names and value types, then try again.",
      advancedNetworkError:
        "Couldn't reach Tabbio. Check your connection and try saving again.",
      advancedSaveError:
        "Couldn't save your memory changes. Try again. If this keeps happening, refresh the page.",
      advancedClearError: "Failed to clear",
      nodeTypes: {
        cv: "CV",
        section: "Section",
        skill: "Skill",
        experience: "Experience",
        education: "Education",
        certification: "Certification",
        job: "Job",
        application: "Application",
        company: "Company",
        recruiter: "Recruiter",
        connector: "Connector",
        agent: "Agent",
        automation: "Automation",
        episode: "Memory",
        fact: "Fact",
        preference: "Preference",
        knowledge_asset: "Asset",
      },
      edgeTypes: {
        CV_HAS_SECTION: "Has section",
        CV_HAS_SKILL: "Has skill",
        SECTION_USES_SKILL: "Uses skill",
        USER_HAS_EXPERIENCE: "Experience",
        USER_HAS_EDUCATION: "Education",
        USER_HAS_CERTIFICATION: "Certification",
        USER_APPLIED_JOB: "Applied",
        USER_SAVED_JOB: "Saved",
        JOB_MATCHED_USER: "Matched",
        JOB_REQUIRES_SKILL: "Requires skill",
        JOB_AT_COMPANY: "At company",
        RECRUITER_WORKS_AT_COMPANY: "Works at",
        AGENT_HAS_ACCESS: "Has access",
        AGENT_GENERATED_EPISODE: "Generated",
        AUTOMATION_READS_NODE: "Reads",
        AUTOMATION_WRITES_NODE: "Writes",
        CONNECTOR_FEEDS_NODE: "Feeds",
        FACT_DERIVED_FROM_EPISODE: "Derived from",
        PREFERENCE_OF_USER: "Preference",
        EPISODE_ABOUT_NODE: "About",
      },
      card: {
        remembered: "Remembered",
        recalled: "Recalled from memory",
        recallEmpty: "Nothing relevant found in memory.",
        more: "+{{count}} more",
        actionLabel: "Memory",
        confirm: "Apply this change to your memory?",
        forgetNodeTitle: "Forget from memory",
        revokeAccessTitle: "Revoke agent access",
        forgetTitle: "Forget memory",
        forgotNode: "Forgotten",
        revokedAccess: "Access revoked",
        forgot: "Forgotten",
      },
    },
  },

  // Public Profile Builder
  publicProfile: {
    title: "Profile",
    defaultName: "Your Name",
    defaultHeadline:
      "Add your bio and social links to complete your public profile",
    joinOnTabbio: "Join me on Tabbio",
    comingSoon: "Coming soon",
    addCollection: "Add collection",
    addBlock: "Add block",
    archive: "Archive >",
    activeBlocks: "active blocks",
    setup: "set up",
    add: "Add",
    preview: "Preview",
    previewNotPublishedTitle: "Not yet visible to the public",
    previewNotPublishedHint: "Toggle visibility in Settings to publish",
    previewSurface: {
      upgrade: "Upgrade",
      website: "Website",
      message: "Message",
      joinOwner: "Join {{name}} on Tabbio",
      cookiePreferences: "Cookie Preferences",
      report: "Report",
      privacy: "Privacy",
      webCv: "CV",
      servicesCount: "{{count}} active service",
      servicesCount_plural: "{{count}} active services",
      projectsCount: "{{count}} featured project",
      projectsCount_plural: "{{count}} featured projects",
      teamCount: "{{count}} public member",
      teamCount_plural: "{{count}} public members",
      getDirections: "Get directions",
      openMap: "Open map",
      contactForm: "Contact form",
      openForm: "Open form",
      searchRoles: "Search roles...",
      location: "Location",
      type: "Type",
      apply: "Apply",
      applyNow: "Apply Now",
      noRoleMatches: "No roles match your filters.",
      noOpenRoles: "There are no public open roles right now.",
      talentPoolCta: "Don't see your role? Join our talent pool.",
      moreRoleOptions: "More options for {{title}}",
      remote: "Remote",
      hybrid: "Hybrid",
      onSite: "On-site",
      discord: "Discord",
      spotify: "Spotify",
    },
    style: "Style",
    editSheetTitle: "Edit profile",
    tapPhotoToChange: "Tap photo to change",
    locationHint: "tappable in preview",
    websiteHint: "used by your Website block",
    saved: "Profile saved",
    saveFailed: "Could not save profile",
    fields: {
      name: "Name on CV",
      bio: "Bio",
      website: "Website",
      location: "Location",
    },
    validation: {
      nameRequired: "Name on CV is required",
      bioTooLong: "Bio is too long (max {{max}} characters)",
      websiteInvalid: "Enter a valid website URL",
    },
    checklist: {
      title: "Setup checklist",
      items: {
        name_bio: "Add your name and bio",
        profile_image: "Add your profile image",
        socials: "Add your socials",
        important_links: "Add your important links",
        customize_design: "Customize your design",
        share_tabbio: "Share your Tabbio",
      },
    },
    addSheet: {
      title: "Add block",
      subtitle: "Build your public page with reusable blocks",
      mode: {
        individual: "Individual",
        company: "Company",
      },
      searchPlaceholder: "Search blocks",
      smartAdd: {
        placeholder: "Paste a link to a website or Google Maps",
        invalid:
          "Paste a full https:// link. Video links arrive in a later update.",
        update: "Update",
      },
      company: {
        title: "Add a block",
        subtitle: "Add one useful part to your Company page.",
        combinedPlaceholder: "Paste a link or search blocks",
        basicsGroup: "Basics",
        linkLabel: "Link",
        noMatches: "No blocks match your search.",
      },
      suggested: "Suggested for you",
      explore: "Explore by goal",
      backExplore: "Back to categories",
      groups: {
        profile: "Profile",
        video: "Video",
        document: "Document",
        audio: "Audio",
        contact: "Contact",
        commerce: "Commerce",
        text: "Text",
        hiring: "Hiring",
        other: "Other",
      },
    },
    design: {
      title: "Style",
      subtitle: "Theme, accent and button settings",
      previewTitle: "Live preview",
      tabs: {
        themes: "Themes",
        accents: "Accents",
        buttons: "Buttons",
      },
      buttonStyle: "Button style",
      buttonShape: "Button shape",
      buttonStyles: {
        fill: "Fill",
        outline: "Outline",
        soft: "Soft",
        glass: "Glass",
      },
      buttonShapes: {
        square: "Square",
        rounded: "Rounded",
        pill: "Pill",
      },
      themes: {
        tabbio: "Tabbio",
        air: "Air",
        midnight: "Midnight",
        galaxy: "Galaxy",
        glass: "Glass",
        aurora: "Aurora",
        mono: "Mono",
        sunset: "Sunset",
        ocean: "Ocean",
      },
    },
    contactSheet: {
      title: "Add contact",
      subtitle: "Choose the channels you want people to reach you on",
    },
    socialSheet: {
      // Not "social link": the sheet also holds the company website, so every
      // outbound link on the page is added from one place.
      title: "Add link",
      subtitle: "Add your username and we build the link for you",
    },
    previewFailed:
      "Preview failed to load. Check that the public site and the API are running.",
    linkPlaceholders: {
      handle: "username",
    },
    linkErrors: {
      handle: "Enter a valid username",
      phone: "Enter a valid phone number",
      email: "Enter a valid email address",
      url: "Enter a full https:// link",
      slack: "Enter a Slack link (slack.com/…)",
    },
    settingsSheet: {
      title: "Settings",
      subtitle: "Privacy and profile controls",
      betaSubtitle:
        "Public profile publishing stays disabled in beta until the beta web rollout is ready.",
      privacyTitle: "Privacy",
      profileVisibility: "Profile",
      photoVisibility: "Photo",
      photoVisibilityHint:
        "Hidden removes the real image URL and uses a neutral placeholder.",
      emailVisibility: "Email",
      phoneVisibility: "Phone",
      visibilityPublic: "Public",
      visibilityEmployers: "Employers",
      visibilityPrivate: "Private",
      visibilityHidden: "Hidden",
      profileVisibilityHint:
        "Public creates a searchable web page that search engines may index. Employers only hides public routes but keeps you discoverable to recruiters.",
      visibility: {
        public: "Public",
        employers: "Employers only",
        private: "Private",
      },
      profileUrl: "Profile URL",
      showContactChannels: "Show contact channels publicly",
      showContactChannelsHint:
        "Let visitors see the direct contact methods you enabled.",
      hideBranding: "Hide Tabbio branding",
      // The switch affects the LIVE public page, not just the preview; the
      // old "in preview mode" wording was wrong. Free on every plan, so the
      // hint deliberately names no tier.
      hideBrandingHint:
        "Removes the “Join on Tabbio” footer from your live public page.",
      sensitiveContent: "Sensitive content warning",
      sensitiveContentHint: "Show a warning before opening sensitive links.",
    },
    teamSheet: {
      title: "Team",
      subtitle: "Curate the public people shown on your company profile",
      members: "Team members",
      addMember: "Add team member",
      emptyTitle: "No team members yet",
      emptySubtitle:
        "Add a public team member to show the people behind the company.",
      memberFallback: "Team member",
      roleFallback: "Role",
      namePlaceholder: "Name",
      rolePlaceholder: "Role",
      bioPlaceholder: "Short bio",
      linkedinPlaceholder: "LinkedIn URL",
      websitePlaceholder: "Website URL",
      validation: {
        nameRequired: "Name is required",
        roleRequired: "Role is required",
      },
      memberCount: "Member {{count}}",
    },
    company: {
      pageTitle: "Company page",
      defaultHeadline: "We're hiring! Join {{name}}.",
      footerTitle: "Tabbio footer",
      fields: {
        tagline: "One-line bio",
        taglinePlaceholder: "One line about what your company does",
        industry: "Industry",
        industryPlaceholder: "Select industry",
        slug: "Public page address",
        slugPlaceholder: "acme",
      },
      removeLogo: "Remove logo",
      removeLogoTitle: "Remove logo?",
      removeLogoBody:
        "Your page will show a placeholder until you upload a new logo.",
      settings: {
        title: "Company page settings",
        subtitle: "Identity, website, publishing, and search.",
        menu: {
          pageGroup: "Page",
          publishingGroup: "Publishing",
          companyProfile: "Company profile",
          companyProfileHint:
            "Logo, name, one-line bio, industry, and location",
          website: "Official website",
          websiteHint: "Add your official company website",
          pageAddress: "Page address",
          pageAddressHint: "Choose your public page address",
          searchAppearance: "Search appearance",
          searchAppearanceHint: "Search title, description, and visibility",
          domainEmbedding: "Domain & embedding",
          advancedSeo: "Advanced SEO",
        },
        saved: "Saved",
        searchSection: "Search appearance",
        searchTitle: "Search title",
        searchTitlePlaceholder: "Acme | Careers",
        searchDescription: "Search description",
        searchDescriptionPlaceholder: "What candidates see in search results",
        indexing: "Search engine indexing",
        indexingHint: "Allow search engines to index your public page.",
        socialSection: "Social sharing",
        useSearchText: "Use search text",
        useSearchTextHint:
          "Social previews reuse your search title and description.",
        socialTitle: "Social title",
        socialTitlePlaceholder: "Title shown when your page is shared",
        socialDescription: "Social description",
        socialDescriptionPlaceholder:
          "Description shown when your page is shared",
        languageSection: "Page language",
        languages: {
          "en-AE": "English (UAE)",
          "ar-AE": "Arabic (UAE)",
          en: "English",
          ar: "Arabic",
        },
      },
      checklist: {
        company_identity: "Complete your company identity",
        logo: "Upload your logo",
        social_or_website: "Add a social link or website",
        careers_ready: "Set up Careers",
        design_reviewed: "Review your design",
        shared: "Share your page",
      },
      dock: {
        add: "Add",
        preview: "Preview",
        design: "Design",
        analytics: "Analytics",
        settings: "Settings",
        enhance: "Enhance",
        setupLabel: "set up",
        setupProgress: "{{percent}}% set up",
        setupOpenHint: "Opens your company page setup checklist",
        enhanceSend: "Send page change request",
        enhanceDefaultPrompt:
          "Improve the company page currently open in Tabbio directly. Preserve every fact about the company. Tighten the bio and block descriptions, use clear and welcoming employer-brand wording, remove repetition, and keep the details that matter most to candidates. Do not invent details unless I ask.",
      },
      backToEditor: "Edit page",
    },
    openRoles: {
      title: "Open Roles",
      subtitle: "Manage jobs, layout, thumbnail, and insights",
      stats: "{{roles}} roles · {{applicants}} applicants",
      statsRoles_one: "{{count}} role",
      statsRoles_other: "{{count}} roles",
      statsApplicants_one: "{{count}} applicant",
      statsApplicants_other: "{{count}} applicants",
      statsOpen_one: "{{count}} open",
      statsOpen_other: "{{count}} open",
      statsTotal_one: "{{count}} total",
      statsTotal_other: "{{count}} total",
      deleted: "Open roles block removed",
      emptyTitle: "Add Open Roles",
      emptySubtitle:
        "Create an open roles block to start hiring from your profile.",
      noJobs: "No roles yet",
      noJobsHint: "Post your first role to start receiving applications.",
      postJob: "Post a Job",
      addJob: "Add job",
      futureRoles: {
        title: "Collect CVs for future roles",
        helper: "Adds “Join our talent network” after the role list.",
      },
      talentLibrary: "Talent Library",
      talentLibraryDesc:
        "Shown at end of job list. Candidates submit their CV to be considered for future roles.",
      gotIdeas: "Got ideas?",
      shareFeedback: "Share feedback",
      deleteBlock: "Delete block",
      layoutHeading: "How roles appear on your profile",
      tabs: {
        jobs: "Jobs",
        layout: "Layout",
        thumbnail: "Thumbnail",
        insights: "Insights",
      },
      layout: {
        byDepartment: "By Department",
        byDepartmentDesc: "Roles grouped under department tabs.",
        cards: "Cards",
        cardsDesc: "Each role as a full card with details.",
        compact: "Compact",
        compactDesc: "Dense list, minimal space used.",
      },
      thumbnailTitle: "Add a thumbnail to this link",
      thumbnailDesc: "Image or icon",
      thumbnailSetTitle: "Thumbnail added",
      thumbnailSetDesc: "Shown next to Open Roles on your profile.",
      thumbnailUploading: "Uploading…",
      thumbnailReplace: "Replace",
      thumbnailRemove: "Remove",
      thumbnailUploaded: "Thumbnail updated",
      thumbnailRemoved: "Thumbnail removed",
      thumbnailUploadFailed: "Could not upload that image. Please try again.",
      insights: {
        all: "All",
        updating: "Updating…",
        error: "Could not load insights for this range.",
        totalViews: "Total Views",
        uniqueVisitors: "Unique Visitors",
        applyClicks: "Apply Clicks",
        peakDay: "Peak Day",
        openRolesClicks: "Open Roles Clicks",
        viewToApply: "View-to-Apply Rate",
        viewToApplyHint:
          "Views come from the page pixel, so ad blockers can under-count them.",
        sourceBreakdown: "Source Breakdown",
        sourceEmpty: "No traffic recorded in this range yet.",
        scopePageWide: "Whole profile page, not just Open Roles.",
        scopeBlock: "Open Roles block only.",
        noData: "–",
        upgrade: "Upgrade ->",
        // `individual.basic_analytics` is granted from Plus upward, so the
        // copy names Plus (the cheapest plan that unlocks it), not Pro.
        locked: "PLUS",
        lockedA11y: "Locked. Upgrade to see this metric.",
        lockedTitle: "Detailed insights need Plus",
        lockedSubtitle:
          "Your plan measures total views only. Visitors, apply clicks, peak day, and traffic sources stay hidden until you upgrade. They are not zero.",
        lockedCta: "Upgrade to see them",
        sourceLocked:
          "Traffic sources need Plus. Upgrade to see where visitors come from.",
        sources: {
          direct: "Direct",
          open_roles: "Open Roles",
          open_roles_more: "Open Roles (see all)",
          social: "Social",
          contact: "Contact",
          form: "Contact form",
          form_submit: "Contact form",
          website: "Website",
          owner_website: "Website",
          portfolio: "Portfolio",
          visit: "Visit",
          map: "Map",
          image: "Image",
          team_member: "Team",
          links: "Links",
        },
      },
    },
    shareSheet: {
      title: "Share your Tabbio",
      subtitle: "Send your profile anywhere",
      copied: "Link copied",
      // Shown when the browser gives us no working clipboard at all: an
      // insecure context or a denied permission. Never report a copy we did
      // not make.
      copyFailed: "Couldn't copy the link",
      copyFailedHint:
        "Your browser blocked the clipboard. Select the link and copy it yourself.",
      unavailable: "This share option is unavailable on this device",
      tweet: "Check out {{name}} on Tabbio",
      copyLink: "Copy link",
      shareVia: "Share via…",
      qrTitle: "QR code",
      qrSubtitle: "People can scan this to open your profile instantly.",
      pageLinkLabel: "Your page link",
      company: {
        title: "Share your company page",
        subtitle: "Choose how to share your public company page.",
        qrSubtitle: "People can scan this to open your company page instantly.",
        copyLink: "Copy page link",
        downloadQr: "Download QR code",
        openPage: "Open public page",
        openPageHint: "See the live page exactly as candidates do",
        emailSignature: "Add to email signature",
        emailSignatureHint: "Copy a one-line signature with your page link",
        signatureCopied: "Signature copied",
        pageLinkLabel: "Company page link",
      },
      menu: {
        addToBio: "Add to bio",
        qrCode: "QR code",
        shareTo: "Share to",
        customize: "Customize",
        open: "Open",
      },
      qr: {
        save: "Save QR",
        download: "Download",
        saved: "QR saved to photos",
        downloaded: "QR ready to share",
      },
      targets: {
        copy: "Copy",
        copied: "Copied",
        whatsapp: "WhatsApp",
        x: "X",
        linkedin: "LinkedIn",
        email: "Email",
        more: "More",
      },
      addToBio: {
        title: "How to add this link to your bio",
        subtitle: "Pick a platform to get the exact steps.",
        shareTo: "Share to {{platform}}",
        steps: {
          1: "Copy your Tabbio link.",
          2: "Open your social profile settings.",
          3: "Paste the link in your bio and save.",
        },
      },
      shareOn: "Share on {{platform}}",
      moreOptions: "More options",
      goToPlatform: "Go to {{platform}}",
      platforms: {
        snapchat: "Snapchat",
        facebook: "Facebook",
        facebook_page: "Facebook Page",
        linkedin: "LinkedIn",
        x: "X",
        whatsapp: "WhatsApp",
        telegram: "Telegram",
        tiktok: "TikTok",
        twitch: "Twitch",
        youtube: "YouTube",
        instagram: "Instagram",
        pinterest: "Pinterest",
        soundcloud: "SoundCloud",
      },
      customize: {
        title: "Customize share settings",
        subtitle:
          "Control branding, visibility and warning labels before sharing.",
        cta: "Open settings",
        backgrounds: {
          gradient: "Gradient",
          light: "Light",
          dark: "Dark",
        },
      },
    },
    previewShareSheet: {
      title: "Share profile",
      subtitle: "Share directly from preview mode",
    },
    previewCtas: {
      getDirections: "Get directions",
      openMap: "Open map",
    },
    searchRoles: "Search roles...",
    filterLocation: "Location",
    filterType: "Type",
    talentPoolCta: "Don't see your role? Join our talent pool.",
    talentPoolApply: "Apply Now",
    applyButton: "Apply",
    footerCookies: "Cookie Preferences",
    footerReport: "Report",
    footerPrivacy: "Privacy",
    joinOwnerOnTabbio: "Join {{name}} on Tabbio",
    upsell: {
      heading1: "Your career.",
      heading2: "One link.",
      subtitle: "Join {{name}} and 23,000+ UAE professionals on Tabbio",
      inputPrefix: "tabbio.com/",
      inputPlaceholder: "yourname",
      cta: "Claim your Tabbio",
      exploreProfiles: "Explore profiles",
      aboutTabbio: "About Tabbio",
    },
    blockCounts: {
      projects: "{{count}} project",
      projects_other: "{{count}} projects",
      services: "{{count}} service",
      services_other: "{{count}} services",
      teamMembers: "{{count}} team member",
      teamMembers_other: "{{count}} team members",
    },
    status: {
      loadingTitle: "Loading your public profile",
      loadingBody: "We’re pulling the latest builder data and settings.",
      errorTitle: "Could not load your public profile",
      errorBody:
        "Try again in a moment. Your saved blocks and settings are unchanged.",
      savingTitle: "Saving changes",
      savingBody: "Your public profile is autosaving in the background.",
      saveErrorTitle: "Could not save changes",
      saveErrorBody:
        "Your last edit did not sync. Review the change and try again.",
    },
    blockEditor: {
      linksLabel: "Links",
      linkTitle: "Label",
      linkTitlePlaceholder: "Careers page",
      linkUrl: "Link",
      linkUrlPlaceholder: "https://example.com/careers",
      linksEmpty: "No links yet: add the first one below.",
      addLink: "Add link",
      faqsLabel: "Questions",
      faqsEmpty: "No questions yet: add the first one below.",
      faqQuestion: "Question",
      faqQuestionPlaceholder: "What time zone are you in?",
      faqAnswer: "Answer",
      faqAnswerPlaceholder:
        "I work primarily in GMT+4, with overlap for EU and EST clients.",
      addQuestion: "Add question",
      testimonialsLabel: "Testimonials",
      testimonialsEmpty: "No testimonials yet: paste a quote below.",
      testimonialAuthor: "Author name",
      testimonialAuthorPlaceholder: "Maya Park",
      testimonialQuote: "Quote",
      testimonialQuotePlaceholder:
        "Working with them was the best decision we made this year.",
      testimonialMeta: "Role / company",
      testimonialMetaPlaceholder: "Co-founder, Acme",
      addTestimonial: "Add testimonial",
      title: "Edit block",
      subtitle: "Adjust block settings and the content behind it.",
      settings: "Block settings",
      sourceData: "Source data",
      visible: "Show on public profile",
      visibleHint:
        "Turn this off to hide the block without deleting its content.",
      titlePlaceholder: "Custom title",
      subtitlePlaceholder: "Custom subtitle",
      urlPlaceholder: "https://example.com",
      bodyPlaceholder: "Add supporting text or instructions",
      buttonLabelPlaceholder: "Button label",
      submitEmailPlaceholder: "submissions@example.com",
      deleteBlock: "Delete block",
      deleteConfirm: "Delete this block? This cannot be undone.",
      deleteSuccess: "Block removed from your public profile",
      authoredTitle: "This block is authored here",
      authoredBody:
        "Changes you make here update the public profile immediately.",
      notFoundTitle: "Block not found",
      notFoundBody:
        "This block may have been removed or is no longer available.",
      sourceLinkTitle: "Source field",
      sourceLinkCta: "Edit profile details",
      websiteSourceTitle: "Website block source",
      websiteSourceBody:
        "This block reads from the website saved on your profile or company details.",
      websiteSourceEmpty:
        "Add a website in profile details so this block points somewhere useful.",
      locationSourceTitle: "Location block source",
      locationSourceBody:
        "Visit and Map blocks both use the location saved on your profile or company details.",
      locationSourceEmpty:
        "Add a location in profile details to give this block a real destination.",
      webcv: {
        title: "CV content",
        body: "{{name}}’s CV uses the latest profile, CV, and career data already saved in Tabbio.",
        cta: "Edit profile details",
      },
      project: {
        emptyTitle: "No featured projects yet",
        emptyBody:
          "Add a featured project so your portfolio block has real work to show.",
        noDescription: "No project summary added yet.",
        add: "Add featured project",
        addAnother: "Add another project",
        addTitle: "New featured project",
        editTitle: "Edit featured project",
        titlePlaceholder: "Project title",
        descriptionPlaceholder: "What did you build and why does it matter?",
        linkPlaceholder: "Project link",
        technologiesPlaceholder: "Technologies, comma separated",
        imagePlaceholder: "Project image URL",
        uploadImage: "Upload image",
        imageUploaded: "Project image uploaded",
        imageUploadFailed: "Could not upload the project image",
        titleRequired: "Add a project title before saving.",
        saveCta: "Save project",
        saved: "Project saved",
        saveFailed: "Could not save the project",
        deleted: "Project removed",
        deleteFailed: "Could not remove the project",
      },
      service: {
        emptyTitle: "No services added yet",
        emptyBody: "Add a service offer so visitors can see what you provide.",
        noDescription: "No service summary added yet.",
        add: "Add service",
        addAnother: "Add another service",
        addTitle: "New service offer",
        editTitle: "Edit service offer",
        titlePlaceholder: "Service title",
        descriptionPlaceholder: "Describe the service, scope, and outcome",
        basePricePlaceholder: "Base price",
        deliveryDaysPlaceholder: "Delivery days",
        deliverablesPlaceholder: "Deliverables, comma separated",
        titleRequired: "Add a service title before saving.",
        numberError: "Use numbers for price and delivery days.",
        saveCta: "Save service",
        saved: "Service saved",
        saveFailed: "Could not save the service",
        deleted: "Service removed",
        deleteFailed: "Could not remove the service",
        priceAndTime: "From {{price}} · {{days}} days",
        timeOnly: "{{days}} days",
      },
    },
    contacts: {
      tabbioMessage: {
        label: "Tabbio Message",
        desc: "In-app messaging through Tabbio",
        placeholder: "",
      },
      whatsapp: {
        label: "WhatsApp",
        desc: "Direct WhatsApp chat",
        placeholder: "+971 50 000 0000",
      },
      telegram: {
        label: "Telegram",
        desc: "Public channel or direct chat",
        placeholder: "@username",
      },
      slack: {
        label: "Slack",
        desc: "Slack workspace link",
        placeholder: "https://slack.com/...",
      },
      email: {
        label: "Email",
        desc: "Direct email address",
        placeholder: "you@email.com",
      },
      phone: {
        label: "Mobile",
        desc: "Mobile phone number",
        placeholder: "+971 50 000 0000",
      },
      landline: {
        label: "Landline",
        desc: "Office or home phone",
        placeholder: "+971 2 000 0000",
      },
    },
    socials: {
      website: {
        label: "Company page / website link",
        desc: "Your own site or careers page",
        placeholder: "https://www.example.com",
      },
      linkedin: {
        label: "LinkedIn",
        desc: "Professional profile",
        placeholder: "https://linkedin.com/in/username",
      },
      instagram: {
        label: "Instagram",
        desc: "Visual portfolio and stories",
        placeholder: "https://instagram.com/username",
      },
      x: {
        label: "X",
        desc: "Posts and professional updates",
        placeholder: "https://x.com/username",
      },
      tiktok: {
        label: "TikTok",
        desc: "Short-form video profile",
        placeholder: "https://tiktok.com/@username",
      },
      youtube: {
        label: "YouTube",
        desc: "Video channel",
        placeholder: "https://youtube.com/@username",
      },
      snapchat: {
        label: "Snapchat",
        desc: "Snap profile link",
        placeholder: "https://snapchat.com/add/username",
      },
      facebook: {
        label: "Facebook",
        desc: "Public page or profile",
        placeholder: "https://facebook.com/username",
      },
      discord: {
        label: "Discord",
        desc: "Community invite link",
        placeholder: "https://discord.gg/...",
      },
      pinterest: {
        label: "Pinterest",
        desc: "Boards and saved inspiration",
        placeholder: "https://pinterest.com/username",
      },
      spotify: {
        label: "Spotify",
        desc: "Artist or playlist profile",
        placeholder: "https://open.spotify.com/...",
      },
      tabbio: {
        label: "Tabbio",
        desc: "Your Tabbio profile",
        placeholder: "https://tabbio.com/username",
      },
      whatsapp: {
        label: "WhatsApp",
        desc: "Direct messaging link",
        placeholder: "https://wa.me/971500000000",
      },
      email: {
        label: "Email",
        desc: "Contact email",
        placeholder: "you@email.com",
      },
    },
    explore: {
      hiring: "Hiring",
      makeMoney: "Make money",
      professional: "Professional",
      media: "Media",
      contact: "Contact",
      text: "Text",
      all: "All blocks",
    },
    blocks: {
      webcv: {
        title: "CV",
        subtitle: "ahmed.tabbio.com",
        desc: "Show your full CV in a public web format.",
      },
      career: {
        title: "Career Page",
        subtitle: "company.tabbio.com",
        desc: "Promote roles and hiring details for your company.",
      },
      services: {
        title: "Services",
        subtitle: "Service offers",
        desc: "Show what you offer with pricing, delivery, and scope.",
      },
      website: {
        title: "Website",
        subtitle: "External site",
        desc: "Link the main personal or company website saved in your profile.",
      },
      insights: {
        title: "Weekly HR Insights",
        subtitle: "Get the latest HR trends",
        desc: "Share newsletters, insights, or recurring updates.",
      },
      links: {
        title: "Links",
        subtitle: "A list of places to go",
        desc: "Group several links in one section, such as a careers page, a press kit or a benefits deck.",
      },
      faqs: {
        title: "FAQs",
        subtitle: "Answer common questions",
        desc: "Answer repetitive questions in a clean FAQ block.",
      },
      visit: {
        title: "Visit Us",
        subtitle: "Physical location",
        desc: "Show your saved address with a directions action.",
      },
      openroles: {
        title: "Open Roles",
        subtitle: "We are hiring",
        desc: "Publish open roles and collect applications.",
      },
      team: {
        title: "Team",
        subtitle: "Meet the team",
        desc: "Showcase team members and roles.",
      },
      talentpool: {
        title: "Talent Pool",
        subtitle: "Build your pipeline",
        desc: "Capture potential candidates for future hiring.",
      },
      portfolio: {
        title: "Portfolio",
        subtitle: "Featured work",
        desc: "Show featured projects with visuals, summaries, and links.",
      },
      testimonial: {
        title: "Testimonials",
        subtitle: "What clients say",
        desc: "Display social proof from clients and partners.",
      },
      booking: {
        title: "Book a Session",
        subtitle: "Reserve time instantly",
        desc: "Allow people to book time directly from your page.",
      },
      map: {
        title: "Map",
        subtitle: "Directions and map preview",
        desc: "Turn your saved location into a map-style directions section.",
      },
      pdf: {
        title: "PDF",
        subtitle: "Downloadable file",
        desc: "Share brochures, portfolios, or guides as PDF.",
      },
      image: {
        title: "Image",
        subtitle: "Featured visual",
        desc: "Showcase a single visual asset or hero image.",
      },
      video: {
        title: "Video",
        subtitle: "Featured video",
        desc: "Embed your featured video or intro clip.",
      },
      digitalproduct: {
        title: "Digital Product",
        subtitle: "Sell downloads",
        desc: "Sell digital files or paid resources.",
      },
      text: {
        title: "Text",
        subtitle: "Free text section",
        desc: "Add custom paragraphs, notes, or updates.",
      },
      header: {
        title: "Header",
        subtitle: "Section heading",
        desc: "Insert a visual heading between sections.",
      },
      form: {
        title: "Form",
        subtitle: "Collect submissions",
        desc: "Create a form for leads, contact, or requests.",
      },
      music: {
        title: "Music",
        subtitle: "Featured audio",
        desc: "Share a track, playlist, or music embed.",
      },
    },
    collections: {
      web_cv: {
        title: "CV",
        subtitle: "ahmed.tabbio.com",
      },
      career_page: {
        title: "Career Page",
        subtitle: "company.tabbio.com",
      },
      services: {
        title: "Services",
        subtitle: "3 active services",
      },
      website: {
        title: "My Website",
        subtitle: "ahmedaldhraif.com",
      },
      insights: {
        title: "Weekly HR Insights",
        subtitle: "Get the latest HR trends",
      },
      faqs: {
        title: "FAQs",
        subtitle: "Answer common questions",
      },
      visit_us: {
        title: "Visit Us",
        subtitle: "Book a meeting",
      },
      booking: {
        title: "Book a Session",
        subtitle: "Reserve time instantly",
      },
    },
  },

  // "Put your link in your bio" walkthroughs, shown from the share sheets.
  // One short list of steps per platform beats one paragraph of prose: the
  // whole point is telling people which field the link actually goes in.
  linkInBio: {
    sectionTitle: "Share to",
    sectionSubtitle:
      "Add your link to your bio, or post about it. Pick a platform.",
    sectionSubtitleCompany:
      "Candidates already follow you somewhere. Add your link to your bio there, or post about it.",
    guideSubtitle: "A couple of minutes, once.",
    stepsLabel: "What to do",
    yourLink: "Your link",
    copy: "Copy link",
    copied: "Copied",
    goTo: "Open {{platform}}",
    postAbout: "Post about it on {{platform}}",
    appOnlyHint: "This one can only be done in the app.",
    prereq: {
      phone: "You'll need your phone for this one.",
      app: "You'll need the app on your phone.",
    },
    notices: {
      linkedin_page: "You need to be a super admin of the Page. Content admins can't edit Page info.",
      tiktok:
        "If you can't see a Website field, your account isn't eligible for one yet. Switching to a Business Account is the reliable way to unlock it.",
      youtube:
        "The Links section needs advanced features turned on, which means verifying your phone number or ID.",
      whatsapp:
        "A personal account has no website field, and the link in About isn't tappable. WhatsApp Business has a real Website field, so use that if you have it.",
      google_business:
        "Your profile has to be claimed and verified first. Worth doing: the link then shows up directly in Google Search and Maps.",
      snapchat:
        "Snapchat only offers a website field on Snap Star and Business profiles. If you don't have one, attach the link to a Snap instead.",
    },
    footnotes: {
      instagram:
        "You can add up to five links, but only the first one shows in your bio, so put this one at the top.",
      linkedin:
        "LinkedIn removed the link that used to sit under your headline, so Contact info is now the place for it.",
      facebook:
        "The website field has its own audience setting and doesn't default to Public. Set it to Public or nobody will see it.",
      facebook_page:
        "Some Pages still use the older layout: About, then Edit Page info, then Contact.",
      youtube:
        "You can add up to fourteen links, but only the first shows next to your Subscribe button.",
    },
    platforms: {
      linkedin: "LinkedIn",
      linkedin_page: "LinkedIn Page",
      instagram: "Instagram",
      x: "X",
      facebook: "Facebook",
      facebook_page: "Facebook Page",
      youtube: "YouTube",
      tiktok: "TikTok",
      whatsapp: "WhatsApp",
      telegram: "Telegram",
      snapchat: "Snapchat",
      google_business: "Google Business Profile",
    },
    steps: {
      linkedin_page: [
        "Open your Page and switch to the super admin view",
        "Click Edit page, then Page info",
        "Paste your link into the Website URL field, not the LinkedIn public URL below it",
        "Save, then open your Page and click the link to check it",
      ],
      linkedin: [
        "Open your profile and click Contact info",
        "Click the pencil, then Add website",
        "Paste your link and choose a website type",
        "Save, then check it from your public profile",
      ],
      // Worded for both surfaces: the Links field exists in the app and on
      // instagram.com/accounts/edit, which is where `webUrl` points.
      instagram: [
        "Open Instagram and go to Edit profile",
        "Open Links, then Add external link",
        "Paste your link and save",
        "Open your profile and tap the link in your bio to check it",
      ],
      x: [
        "Open your profile and click Edit profile",
        "Paste your link into the Website field",
        "Save, then click the link on your profile to check it",
      ],
      facebook_page: [
        "Open your Page and tap Edit under the Page name",
        "Tap the pencil next to Links",
        "Paste your link, including the https:// at the front",
        "Save, then open your Page and click the link to check it",
      ],
      facebook: [
        "Go to the About tab on your profile",
        "Open Contact and basic info, then Websites and social links",
        "Paste your link and set the audience to Public",
        "Save, then check the link from your profile",
      ],
      youtube: [
        "Open YouTube Studio, then Customisation, then Profile",
        "Scroll to Links and click Add link",
        "Give it a title and paste your link",
        "Click Publish, then check it on your channel page",
      ],
      tiktok: [
        "Open the TikTok app and tap Profile",
        "Tap Edit profile",
        "Scroll to Website and paste your link",
        "Save, then tap the link in your bio to check it",
      ],
      whatsapp: [
        "Open WhatsApp Business and go to Settings",
        "Tap Business tools, then Business profile",
        "Paste your link into the Website field, including the https://",
        "Save, then open your profile to check it",
      ],
      google_business: [
        "Search your business name on Google while signed in, or open business.google.com",
        "Click Edit profile, then Contact",
        "Paste your link into the Website field, including the https://",
        "Save. Google usually shows the change within a few minutes",
      ],
      snapchat: [
        "Open Snapchat and tap your profile icon, then Public Profile",
        "Tap the pencil to edit it",
        "Paste your link into the Website field",
        "Snapchat saves on its own. Open your public profile to check it",
      ],
    },
  },

  // Chat
  chat: {
    historyLoadError: "We couldn't load this conversation. Please try again.",
    retryHistory: "Try again",
    noConversations: "No conversations yet",
    recents: "Recents",
    newChat: "New chat",
    searchChats: "Search chats",
    searchPlaceholder: "Search conversations…",
    emptyState: "Ready when you are.",
    askAnything: "Ask anything",
    // Assistant activity trace. Resolution lives in tool-activity-copy.ts:
    // per-tool override → verb × entity template → generic fallback.
    // Every phrase is honest about what actually happened: a step never claims
    // work the agent did not do, and there is no fake percentage anywhere.
    // Shown when a turn fails without the server supplying a message.
    turnError: "Something went wrong on that reply.",
    // A reply the model stopped at its output limit (finishReason "length"):
    // the whole message when nothing was written, a closing line after part
    // of one. createMastraChatModel adds them; the server does not store them.
    errors: {
      truncated:
        "The reply was cut off because it was too long. Ask for a shorter version.",
      truncatedPartial:
        "The reply was cut off here. Ask to continue or for a shorter version.",
    },
    approval: {
      confirm: "Confirm this action?",
      approve: "Approve",
      decline: "Decline",
      declining: "Declining…",
      approved: "Approved",
      declined: "Declined",
      reasonOptional: "Reason (optional)",
      approveFailed: "Could not approve this action.",
      declineFailed: "Could not decline this action.",
      // Agent CV publish (B10): what the person approves.
      publishCvTitle: "Publish this CV?",
      publishCvBody:
        "Anyone with the link will be able to see this CV. You approve it as it is now: if it changes, or 10 minutes pass, Tabbio asks again.",
      // A refused approval (B10).
      stale:
        "This changed after the request was made, so it was not done. Ask Tabbio again to review the latest version.",
      expired:
        "This approval expired, so nothing was done. Ask Tabbio again if you still want it.",
    },
    // The fallback tool card: a failed call's sentence and its Details
    // (collapsed), plus the development-only raw result of a successful call.
    // What a user attached to a sent message (images and PDFs), shown with
    // the message after a refresh.
    messageAttachments: {
      image: "Image",
      pdf: "PDF",
      download: "Download {{name}}",
      open: "Open {{name}}",
    },
    toolResult: {
      rawPayload: "Raw result",
      details: "Details",
      code: "Code",
      status: "Status",
      failed: "This step could not be completed.",
      inputInvalid: "This step did not run because its input was not valid.",
    },
    cvCard: {
      title: "Your CVs",
      count_one: "{{count}} CV",
      count_other: "{{count}} CVs",
      main: "Main",
      profileBacked: "Profile CV",
      public: "Public",
      private: "Private",
      untitled: "Untitled CV",
      empty: "No CVs found.",
      openHint: "Open this CV",
      showAll: "Show all CVs",
    },
    // Chat menu: recency headings + per-thread actions.
    threadGroups: {
      pinned: "Pinned",
      today: "Today",
      yesterday: "Yesterday",
      previous7Days: "Previous 7 days",
      previous30Days: "Previous 30 days",
      older: "Older",
    },
    threadActions: {
      rename: "Rename",
      renameTitle: "Rename chat",
      renamePlaceholder: "Chat name",
      pin: "Pin",
      unpin: "Unpin",
      delete: "Delete",
      more: "Chat options",
      renameFailed: "Couldn't rename this chat",
      pinFailed: "Couldn't update this chat",
    },
    threadSearch: {
      noResults: 'No chats match "{{query}}"',
      clear: "Clear search",
    },
    // External pages the agent's tools actually read, shown under an answer.
    sources: {
      count_one: "{{count}} source",
      count_other: "{{count}} sources",
    },
    followUps: {
      title: "Follow-ups",
    },
    // Task rows: one unit of work with a status (applications, Nova runs).
    tasks: {
      completed: "Completed",
      failed: "Failed",
      running: "In progress",
      pending: "Queued",
      statusUpdate: "Status update",
      submitted: "Submitted",
      untitledJob: "Untitled job",
      started: "Started",
      finished: "Finished",
      more_one: "and {{count}} more",
      more_other: "and {{count}} more",
    },
    activity: {
      phase: {
        thinking: "Thinking…",
        writing: "Writing…",
        // A regenerated answer: the loader wears its "rewriting" face.
        rewriting: "Rewriting…",
      },
      // The trace header: a verb while the turn works, a summary once it settles.
      // The timed variant is only used on the device that watched the run.
      header: {
        working: "Working…",
        done_one: "Ran {{count}} step",
        done_other: "Ran {{count}} steps",
        doneTimed_one: "Ran {{count}} step in {{duration}}",
        doneTimed_other: "Ran {{count}} steps in {{duration}}",
      },
      elapsed: {
        seconds: "{{value}}s",
        minutes: "{{minutes}}m {{seconds}}s",
      },
      // Shown while a finished trace is folded away on an older message.
      summary_one: "{{count}} step",
      summary_other: "{{count}} steps",
      showWork: "Show work",
      hideWork: "Hide work",
      generic: {
        running: "Working on it…",
        done: "Done",
        waiting: "Waiting for your confirmation",
        failed: "That step didn't finish",
      },
      // Nouns are singular/definite: every collection-shaped tool has its own
      // override below, so these never need a plural form.
      entity: {
        profile: "your profile",
        experience: "your experience",
        education: "your education",
        certification: "your certification",
        project: "your project",
        serviceOffer: "your service offer",
        cv: "your CV",
        job: "the job",
        catalogJob: "the job",
        catalogApply: "your application",
        application: "your application",
        company: "the company",
        candidate: "the candidate",
        message: "your message",
        conversation: "your conversations",
        asset: "your files",
        skill: "the skill",
        skills: "your skills",
        nova: "your automation",
        memory: "your memory",
        mcp: "the connection",
        user: "people",
        notification: "your notifications",
        personal: "your CV analytics",
        studio: "your career tools",
        catalog: "your jobs",
        messaging: "your messaging settings",
        careerHighlight: "your career highlight",
        public: "your public profile",
      },
      verb: {
        search: {
          running: "Searching {{entity}}…",
          done: "Searched {{entity}}",
          waiting: "Waiting to search {{entity}}",
          failed: "Couldn't search {{entity}}",
        },
        read: {
          running: "Reading {{entity}}…",
          done: "Read {{entity}}",
          waiting: "Waiting to open {{entity}}",
          failed: "Couldn't open {{entity}}",
        },
        check: {
          running: "Checking {{entity}}…",
          done: "Checked {{entity}}",
          waiting: "Waiting to check {{entity}}",
          failed: "Couldn't check {{entity}}",
        },
        create: {
          running: "Creating {{entity}}…",
          done: "Created {{entity}}",
          waiting: "Waiting to create {{entity}}",
          failed: "Couldn't create {{entity}}",
        },
        update: {
          running: "Updating {{entity}}…",
          done: "Updated {{entity}}",
          waiting: "Waiting to update {{entity}}",
          failed: "Couldn't update {{entity}}",
        },
        publish: {
          running: "Publishing {{entity}}…",
          done: "Published {{entity}}",
          waiting: "Waiting to publish {{entity}}",
          failed: "Couldn't publish {{entity}}",
        },
        send: {
          running: "Sending {{entity}}…",
          done: "Sent {{entity}}",
          waiting: "Waiting to send {{entity}}",
          failed: "Couldn't send {{entity}}",
        },
        run: {
          running: "Running {{entity}}…",
          done: "Ran {{entity}}",
          waiting: "Waiting to run {{entity}}",
          failed: "Couldn't run {{entity}}",
        },
        remove: {
          running: "Removing {{entity}}…",
          done: "Removed {{entity}}",
          waiting: "Waiting to remove {{entity}}",
          failed: "Couldn't remove {{entity}}",
        },
        attach: {
          running: "Attaching {{entity}}…",
          done: "Attached {{entity}}",
          waiting: "Waiting to attach {{entity}}",
          failed: "Couldn't attach {{entity}}",
        },
        memory: {
          running: "Saving to {{entity}}…",
          done: "Saved to {{entity}}",
          waiting: "Waiting to save to {{entity}}",
          failed: "Couldn't save to {{entity}}",
        },
      },
      tool: {
        skillsList: {
          running: "Looking through your skills…",
          done_one: "Found {{count}} skill",
          done_other: "Found {{count}} skills",
          waiting: "Waiting to open your skills",
          failed: "Couldn't load your skills",
        },
        jobSearch: {
          running: "Searching jobs…",
          done_one: "Found {{count}} job",
          done_other: "Found {{count}} jobs",
          waiting: "Waiting to search jobs",
          failed: "Couldn't search jobs",
        },
        catalogJobSearch: {
          running: "Searching live listings…",
          done_one: "Found {{count}} listing",
          done_other: "Found {{count}} listings",
          waiting: "Waiting to search listings",
          failed: "Couldn't search listings",
        },
        jobRecommend: {
          running: "Matching you to jobs…",
          done_one: "Found {{count}} match",
          done_other: "Found {{count}} matches",
          waiting: "Waiting to match jobs",
          failed: "Couldn't match jobs",
        },
        candidateSearch: {
          running: "Searching candidates…",
          done_one: "Found {{count}} candidate",
          done_other: "Found {{count}} candidates",
          waiting: "Waiting to search candidates",
          failed: "Couldn't search candidates",
        },
        assetRecall: {
          running: "Looking through your files…",
          done_one: "Found {{count}} file",
          done_other: "Found {{count}} files",
          waiting: "Waiting to open your files",
          failed: "Couldn't open your files",
        },
        novaList: {
          running: "Checking your automations…",
          done_one: "Found {{count}} automation",
          done_other: "Found {{count}} automations",
          waiting: "Waiting to check automations",
          failed: "Couldn't load your automations",
        },
        cvList: {
          running: "Looking through your CVs…",
          done_one: "Found {{count}} CV",
          done_other: "Found {{count}} CVs",
          waiting: "Waiting to open your CVs",
          failed: "Couldn't load your CVs",
        },
        cvGet: {
          running: "Reading your CV…",
          done: "Read your CV",
          waiting: "Waiting to open your CV",
          failed: "Couldn't open your CV",
        },
        cvTailor: {
          running: "Tailoring your CV…",
          done: "Tailored your CV",
          waiting: "Waiting to tailor your CV",
          failed: "Couldn't tailor your CV",
        },
        cvCreate: {
          running: "Building your CV…",
          done: "Built your CV",
          waiting: "Waiting to build your CV",
          failed: "Couldn't build your CV",
        },
        cvExport: {
          running: "Preparing your download…",
          done: "Your download is ready",
          waiting: "Waiting to prepare your download",
          failed: "Couldn't prepare your download",
        },
        cvPublish: {
          running: "Publishing your CV…",
          done: "Published your CV",
          waiting: "Waiting for you to confirm publishing",
          failed: "Couldn't publish your CV",
        },
        profileGet: {
          running: "Reading your profile…",
          done: "Read your profile",
          waiting: "Waiting to open your profile",
          failed: "Couldn't open your profile",
        },
        applicationPrepare: {
          running: "Preparing your application…",
          done: "Your application is ready",
          waiting: "Waiting to prepare your application",
          failed: "Couldn't prepare your application",
        },
        catalogApplyPrepare: {
          running: "Preparing your application…",
          done: "Your application is ready",
          waiting: "Waiting to prepare your application",
          failed: "Couldn't prepare your application",
        },
        applicationSubmit: {
          running: "Submitting your application…",
          done: "Application submitted",
          waiting: "Waiting for you to confirm sending",
          failed: "Couldn't submit your application",
        },
        catalogApplySubmit: {
          running: "Submitting your application…",
          done: "Application submitted",
          waiting: "Waiting for you to confirm sending",
          failed: "Couldn't submit your application",
        },
        messageDraft: {
          running: "Drafting your message…",
          done: "Draft ready",
          waiting: "Waiting to draft your message",
          failed: "Couldn't draft your message",
        },
        messageSend: {
          running: "Sending your message…",
          done: "Message sent",
          waiting: "Waiting for you to confirm sending",
          failed: "Couldn't send your message",
        },
        skillRun: {
          running: "Running the skill…",
          done: "Skill finished",
          waiting: "Waiting for you to confirm the run",
          failed: "The skill didn't finish",
        },
        skillInvoke: {
          running: "Running the skill…",
          done: "Skill finished",
          waiting: "Waiting for you to confirm the run",
          failed: "The skill didn't finish",
        },
        novaCompile: {
          running: "Designing the automation…",
          done: "Automation ready",
          waiting: "Waiting to design the automation",
          failed: "Couldn't design the automation",
        },
        listConnectedServices: {
          running: "Checking your connections…",
          done_one: "Found {{count}} connection",
          done_other: "Found {{count}} connections",
          waiting: "Waiting to check your connections",
          failed: "Couldn't check your connections",
        },
        getRecentEmails: {
          running: "Reading your inbox…",
          done_one: "Read {{count}} email",
          done_other: "Read {{count}} emails",
          waiting: "Waiting to open your inbox",
          failed: "Couldn't open your inbox",
        },
        getRecentSlackMessages: {
          running: "Reading your Slack…",
          done_one: "Read {{count}} message",
          done_other: "Read {{count}} messages",
          waiting: "Waiting to open Slack",
          failed: "Couldn't open Slack",
        },
        sendEmail: {
          running: "Sending the email…",
          done: "Email sent",
          waiting: "Waiting for you to confirm sending",
          failed: "Couldn't send the email",
        },
        postSlackMessage: {
          running: "Posting to Slack…",
          done: "Posted to Slack",
          waiting: "Waiting for you to confirm posting",
          failed: "Couldn't post to Slack",
        },
        triggerZapierWorkflow: {
          running: "Starting your Zap…",
          done: "Zap started",
          waiting: "Waiting for you to confirm the run",
          failed: "Couldn't start your Zap",
        },
        triggerMakeScenario: {
          running: "Starting your scenario…",
          done: "Scenario started",
          waiting: "Waiting for you to confirm the run",
          failed: "Couldn't start your scenario",
        },
        addMemory: {
          running: "Remembering that…",
          done: "Noted",
          waiting: "Waiting to save that",
          failed: "Couldn't save that",
        },
        recallMemory: {
          running: "Recalling what I know…",
          done: "Recalled your context",
          waiting: "Waiting to recall your context",
          failed: "Couldn't recall your context",
        },
        forgetMemory: {
          running: "Forgetting that…",
          done: "Forgotten",
          waiting: "Waiting for you to confirm",
          failed: "Couldn't forget that",
        },
        exportMemory: {
          running: "Exporting your memory…",
          done: "Memory exported",
          waiting: "Waiting to export your memory",
          failed: "Couldn't export your memory",
        },
        memoryGraphGet: {
          running: "Recalling what I know…",
          done: "Recalled your context",
          waiting: "Waiting to recall your context",
          failed: "Couldn't recall your context",
        },
        // Research and artifacts. `pending` is the finished call of a file
        // that is still rendering; createSlides is createDocument for decks.
        webSearch: {
          running: "Searching the web…",
          done: "Searched the web",
          waiting: "Waiting to search the web",
          failed: "Couldn't search the web",
        },
        readUrl: {
          running: "Reading the page…",
          done: "Read the page",
          waiting: "Waiting to read the page",
          failed: "Couldn't read the page",
        },
        readPdf: {
          running: "Reading the PDF…",
          done: "Read the PDF",
          waiting: "Waiting to read the PDF",
          failed: "Couldn't read the PDF",
        },
        readGithubRepo: {
          running: "Reading the repository…",
          done: "Read the repository",
          waiting: "Waiting to read the repository",
          failed: "Couldn't read the repository",
        },
        readSkill: {
          running: "Reading the skill…",
          done: "Read the skill",
          waiting: "Waiting to read the skill",
          failed: "Couldn't read the skill",
        },
        generateImage: {
          running: "Creating the image…",
          done: "Image ready",
          pending: "Still creating the image",
          waiting: "Waiting to create the image",
          failed: "Couldn't create the image",
        },
        createDocument: {
          running: "Preparing the document…",
          done: "Document ready",
          pending: "Still preparing the document",
          waiting: "Waiting to prepare the document",
          failed: "Couldn't prepare the document",
        },
        createSlides: {
          running: "Preparing the slides…",
          done: "Slides ready",
          pending: "Still preparing the slides",
          waiting: "Waiting to prepare the slides",
          failed: "Couldn't prepare the slides",
        },
        createPage: {
          running: "Building the page…",
          done: "Page ready",
          pending: "Still building the page",
          waiting: "Waiting to build the page",
          failed: "Couldn't build the page",
        },
        artifactList: {
          running: "Listing your files…",
          done_one: "Found {{count}} file",
          done_other: "Found {{count}} files",
          waiting: "Waiting to list your files",
          failed: "Couldn't list your files",
        },
        artifactGet: {
          running: "Opening the file…",
          done: "Opened the file",
          waiting: "Waiting to open the file",
          failed: "Couldn't open the file",
        },
        publishArtifact: {
          running: "Preparing to publish…",
          done: "Ready to publish",
          waiting: "Waiting to prepare the site",
          failed: "Couldn't prepare to publish",
        },
        unpublishArtifact: {
          running: "Unpublishing the site…",
          done: "Site unpublished",
          waiting: "Waiting to unpublish the site",
          failed: "Couldn't unpublish the site",
        },
        updatePage: {
          running: "Editing the page…",
          done: "Edited the page",
          waiting: "Waiting to edit the page",
          failed: "Couldn't edit the page",
        },
        updateDocument: {
          running: "Editing the document…",
          done: "Edited the document",
          waiting: "Waiting to edit the document",
          failed: "Couldn't edit the document",
        },
        updateSlides: {
          running: "Editing the slides…",
          done: "Edited the slides",
          waiting: "Waiting to edit the slides",
          failed: "Couldn't edit the slides",
        },
        suggestEdits: {
          running: "Looking for improvements…",
          done: "Suggestions ready",
          waiting: "Waiting to suggest edits",
          failed: "Couldn't suggest edits",
        },
        editImage: {
          running: "Editing the image…",
          done: "Edited the image",
          pending: "Still editing the image",
          waiting: "Waiting to edit the image",
          failed: "Couldn't edit the image",
        },
        artifactRead: {
          running: "Reading the file…",
          done: "Read the file",
          waiting: "Waiting to read the file",
          failed: "Couldn't read the file",
        },
      },
    },
    model: {
      label: "Model",
      locked: {
        plus: "Plus",
        pro: "Pro",
      },
      newBadge: "New",
      auto: {
        title: "Auto",
        description: "Recommended model for your plan",
      },
      providers: {
        google: "Google",
        anthropic: "Anthropic",
        openai: "OpenAI",
      },
      options: {
        // Every model does every job: build a CV, tailor it, write the cover
        // letter. What changes down the list is how much care it takes, so the
        // copy reads as one ladder and never fences a task off to one model.
        geminiFlashLite: {
          description: "Fastest. Quick edits and first drafts",
        },
        geminiFlash: {
          description: "Stronger reasoning for polished drafts and tailoring",
        },
        geminiPro: {
          description: "Thinks longer on tailoring and rewrites",
        },
        claudeSonnet: {
          description: "Sharper wording, stays close to your details",
        },
        claudeOpus: {
          description: "Most thorough on full CVs and applications",
        },
      },
    },
    // Composer controls: Research, Image and the Result chip. Web desktop
    // shows them as chips beside the model picker; phones put them in "+".
    capabilities: {
      states: {
        auto: "Auto",
        on: "On",
        off: "Off",
      },
      addFiles: "Add photos and files",
      menuLabel: "Add files and tools",
      research: {
        label: "Research",
        sheetTitle: "Search the web",
        options: {
          auto: {
            title: "Auto",
            description: "Searches the web when your message needs current information",
          },
          on: {
            title: "On",
            description: "Searches the web before every answer",
          },
          off: {
            title: "Off",
            description: "Answers without searching the web",
          },
        },
      },
      image: {
        label: "Image",
        sheetTitle: "Create an image",
        lockedHint: "Upgrade to Plus to create images",
        options: {
          auto: {
            title: "Auto",
            description: "Creates an image only when you ask for one",
          },
          on: {
            title: "On",
            description: "Creates an image with every message",
          },
          off: {
            title: "Off",
            description: "Never creates images",
          },
        },
      },
      result: {
        label: "Result",
        sheetTitle: "Result format",
        options: {
          auto: {
            title: "Auto",
            description: "Picks the format that fits your message",
          },
          summary: {
            title: "Summary",
            description: "Answers in the chat",
          },
          document: {
            title: "Document",
            description: "A document you can download as a PDF",
          },
          slides: {
            title: "Slides",
            description: "A slide deck you can download for PowerPoint",
          },
          page: {
            title: "Page",
            description: "A web page you can preview and download",
          },
          image: {
            title: "Image",
            description: "An image you can save",
          },
        },
      },
    },
    // The composer: its "+" sources menu, the "@" mentions typed into the
    // draft, the "/" commands menu and dictation. The native "+" sheet and
    // suggestion list use the same rows and mention labels.
    composer: {
      sourcesFooter: "Type to search sources and files",
      commandsFooter: "Type to search commands",
      placeholderJobs: "Find jobs that match my CV...",
      placeholderCv: "Help improve my CV...",
      noMatches: "No matches for “{{query}}”",
      sources: {
        attach: {
          description: "Upload from your device",
        },
        research: {
          title: "Web search",
          description: "Search the web for current information",
        },
        image: {
          title: "Create an image",
          description: "Let Tabbio make images for you",
        },
        result: {
          title: "Result format",
          description: "Chat, document, slides or image",
        },
      },
      // Picking a source from "@" writes its label into the message. The
      // label is what the message is read for, so keep these short.
      mentions: {
        research: "Web search",
        image: "Image",
        document: "Document",
        slides: "Slides",
        page: "Page",
      },
      mentionHints: {
        research: "Search the web for this message",
        image: "Create an image for this message",
        document: "Answer this message as a document",
        slides: "Answer this message as slides",
        page: "Answer this message as a web page",
      },
      listening: "Listening…",
      dictate: "Start dictation",
      stopDictation: "Stop dictation",
      send: "Send",
      stop: "Stop generating",
      removeAttachment: "Remove {{name}}",
      // The phone's "+" sheet: the Photos and Camera tiles and the recent
      // photos, then one row per tool. A tool row puts a chip above the input
      // that applies to the next message only.
      sheet: {
        photos: "Photos",
        camera: "Camera",
        recentPhoto: "Attach recent photo {{number}}",
        rows: {
          research: {
            title: "Web search",
            description: "Search the web for this message",
          },
          image: {
            title: "Create an image",
            description: "Create and edit",
          },
          document: {
            title: "Document",
            description: "Write a document you can download as a PDF",
          },
          slides: {
            title: "Slides",
            description: "A deck you can download for PowerPoint",
          },
          page: {
            title: "Page",
            description: "A web page you can preview and download",
          },
          attach: {
            title: "More uploads",
            description: "Files",
          },
        },
      },
      // The chips a "+" sheet row puts above the input. Their labels are the
      // mention labels above.
      chips: {
        label: "Tools for this message",
        remove: "Remove {{name}}",
      },
    },
    // Files the agent creates (documents, slides, pages, images): chat cards,
    // the viewer and the run panel.
    artifacts: {
      kind: {
        document: "Document",
        slides: "Slides",
        page: "Page",
        image: "Image",
        file: "File",
      },
      preparing: "Preparing…",
      failed: "This could not be created. Ask again to try once more.",
      refused: "This image could not be created from that request.",
      unavailable: "This file is no longer available.",
      runLabel: "Files",
      openImage: "Open image: {{title}}",
      createdAt: "{{kind}} · {{time}}",
      saved: "Saved",
      // An agent edit's card, and a saved version the checks still flag
      // (a success: the page is kept and cannot be published yet).
      card: {
        versionSaved: "Version {{number}} saved",
        fixBefore_one: "Fix {{count}} issue before publishing",
        fixBefore_other: "Fix {{count}} issues before publishing",
      },
      // The streaming writer's failures (plan 7.7); anything else reads as
      // `failed` above.
      failures: {
        CANCELLED: "Stopped before it finished",
        WRITER_TIMEOUT: "This took too long to write.",
        WRITER_FAILED: "This could not be written.",
      },
      // The building card and the canvas panel while the writer streams.
      building: {
        page: "Building the page",
        document: "Writing the document",
        slides: "Writing the slides",
        editing: "Editing",
        checking: "Checking the page",
        fixing_one: "Fixing {{count}} issue",
        fixing_other: "Fixing {{count}} issues",
        lines_one: "{{count}} line",
        lines_other: "{{count}} lines",
        section: "Section {{current}} of {{total}}",
        sectionOnly: "Section {{current}}",
        more: "+{{count}} more",
        status: "Building",
        jumpToLatest: "Jump to latest",
        stopped: "Stopped before it finished",
        meta: "{{status}} · {{detail}}",
        elapsed: "{{count}}s",
        finished: "Finished: {{title}}",
        codeLabel: "Code of {{title}}",
      },
      actions: {
        open: "Open",
        copyLink: "Copy link",
        copyCode: "Copy code",
        codeCopied: "Code copied",
        linkCopied: "Link copied",
        copyFailed: "Could not copy the link",
        save: "Save",
        openPdf: "Open PDF",
        back: "Back",
        retry: "Try again",
        tryAgain: "Try again",
        downloadFullSize: "Download full size",
        more: "More",
        saveImage: "Save image",
        share: "Share",
        expand: "Expand",
        collapse: "Exit full width",
        download: "Download",
        backToChat: "Back to chat",
        close: "Close",
      },
      download: {
        pdf: "PDF",
        pptx: "PowerPoint",
        html: "HTML",
        image: "Image",
        hint: "Download {{format}}",
        failed: "Download failed. Please try again.",
      },
      list: {
        title: "Your files",
        empty: "No files yet.",
        more: "Showing your latest files.",
      },
      // Share, the Publish sheet and the published state (plan 7.6, 7.7).
      publish: {
        share: "Share",
        shareLabel: "Share {{title}}",
        copyPrivateLink: "Copy private link",
        toWeb: "Publish to the web",
        title: "Publish",
        addressLabel: "Site address",
        addressHint: "Letters, numbers and hyphens, 3 to 40 characters.",
        checking: "Checking…",
        available: "Available",
        taken: "That name is taken",
        reserved: "That name is reserved",
        held: "That name is on hold. Try another.",
        invalid: "Use 3 to 40 letters, numbers or hyphens",
        blocked: "Choose another name",
        usernameProtected: "That name belongs to a Tabbio member",
        checkFailed: "Couldn't check the name. Try again.",
        try: "Try",
        suggestionsLabel: "Available names",
        searchToggle: "Show in search engines",
        searchHint: "Search engines can take a few weeks to list a site.",
        badgeToggle: "Show “Made with Tabbio”",
        badgeFree: "Free sites show a small “Made with Tabbio” badge.",
        limit: "You have {{used}} of {{limit}} live sites.",
        upgrade: "Upgrade",
        unpublishOther: "Unpublish another site",
        fixFirst: "Fix the issues before publishing.",
        findings_one: "{{count}} issue to fix before publishing",
        findings_other: "{{count}} issues to fix before publishing",
        fixWithTabbio: "Fix with Tabbio",
        fixPrompt: "Fix the issues the checks found on this page so I can publish it.",
        cta: "Publish",
        publishing: "Publishing…",
        buildingHint: "Your site is almost ready. This can take a few more seconds.",
        failed: "Publishing failed. Try again.",
        disabled: "Publishing is not available for this account yet.",
        quota: "You reached today's publishing limit. Try again tomorrow.",
        live: "Your site is live",
        linkLabel: "Site link",
        open: "Open site",
        copySiteLink: "Copy site link",
        copyLink: "Copy link",
        shareLink: "Share",
        manage: "Manage site",
        update: "Update site",
        updating: "Updating the site…",
        updateHint: "Version {{number}} is newer than the published site.",
        upToDate: "The site shows the latest version.",
        rename: "Change address",
        renameHint: "The old address sends visitors to the new one for 30 days.",
        renameSave: "Save address",
        renaming: "Changing the address…",
        renamed: "Address changed",
        redirecting: "{{host}} sends visitors here for now.",
        history: "Site history",
        historyEmpty: "Nothing published yet.",
        deployRow: "Version {{version}} · {{time}}",
        deployCurrent: "Live now",
        deployBuilding: "Building",
        deployFailed: "Failed",
        rollback: "Roll back",
        rollbackLabel: "Roll back to version {{version}}",
        rolledBack: "The site shows the older version again",
        unpublish: "Unpublish",
        unpublishTitle: "Unpublish this site?",
        unpublishBody: "The link stops working right away. You can publish again later.",
        unpublishing: "Unpublishing…",
        unpublished: "Site unpublished",
        unpublishFailed: "Couldn't unpublish the site. Try again.",
        views_one: "{{count}} view in the last 30 days",
        views_other: "{{count}} views in the last 30 days",
        liveChip: "Live",
        liveChipLabel: "Live at {{host}}",
        publishedAt: "Published at {{host}}",
        back: "Back",
        done: "Done",
        close: "Close",
        suspended: "This site was removed for breaking the Tabbio rules.",
        loadFailed: "Couldn't load the site. Try again.",
        policy: "Published sites follow the Tabbio sites policy.",
        policyLink: "Read the policy",
      },
      // The publishArtifact and unpublishArtifact card (card type site-publish).
      siteCard: {
        ready: "Ready to publish",
        noName: "Choose the address in the Publish sheet",
        published: "Published",
        unpublished: "Site unpublished",
        publish: "Publish",
        publishAgain: "Publish again",
        manage: "Manage",
      },
      // Manual editing (plan 7.3): Edit and Done, the save states, the
      // findings banner and the Code tab.
      edit: {
        edit: "Edit",
        editLabel: "Edit {{title}}",
        done: "Done",
        saving: "Saving…",
        saved: "Saved",
        saveFailed: "Couldn't save. Try again.",
        retry: "Try again",
        conflict: "This file changed somewhere else. Reload to see the latest version.",
        reload: "Reload",
        removedNote: "Scripts, forms and external files were removed.",
        findings_one: "{{count}} issue to fix before publishing",
        findings_other: "{{count}} issues to fix before publishing",
        advice_one: "{{count}} thing to improve",
        advice_other: "{{count}} things to improve",
        showFindings: "Show the issues",
        hideFindings: "Hide the issues",
        fixWithTabbio: "Fix with Tabbio",
        fixPrompt: "Fix the issues the checks found in this file.",
        frameTitle: "Editing {{title}}",
        loading: "Opening the editor…",
        loadFailed: "Couldn't open the editor. Try again.",
        limit: "This part holds up to {{max}} characters.",
        editHint: "Click any text to edit it. Tab moves to the next block.",
        editHintTouch: "Tap any text to edit it.",
        codeSave: "Save",
        codeSaveLabel: "Save the code ({{shortcut}})",
        codeSaving: "Saving…",
        codeSaved: "Saved",
        codeUnsaved: "Unsaved changes",
        codeWebOnly: "The code can be edited in Tabbio on the web.",
        codeEditLabel: "Code of {{title}}, editable",
        notEditable: "This file can't be edited here.",
      },
      // The element bar and the selection toolbar (plan 7.3, 7.4).
      toolbar: {
        label: "Actions for the selected text",
        elementLabel: "Actions for this part",
        ask: "Ask Tabbio",
        shorter: "Shorter",
        longer: "Longer",
        tone: "Tone",
        casual: "More casual",
        formal: "More formal",
        suggest: "Suggest edits",
        editLink: "Edit link",
        replaceImage: "Replace image",
        editImage: "Edit with Tabbio",
        altText: "Alt text",
        moveUp: "Move up",
        moveDown: "Move down",
        duplicate: "Duplicate",
        remove: "Remove",
        more: "More actions",
      },
      // What a quick action sends, with the selection as context.
      prompts: {
        shorter: "Make this shorter",
        longer: "Make this longer",
        casual: "Make this more casual",
        formal: "Make this more formal",
        suggest: "Suggest edits for this part",
        image: "Edit this image",
      },
      // The composer chip of Ask Tabbio (plan 7.4).
      selection: {
        chip: "Editing: {{text}}",
        clear: "Clear selection",
        image: "the image {{title}}",
      },
      // Edit link, Replace image and Alt text on a page (plan 7.3).
      link: {
        title: "Edit link",
        url: "Link address",
        urlHint: "An https, http or mailto link, or #section on this page.",
        text: "Link text",
        invalid: "Use a link that starts with https://, http://, mailto: or #.",
        save: "Save",
        cancel: "Cancel",
      },
      image: {
        replaceTitle: "Replace image",
        yours: "Your images",
        loadingYours: "Loading your images…",
        noImages: "You have no images yet. Ask Tabbio to create one, or paste a link.",
        choose: "Use {{title}}",
        urlLabel: "Or paste an image link",
        urlHint: "The link must start with https://.",
        urlInvalid: "Use an image link that starts with https://.",
        use: "Use image",
        altTitle: "Alt text",
        altLabel: "Describe the image for people who can't see it",
        save: "Save",
        cancel: "Cancel",
        // The lightbox's Edit (plan 7.3).
        edit: "Edit",
        editLabel: "Edit this image",
        editPrompt: "The change to make",
        editPlaceholder: "Describe the change, for example: make the background white",
        editSubmit: "Edit image",
        editing: "Editing the image…",
        editFailed: "This edit could not be made.",
        editStartFailed: "Couldn't start the edit. Try again.",
        edited: "Image edited",
      },
      // Suggest edits (plan 7.4), the artifact-suggestions card.
      suggestions: {
        title: "Suggested edits",
        count_one: "{{count}} suggestion for {{title}}",
        count_other: "{{count}} suggestions for {{title}}",
        apply: "Apply",
        applyAll: "Apply all",
        dismiss: "Dismiss",
        applied: "Applied",
        dismissed: "Dismissed",
        applying: "Applying…",
        before: "Now",
        after: "Suggested",
        none: "No changes to suggest this time.",
        failed: "Couldn't apply this suggestion. Try again.",
        stale: "This file changed since these suggestions were made. Ask again for new ones.",
        notApplicable: "This one can't be applied automatically.",
        open: "Open to review",
        markLabel: "Suggested edit: {{reason}}",
        position: "Suggestion {{current}} of {{total}}",
        previous: "Previous suggestion",
        next: "Next suggestion",
      },
      // Version control (plan 7.5).
      versions: {
        label: "Version {{number}} of {{total}}",
        previous: "Previous version",
        next: "Next version",
        history: "Version history",
        byYou: "Edited by you",
        byTabbio: "Made by Tabbio",
        restoredFrom: "Restored from version {{number}}",
        restoredFromUnknown: "Restored from an older version",
        viewingOld: "You're viewing version {{number}}.",
        restore: "Restore this version",
        restoring: "Restoring…",
        backToLatest: "Back to latest",
        restored: "Version {{number}} restored",
        restoreFailed: "Couldn't restore this version. Try again.",
        cardMeta: "Version {{number}}",
        row: "Version {{number}}",
        latest: "Latest",
        live: "Live on the site",
        pending: "Being made",
        failed: "Couldn't be made",
        loading: "Loading the versions…",
        loadFailed: "Couldn't load the versions. Try again.",
        more: "Show older versions",
      },
      // The document and slides editors (plan 7.3).
      docEditor: {
        label: "Editing {{title}}",
        title: "Title",
        subtitle: "Subtitle",
        heading: "Section heading",
        section: "Section {{number}}",
        addSection: "Add a section",
        removeSection: "Remove this section",
        addParagraph: "Add a paragraph",
        blockType: "Block type",
        blockTypes: {
          paragraph: "Paragraph",
          bullets: "Bulleted list",
          numbered: "Numbered list",
          quote: "Quote",
          callout: "Callout",
        },
        bold: "Bold",
        italic: "Italic",
        link: "Link",
        linkPrompt: "Link address",
        removeLink: "Remove link",
        moveUp: "Move up",
        moveDown: "Move down",
        delete: "Delete",
        item: "Item {{number}}",
        addItem: "Add an item",
        removeItem: "Remove this item",
        addRow: "Add a row",
        removeRow: "Remove the last row",
        addColumn: "Add a column",
        removeColumn: "Remove the last column",
        cell: "Row {{row}}, column {{column}}",
        header: "Column {{column}} heading",
        caption: "Caption",
        alt: "Alt text",
        attribution: "Attribution",
        tone: "Tone",
        tones: {
          info: "Note",
          success: "Good news",
          warning: "Warning",
        },
        image: "Image",
        table: "Table",
        diagram: "Diagram",
        diagramLabels: "Labels",
        diagramNode: "Box {{number}}",
        diagramHint: "Ask Tabbio to change the diagram's shape or connections.",
        editBlock: "Edit",
        placeholder: "Write something",
        preparingPdf: "Preparing the PDF",
        limits: {
          sections: "A document holds up to {{max}} sections.",
          blocks: "A document holds up to {{max}} blocks.",
          items: "A list holds up to {{max}} items.",
          rows: "A table holds up to {{max}} rows.",
          columns: "A table holds up to {{max}} columns.",
          characters: "This part holds up to {{max}} characters.",
        },
      },
      viewer: {
        title: "File",
        loading: "Loading your file…",
        pending: "Preparing your file. It appears here when it is ready.",
        unsupported: "This file can't be previewed here. Download it instead.",
        offline: "You're offline. Check your connection and try again.",
        expired: "The preview link expired.",
        notFoundTitle: "This file is no longer available",
        notFoundHint: "It may have been deleted, or it belongs to another account.",
        error: "The file could not be loaded.",
        page: "Preview",
        pdf: "PDF",
        code: "Code",
        frameTitle: "Preview of {{title}}",
      },
    },
    // Research card: what the agent searched and read. Its sources are in the
    // Sources row under the answer, not repeated here.
    research: {
      title: {
        search: "Web search",
        url: "Pages read",
        pdf: "PDF read",
        github: "Repository read",
        skill: "Skill guidance",
      },
      searchedFor: "Searched for {{queries}}",
      urlStatus: {
        success: "Success",
        error: "Could not open",
        paywall: "Paywalled",
        unsafe: "Blocked",
      },
      commit: "Commit {{sha}}",
      filesRead: "Files read: {{files}}",
      treeTruncated: "Large repository, only part of it was read",
      skillVersion: "Version {{version}}",
      showMore: "Show more",
      showLess: "Show less",
      activity: {
        search: "Searched the web",
        read: "Read {{name}}",
      },
    },
    dictationStart: "Start dictation",
    dictationStop: "Stop dictation",
    dictationPermissionDeniedTitle: "Voice access needed",
    dictationPermissionDeniedMessage:
      "Allow microphone and speech recognition to dictate messages.",
    dictationPermissionSettingsMessage:
      "Enable microphone and speech recognition in Settings to use dictation.",
    dictationUnavailableTitle: "Dictation unavailable",
    dictationUnavailableMessage:
      "Speech recognition is not available on this device right now.",
    aiLimitReached: "You've used your available credits",
    aiWorkspaceLimitReached: "Your workspace AI limit has been reached",
    aiLimitReachedHint:
      "Upgrade for a bigger credit pool, or come back tomorrow when your credits reset.",
    freeRepliesUsed: "You've used today's {{limit}} free replies",
    freeRepliesResetHint:
      "Free replies reset daily. Upgrade to Plus for generous AI use.",
    subscriptionBlocked: {
      label: "Subscription",
      title: "You've used your available credits",
      workspaceTitle: "Workspace AI limit reached",
      defaultMessage:
        "Upgrade for a bigger credit pool, or continue tomorrow when your daily credits reset.",
      upgradeCta: "View upgrade options",
    },
    home: {
      greeting: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
        night: "Up late",
        fallback: "Welcome back",
      },
      starterPrompts: {
        matchJobs: "Find jobs that match my profile",
        matchSkills: "Find jobs matching my skills in the GCC",
        tailorCv: "Tailor my CV for a specific job posting",
        interview: "Help me practise for an interview",
        remoteRoles: "Find remote roles in the GCC that match my profile",
        bestFit: "Which jobs fit my profile best? Explain why the top matches suit me.",
        chooseCv: "Help me choose the best of my CVs for a role",
        exploreCvs: "Show me my CVs and explain when to use each one",
        recommendSkill: "Recommend a Tabbio skill that would help with my career goals",
        cvSkills: "Show me the available Tabbio skills for improving my CV",
        linkedinPost: "Write a LinkedIn post I can publish this week about my work and my industry",
        industryBriefing: "Give me today's briefing on what is happening in my industry",
      },
      moreSuggestions: "More suggestions",
      rotatingSuggestions: {
        remoteRoles: "Remote roles in the GCC",
        bestFit: "Which fit me best?",
        chooseCv: "Choose a CV for a role",
        exploreCvs: "Explore my CVs",
        recommendSkill: "Recommend a skill",
        cvSkills: "Show CV skills",
      },
      greetingMove: "Hi {{name}}, what's the move?",
      greetingMoveNoName: "What's the move?",
      placeholder: "Ask anything…",
      sendLabel: "Send message",
      voiceLabel: "Use voice mode",
      dictateLabel: "Dictation",
      addLabel: "Add files or tools",
      menuLabel: "Open menu",
      tryTitle: "Try Tabbio",
      tryClose: "Dismiss suggestions",
      listening: "Listening…",
      topnav: {
        profile: "Profile",
        webcv: "CV",
        jobs: "Jobs",
        analytics: "Views",
        apps: "Apps",
      },
      actions: {
        // ── Seeker (personal): backed by cv.*, application.*, catalog.*, profile.* tools
        webcv: {
          label: "Build my CV",
          prompts: [
            "Build my CV from my most recent experience",
            "Rewrite my About section for hiring managers in Dubai",
            "Add a section for my GCC market experience",
          ],
        },
        tailor: {
          label: "Tailor my CV",
          prompts: [
            "Tailor my main CV for a senior frontend role in Abu Dhabi",
            "Rewrite my latest role to highlight bilingual Arabic and English work",
            "Show me where I could add real metrics to a property sales CV",
          ],
        },
        jobs: {
          label: "Find jobs for me",
          prompts: [
            "Find senior React roles in Dubai or Abu Dhabi",
            "Show Emirati-priority jobs posted this week",
            "Find healthcare roles in Sharjah that are open to GCC residents",
          ],
        },
        apply: {
          label: "Track my applications",
          prompts: [
            "Show the status of my open applications",
            "Show applications waiting on a recruiter reply",
            "Write a polite follow-up for the job I applied to last week",
          ],
        },
        autoapply: {
          label: "Automation",
          prompts: [
            "Show strong React Native matches in the UAE",
            "Show matches outside the GCC before I apply",
            "Find matches that need a tailored cover letter",
          ],
        },
        interview: {
          label: "Interview prep",
          prompts: [
            "Help me prepare for an interview with a Dubai bank next week",
            "Turn my latest role into STAR examples for a behavioural interview",
            "Quiz me on React system design for a senior role",
          ],
        },
        linkedin: {
          label: "Polish my LinkedIn",
          prompts: [
            "Review my LinkedIn headline for the UAE job market",
            "Suggest three post ideas about working in GCC tech",
            "Rewrite my LinkedIn About section to match my CV",
          ],
        },

        // ── Company: backed by job.*, candidate.*, message.*, company.* tools
        // The company chat home's opening starter, standing where the seeker
        // home has "Find roles that match me". Same briefcase glyph, so the
        // copy stays about the role rather than the search.
        findCandidates: {
          label: "Find candidates for my open roles",
          prompts: [
            "Find candidates for my open roles and rank the strongest matches",
            "Find Emirati candidates for our Mechanical Engineer role",
            "Show me the best matches for our senior React role",
          ],
        },
        postjob: {
          label: "Post a role",
          prompts: [
            "Draft a senior React Native job for our Dubai office",
            "Draft a Mechanical Engineer role for Emirati nationals",
            "Turn our last job description into a new draft",
          ],
        },
        shortlist: {
          label: "Build a shortlist",
          prompts: [
            "Find Emirati candidates for the Mechanical Engineer role",
            "Find bilingual sales candidates in Riyadh",
            "Compare my last three shortlists for this role",
          ],
        },
        talent: {
          label: "Find talent",
          prompts: [
            "Find React engineers in the UAE with at least five years of experience",
            "Show me Arabic-speaking marketers in Dubai",
            "Search for property consultants in Abu Dhabi",
          ],
        },
        outreach: {
          label: "Draft outreach",
          prompts: [
            "Write a message to my top shortlisted candidate",
            "Write a polite follow-up for candidates we paused last week",
            "Personalise an introduction using the candidate's portfolio",
          ],
        },
        schedule: {
          label: "Schedule interviews",
          prompts: [
            "Draft a 30-minute interview invitation for tomorrow at 10am Gulf time",
            "Draft interview invitations for my top three candidates",
            "Draft feedback notes for the interview panel",
          ],
        },
        careerpage: {
          label: "Polish my Career Page",
          prompts: [
            "Update my Career Page with our latest hires",
            "Improve my Career Page for Emirati-priority roles",
            "Add a benefits section about GCC perks",
          ],
        },
        analytics: {
          label: "Views",
          prompts: [
            "Show the hiring funnel for my senior frontend role",
            "Which open roles lose the most applicants?",
            "Compare applications from the last 30 days with the previous period",
          ],
        },
      },
    },
  },
  drawer: {
    tools: "Tools",
    inbox: "Inbox",
    notifications: "Notifications",
    apps: "Apps",
    profile: "Profile",
    webCv: "CV",
    findJobs: "Find Jobs",
    analytics: "Views",
    cvBuilder: "CV",
    findJob: "Find Jobs",
    upgrade: "Upgrade",
    upgradeBlurb: "More models and credits",
    chatCta: "Chat",
    premium: "Premium",
    categoryCareer: "Career Tools",
    categoryHiring: "Hiring Tools",
    categoryCustomize: "Customize",
  },

  // Messages / Inbox
  messages: {
    title: "Messages",
    noMessages: "No messages yet",
    noMessagesSubtitle:
      "Conversations with recruiters and employers will appear here",
    selectConversation: "Select a conversation to start messaging",
    online: "Online",
    sendMessage: "Type a message...",
    typing: "{{name}}typing...",
    typingMultiple: "Several people are typing...",
    read: "Read",
    delivered: "Delivered",
    sent: "Sent",
    sending: "Sending...",
    sendFailed: "Not sent",
    sendFailedBody:
      "Your message was not sent. Check your connection and try again.",
    today: "Today",
    yesterday: "Yesterday",
    newConversation: "New Message",
    systemMessage: "Tabbio",
    systemReadOnly: "Messages from Tabbio are read-only.",
    loadingMessages: "Loading messages...",
    noMoreMessages: "No more messages",
    messageDeleted: "This message was deleted",
    deletedUser: "Deleted user",
    connectionLost: "Reconnecting...",
    searchUsers: "Search by name, username, or email...",
    noUsersFound: "No users found",
    startConversation: "Start a conversation",
    conversationCreated: "Conversation started",
    conversationFailed: "Could not start conversation",
    blockedError: "You can't message this person right now.",
    deleteTitle: "Delete Conversation",
    deleteConfirm:
      "Delete your conversation with {{name}}? This cannot be undone.",
    deleteConfirmSimple: "Delete this conversation? This cannot be undone.",
    delete: "Delete",
    cancel: "Cancel",
    deleteSuccess: "Conversation deleted",
    deleteFailed: "Could not delete conversation",
  },

  // Notifications
  notifications: {
    title: "Notifications",
    unread: "Unread",
    moreActions: "Notification actions",
    time: {
      now: "now",
      minutes: "{{value}}m",
      hours: "{{value}}h",
      days: "{{value}}d",
      weeks: "{{value}}w",
      months: "{{value}}mo",
      years: "{{value}}y",
    },
    subtitle: "Your latest updates and activity",
    tabs: {
      jobAlerts: "Job Alerts",
      activity: "Activity",
      updated: "Updated",
    },
    noNotifications: "No notifications yet",
    noNotificationsSubtitle:
      "Job updates, application status, and alerts will appear here",
    selectNotification: "Select a notification to view details",
    markAllRead: "Mark all as read",
    groups: {
      today: "Today",
      week: "Earlier this week",
      earlier: "Earlier",
    },
    newCount_one: "{{count}} new",
    newCount_other: "{{count}} new",
    viaSource: "via {{source}}, {{when}}",
    matchChip: "{{score}}% match",
    matchesSummary_one:
      "{{count}} job matched across your {{alerts}} saved alerts",
    matchesSummary_other:
      "{{count}} jobs matched across your {{alerts}} saved alerts",
    manageFooter: "Manage what lands here in",
    manageFooterLink: "Job alerts",
    searchPlaceholder: "Search notifications…",
    markingAllRead: "Marking all as read…",
    allMarkedRead: "All notifications marked as read",
    deleted: "Notification deleted",
    deleteConfirm: "Delete this notification?",
    permissionTitle: "Enable Notifications",
    permissionBody:
      "Get notified about job updates, interview schedules, and application status.",
    permissionAllow: "Allow Notifications",
    permissionLater: "Maybe Later",
    loadingMore: "Loading more…",
    loadFailed: "Could not load notifications",
    retry: "Try again",
    types: {
      applicationUpdate: "Application Update",
      jobMatch: "Job Match",
      autoApply: "Automation",
      profileView: "Profile View",
      interview: "Interview",
      system: "System",
    },
  },

  // Employer Job Posting
  jobPost: {
    defaults: {
      roleSlug: "role",
    },
    stepCounter: "{{current}} of {{total}}",
    generating: {
      title: "Building your job post",
      subtitle: "Reading your input and filling in the details.",
    },
    success: {
      title: "You're live.",
      subtitle: "Your job is now active in your company workspace.",
      postedTo: "Posted to {{company}}",
    },
    actions: {
      next: "Next",
      post: "Post",
      postRole: "Post Role",
      posting: "Posting...",
      done: "Done",
      copy: "Copy",
      copied: "Copied",
      buildJobPost: "Build Job Post",
      postManually: "Post it manually",
      writeWithAi: "Write with AI",
    },
    entry: {
      title: "Job Postings",
      subtitle:
        "Create role postings with a guided flow and publish when ready.",
      heroOverview:
        "{{roles}} live roles and {{applicants}} applicants moving through your hiring flow.",
      searchPlaceholder: "Search roles, teams, or locations",
      heroPill: "{{count}} applicants in review",
      openRolePill: "Open role, waiting for the first applicant",
      applicantsShort: "appl.",
      cardTitle: "Post a new role",
      cardSubtitle:
        "Start with AI or fill details manually in a 3-step wizard.",
      cta: "Start Posting",
      loading: "Loading jobs...",
      loadFailed: "Could not load company jobs",
      loadFailedHint:
        "Try again in a moment. Your workspace and company access are still required.",
      emptyTitle: "No jobs yet",
      emptySubtitle:
        "Post your first role and it will appear here and in company Open Roles.",
      emptyFilteredTitle: "No matching roles",
      emptyFilteredSubtitle:
        "Try another search term or switch to a different role tab.",
      applications: "{{count}} applicants",
      postedOn: "Posted {{date}}",
      viewApplicants: "View applicants",
      live: "Live",
      stats: {
        liveRoles: "Live roles",
        totalApplicants: "Total applicants",
      },
      tabs: {
        all: "All",
        active: "Active",
        draft: "Draft",
        closed: "Closed",
      },
    },
    step1: {
      title: "Let's post a job.",
      subtitle:
        "Paste a job description and we'll set everything up. Or fill it in yourself.",
      textareaPlaceholder:
        'Paste a job description here, or type something like:\n"Frontend developer, Dubai, hybrid, React"',
      or: "or",
      attach: "Attach a job description (PDF, Word, or photo)",
      attaching: "Reading your file...",
      attachClear: "Remove attachment",
    },
    step2: {
      title: "Job details",
      subtitleAi:
        "We filled these in from your description. Change anything that needs fixing.",
      subtitleManual: "Fill in the basics. You can always edit later.",
      fields: {
        title: "Job title",
        description: "Job description",
        department: "Department",
        country: "Country",
        city: "City",
        workType: "Work type",
        employment: "Employment",
        experienceLevel: "Experience level",
        applyMethod: "How do candidates apply?",
      },
      placeholders: {
        title: "e.g. Senior Product Designer",
        description:
          "About the role\nWhat the person will do, what you're looking for, and why they should apply.",
        department: "Select department",
        country: "Select country",
        city: "Select city",
        experienceLevel: "Select level",
        externalUrl: "https://careers.company.com/apply",
      },
      applyMethod: {
        tabbio: "Through Tabbio",
        external: "External link",
      },
      applyMethodHint: {
        tabbio:
          "Candidates apply on your Tabbio page. Applications go to your Hiring Library.",
        external:
          "Candidates click Apply and go to this link. Applications won't show in your Hiring Library.",
      },
    },
    step3: {
      title: "Who should apply?",
      subtitleTabbio:
        "Add filters so the right people apply. These show before submission.",
      subtitleExternal: "Optional filters shown on the role card.",
      fields: {
        nationality: "Nationality",
        gender: "Gender",
        genderReason: "Reason",
        yearsExperience: "Experience",
        qualification: "Qualification",
        prescreens: "Pre-screen questions",
        customQuestion: "Custom question",
      },
      placeholders: {
        genderReason: "Select reason",
        yearsExperience: "Any",
        qualification: "Any",
        customQuestion: "Add your own question",
      },
      prescreensHint: "Candidates answer these before submitting.",
    },
    ai: {
      title: "Senior Marketing Lead",
      badge: "AI-generated",
      generatedDescription:
        "About the role\nWe're looking for a Senior Marketing Lead to drive regional growth across the GCC. You'll own the full marketing funnel from brand awareness to conversion, working directly with Product and Sales.\n\nResponsibilities\n• Execute regional growth campaigns across UAE, KSA, and Qatar\n• Manage monthly ad budgets and optimize for performance\n• Build and lead a marketing team of 3 to 5 people\n• Align go-to-market strategy with Product and Sales\n• Drive organic acquisition through content and SEO\n\nRequirements\n• 5+ years in B2B marketing, ideally in the GCC\n• Experience managing paid campaigns across Google, Meta, and LinkedIn\n• Familiarity with HubSpot or equivalent marketing automation\n• Strong analytical skills with GA4 and reporting tools\n• Fluent in Arabic and English",
    },
    errors: {
      companyRequired: "Switch to a company workspace before posting a job.",
      titleRequired: "Add a job title before posting.",
      descriptionRequired: "Add a job description before posting.",
      descriptionTooShort:
        "Job description must be at least {{count}} characters.",
      externalUrlRequired: "Add an external application URL before posting.",
      submitFailed: "Could not post this job",
      submitFailedHint: "Please try again. The role was not published.",
      promptRequired: "Describe the role you want to post first.",
      generateFailed: "Could not generate this job post",
    },
    list: {
      generalDepartment: "General",
      remoteFallback: "Remote",
      status: {
        active: "Active",
        draft: "Draft",
        closed: "Closed",
      },
    },
    options: {
      other: "Other",
      departments: {
        engineering: "Engineering",
        marketing: "Marketing",
        sales: "Sales",
        product: "Product",
        design: "Design",
        finance: "Finance",
        operations: "Operations",
        hr: "HR",
        legal: "Legal",
        customerSuccess: "Customer Success",
      },
      countries: {
        uae: "United Arab Emirates",
        saudi: "Saudi Arabia",
        qatar: "Qatar",
        oman: "Oman",
        bahrain: "Bahrain",
        kuwait: "Kuwait",
        egypt: "Egypt",
        jordan: "Jordan",
        remote: "Remote",
      },
      cities: {
        dubai: "Dubai",
        abuDhabi: "Abu Dhabi",
        sharjah: "Sharjah",
        ajman: "Ajman",
        rasAlKhaimah: "Ras Al Khaimah",
        fujairah: "Fujairah",
        alAin: "Al Ain",
        riyadh: "Riyadh",
        jeddah: "Jeddah",
        dammam: "Dammam",
        makkah: "Makkah",
        madinah: "Madinah",
        khobar: "Khobar",
        doha: "Doha",
        alWakrah: "Al Wakrah",
        alRayyan: "Al Rayyan",
        muscat: "Muscat",
        salalah: "Salalah",
        sohar: "Sohar",
        manama: "Manama",
        muharraq: "Muharraq",
        riffa: "Riffa",
        kuwaitCity: "Kuwait City",
        hawalli: "Hawalli",
        salmiya: "Salmiya",
        cairo: "Cairo",
        alexandria: "Alexandria",
        giza: "Giza",
        sharmElSheikh: "Sharm El Sheikh",
        amman: "Amman",
        irbid: "Irbid",
        zarqa: "Zarqa",
      },
      workType: {
        onsite: "On-site",
        hybrid: "Hybrid",
        remote: "Remote",
      },
      employment: {
        fullTime: "Full-time",
        partTime: "Part-time",
        contract: "Contract",
        internship: "Internship",
      },
      experienceLevel: {
        entry0to2: "Entry (0-2 years)",
        mid2to5: "Mid (2-5 years)",
        senior5to8: "Senior (5-8 years)",
        lead8to12: "Lead (8-12 years)",
        executive12plus: "Executive (12+ years)",
      },
      nationality: {
        open: "Open to all",
        emiratiOnly: "Emirati only",
        saudiOnly: "Saudi only",
        gcc: "GCC Nationals",
      },
      gender: {
        any: "Any",
        male: "Male",
        female: "Female",
      },
      genderReason: {
        legalRequirement: "Legal requirement",
        roleSpecific: "Role specific",
      },
      yearsExperience: {
        any: "Any",
        zeroToOne: "0-1 years",
        oneToThree: "1-3 years",
        threeToFive: "3-5 years",
        fiveToEight: "5-8 years",
        eightToTwelve: "8-12 years",
        twelvePlus: "12+ years",
      },
      qualification: {
        any: "Any",
        highSchool: "High School",
        diploma: "Diploma",
        bachelors: "Bachelor's",
        masters: "Master's",
        phd: "PhD",
      },
    },
    prescreens: {
      exp: {
        label: "Do you have the required experience?",
        tag: "Experience",
      },
      visa: {
        label: "Do you have a valid work visa?",
        tag: "Eligibility",
      },
      relocate: {
        label: "Are you willing to relocate?",
        tag: "Eligibility",
      },
      start: {
        label: "Can you start within 30 days?",
        tag: "Availability",
      },
      team: {
        label: "Have you managed a team?",
        tag: "Experience",
      },
      arabic: {
        label: "Are you fluent in Arabic?",
        tag: "Language",
      },
      english: {
        label: "Are you fluent in English?",
        tag: "Language",
      },
      degree: {
        label: "Do you have a relevant degree?",
        tag: "Qualification",
      },
    },
  },

  jobDetail: {
    title: "Role workspace",
    tabs: {
      overview: "Overview",
      applicants: "Applicants",
      matched: "Matched",
    },
    close: {
      title: "Close this role",
      body: "Closed roles move out of active hiring but keep applicants and reporting available.",
      action: "Close role",
      confirmTitle: "Close this role?",
      confirmBody:
        "Candidates will no longer see this role as active. Existing applicants stay in your pipeline.",
      confirmAction: "Confirm close",
      closedLabel:
        "This role is closed. Applicants and reporting remain available.",
      successTitle: "Role closed",
      successBody: "This role moved to your closed jobs.",
      errorTitle: "Could not close role",
    },
    matched: {
      matchLabel: "match",
      upgradeBadge: "Upgrade to message",
      empty: {
        title: "No matches yet",
        subtitle:
          "We'll surface Tabbio candidates here as they match this role. Check back soon.",
      },
    },
  },
  jobApplicants: {
    title: "Applicants",
    subtitle:
      "Review every candidate for this role and move them through your hiring pipeline.",
    heroOverview:
      "{{total}} applicants in total, {{reviewed}} reviewed, {{shortlisted}} shortlisted, and {{interview}} in interview.",
    detailTitle: "Applicant",
    detailSubtitle: "Full application package",
    searchPlaceholder: "Search name, email, title, or CV",
    clearFilters: "Clear filters",
    loadingTitle: "Loading applicants...",
    loadingSubtitle: "We are pulling everyone who applied to this role.",
    loadingDetailTitle: "Loading applicant...",
    loadingDetailSubtitle:
      "Fetching the full application package and CVs.",
    loadErrorTitle: "Could not load applicants",
    loadErrorSubtitle:
      "Try again in a moment. Your company workspace still needs access to this role.",
    emptyTitle: "No applicants yet",
    emptySubtitle:
      "Applicants will appear here once candidates apply through Tabbio.",
    emptyFilteredTitle: "No matching applicants",
    emptyFilteredSubtitle:
      "Try a different search or clear some stage filters.",
    notFoundTitle: "Applicant not found",
    notFoundSubtitle:
      "This application is no longer available in the active workspace.",
    noTitle: "No title added",
    noSubmittedCv: "No submitted CV",
    noFurtherActions:
      "No more employer actions are available for this application.",
    noDataValue: "Not provided",
    appliedAgo: "Applied {{time}}",
    messageHint:
      "Candidates receive an in-app notification and the first unread recruiter message also sends email.",
    messagePlaceholder: "Write a message to this candidate",
    statusUpdatedTitle: "Status updated",
    statusUpdatedBody: "Application moved to {{status}}.",
    statusUpdateFailedTitle: "Could not update applicant status",
    messageSentTitle: "Message sent",
    messageSentBody: "The candidate can now reply from their Tabbio inbox.",
    messageFailedTitle: "Could not send message",
    currentLabel: "Current",
    filters: {
      all: "All",
      clear: "Clear",
    },
    counts: {
      total: "Total",
      reviewed: "Reviewed",
      shortlisted: "Shortlisted",
      interview: "Interview",
      offered: "Offer",
      rejected: "Rejected",
    },
    status: {
      applied: "Applied",
      reviewed: "Reviewed",
      shortlisted: "Shortlisted",
      interview: "Interview",
      offered: "Offered",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
    },
    stage: {
      inbox: "Inbox",
      screening: "Screening",
      interview: "Interview",
      offer: "Offer",
    },
    actions: {
      sendMessage: "Send message",
      sendingMessage: "Sending...",
      openThread: "Open thread",
      openApplication: "Open application",
      reviewed: "Mark reviewed",
      shortlisted: "Shortlist",
      interview: "Move to interview",
      offered: "Move to offer",
      rejected: "Reject candidate",
    },
    sections: {
      actions: "Hiring actions",
      message: "Recruiter message",
      contact: "Private details",
      screeningAnswers: "Screening answers",
      statusHistory: "Status history",
      smartCv: "CV",
      submittedCv: "Submitted CV",
      additionalCv: "Additional CV",
      profile: "Profile summary",
      skills: "Skills",
      experience: "Experience",
      education: "Education",
      certifications: "Certifications",
      highlights: "Highlights",
      projects: "Projects",
    },
    fields: {
      job: "Job",
      company: "Company",
      appliedAt: "Applied",
      updatedAt: "Updated",
      location: "Location",
      email: "Email",
      phone: "Phone",
      nationality: "Nationality",
      byUserId: "Updated by {{id}}",
      coverLetter: "Cover letter",
    },
    cv: {
      updatedOn: "Updated {{date}}",
      submitted: "Submitted for this role",
      additional: "Additional version",
      empty: "No CV attached to this application.",
      sectionFallback: "Untitled section",
    },
    pagination: {
      previous: "Previous page",
      next: "Next page",
      pageOf: "Page {{page}} of {{total}}",
    },
    noData: {
      cv: "No CV attached to this application.",
      screeningAnswers: "No screening answers were submitted.",
      statusHistory: "No status updates yet.",
      profile: "No profile summary added yet.",
      skills: "No skills listed yet.",
      experience: "No experience added yet.",
      education: "No education added yet.",
      certifications: "No certifications added yet.",
      highlights: "No highlights added yet.",
      projects: "No projects added yet.",
    },
  },

  employerCandidates: {
    title: "Candidates",
    detailHeaderFallback: "Candidate",
    subtitle:
      "Search discoverable Tabbio talent, save strong profiles into your private Library, and shortlist them for follow-up.",
    searchTitle: "Search the candidate directory",
    searchSubtitle:
      "Use boolean search with AND, OR, NOT, quotes, and field prefixes like skills:, location:, and company:.",
    searchPlaceholder:
      'skills:"product design" AND location:dubai NOT company:"Acme"',
    clearFilters: "Clear filters",
    moreFilters: "Filters",
    moreFiltersTitle: "More filters",
    searchByJD: "Search by job description",
    searchByJDTitle: "Paste a job description",
    searchByJDPlaceholder: "Paste the full job description here…",
    searchByJDAction: "Search",
    resultsCount: "{{count}} candidates",
    smartParse: {
      buttonLabel: "Smart parse",
      successTitle: "Search translated",
      successMessage_one: "Mapped {{count}} filter from your search",
      successMessage_other: "Mapped {{count}} filters from your search",
      fallbackTitle: "Searched as keyword",
      fallbackMessage:
        "Couldn't extract specific filters: the original text stays as a keyword query.",
      errorTitle: "Could not parse search",
    },
    resultsHint:
      "Structured filters combine with the boolean query, and only public discoverable candidates appear here.",
    loadingTitle: "Loading candidates...",
    loadingSubtitle:
      "Pulling public, discoverable candidates for this company workspace.",
    loadingDetailTitle: "Loading candidate...",
    loadingDetailSubtitle:
      "Fetching the profile, contact visibility, and shortlist state.",
    errorTitle: "Could not load candidates",
    errorSubtitle:
      "Try again in a moment. Your active workspace still needs company access.",
    emptyTitle: "No matching candidates",
    emptySubtitle:
      "Try a broader boolean search or clear some location, skill, or company filters.",
    notFoundTitle: "Candidate not found",
    notFoundSubtitle:
      "This candidate is no longer available in the public directory for the active workspace.",
    retry: "Try again",
    back: "Back",
    contactEmpty:
      "No reachable contact options are available for this candidate right now.",
    outreachTemplates: {
      openButton: "Templates",
      sheetTitle: "Outreach templates",
      sheetSubtitle:
        "Pick a saved template to fill in the message, or save what you've drafted for next time.",
      loading: "Loading templates...",
      empty:
        "No templates yet: start typing in the box below to create your first template.",
      privateBadge: "Private",
      companyBadge: "Team",
      byCreator: "Team · by {{name}}",
      deleteAction: "Delete template",
      saveCurrentTitle: "Save current message as a template",
      namePlaceholder: "Template name (e.g. Initial outreach)",
      saveButton: "Save template",
      savingButton: "Saving…",
      visibility: {
        private: "Private",
        company: "Team",
      },
      appliedTitle: "Template applied",
      appliedMessage: 'Loaded "{{name}}" into the message.',
      savedTitle: "Template saved",
      deletedTitle: "Template deleted",
      errorTitle: "Could not save template",
    },
    outreachHint:
      "Sending outreach creates or reuses the Tabbio conversation, and the candidate also gets email when one is available.",
    outreachDrafting: "Drafting outreach with AI…",
    composeTitle: "Message {{name}}",
    composeSubtitle: "Send a recruiter message without leaving the directory.",
    outreachPlaceholder: "Write a recruiter message to this candidate",
    outreachLockedBody:
      "Direct outreach is locked on the free company plan. Upgrade the workspace to unlock recruiter messaging and private contact visibility.",
    outreachCharCounter: "{{count}} / {{max}}",
    outreachValidation: {
      tooShort: "Message must be at least {{min}} characters",
      tooLong: "Message must be at most {{max}} characters",
      placeholders:
        "Resolve template placeholders like {{'{{name}}'}} before sending",
    },
    preSendChecklist: {
      title: "Before sending",
      emailOnFile: "Candidate has an email on file",
      messageLength: "Message is at least 30 characters",
      noPlaceholders: "No leftover {placeholders} in the message",
    },
    experienceEmpty: "No public experience details are available yet.",
    educationEmpty: "No public education details are available yet.",
    educationFallback: "Education",
    matchEvidence: {
      skills_one: "1 matched skill",
      skills_other: "{{count}} matched skills",
      company: "Same company",
      location: "Same location",
      experience: "Role fit",
      source: "Open to work",
    },
    spotlight: {
      openToWork: "Open to work",
      recentlyActive: "Recently active",
      highMatch: "High match",
      notContacted: "Not contacted",
      saved: "Saved",
      shortlisted: "Shortlisted",
      pastApplicant: "Past applicant",
    },
    badges: {
      saved: "Saved to Library",
      shortlistedCount: "{{count}} shortlists",
      outreachLocked: "Outreach locked",
    },
    filters: {
      location: "Locations",
      skills: "Skills",
      companies: "Companies",
      locationPlaceholder: "Dubai, Abu Dhabi",
      skillsPlaceholder: "React, Figma, Recruiting",
      companiesPlaceholder: "Google, Careem, Tabbio",
    },
    actions: {
      save: "Save",
      saved: "Saved",
      createShortlist: "Shortlist",
      shortlisted: "Added",
      message: "Message",
      chat: "Chat",
      email: "Email",
      sendOutreach: "Send",
      cancel: "Cancel",
      suggestDraft: "Suggest with AI",
      regenerateDraft: "Regenerate with AI",
    },
    confirm: {
      saveTitle: "Save to Library?",
      saveMessage:
        "Add {{name}} to your private candidate Library for follow-up.",
      shortlistTitle: "Add to shortlist?",
      shortlistMessage: "Add {{name}} to a shortlist for this role.",
      chatTitle: "Start a chat?",
      chatMessage: "Open a Tabbio conversation with {{name}}.",
    },
    sections: {
      contact: "Visible contact channels",
      shortlists: "Shortlists",
      outreach: "Outreach",
      skills: "Skills",
      about: "About",
      experience: "Experience",
      education: "Education",
    },
    contacts: {
      tabbioMessage: "Tabbio inbox",
      whatsapp: "WhatsApp",
      telegram: "Telegram",
      slack: "Slack",
      email: "Email",
      phone: "Phone",
      landline: "Landline",
    },
    contactValues: {
      tabbioMessage: "Open in Tabbio inbox",
    },
    shortlists: {
      empty:
        "You have not created a shortlist yet. Create one here and the candidate will be added immediately.",
      defaultName: "{{name}} shortlist",
    },
    toasts: {
      savedTitle: "Candidate saved",
      savedMessage: "The candidate is now available inside your Library.",
      saveFailedTitle: "Could not save candidate",
      shortlistCreatedTitle: "Shortlist created",
      shortlistCreatedMessage: "The candidate was added to the new shortlist.",
      shortlistAddedTitle: "Shortlist updated",
      shortlistAddedMessage: "The candidate was added to the shortlist.",
      shortlistAlreadyAddedMessage:
        "This candidate is already in that shortlist.",
      shortlistFailedTitle: "Could not update shortlist",
      emailFailedTitle: "Could not open email",
      outreachSentTitle: "Outreach sent",
      outreachSentMessage:
        "The candidate can now reply from their Tabbio inbox.",
      outreachFailedTitle: "Could not send outreach",
      draftReadyTitle: "AI draft ready",
      draftReadyMessage: "Review the draft and edit before sending.",
      draftFailedTitle: "Could not draft outreach",
    },
  },

  // Services / Apps
  services: {
    title: "Apps",
    subtitle:
      "Connect Tabbio to Gmail, Zapier, Make, Calendar, and MCP sources.",
    searchPlaceholder: "Search connections…",
    // Categories
    categories: {
      topPicks: "Top Picks",
      career: "Career",
      writing: "Writing",
      interview: "Interview",
      analytics: "Views",
    },
    // Sections
    featured: "Featured",
    featuredSubtitle: "Curated career tools by Tabbio",
    trending: "Trending",
    trendingSubtitle: "Most popular services by our users",
    // Service names
    cvScorePro: "CV Score Pro",
    tailoredCvs: "Tailored CVs",
    coverLetters: "Cover Letters",
    applicationPack: "Application Pack",
    interviewPrep: "Interview Prep",
    salaryInsights: "Salary Insights",
    recruiterPreview: "Recruiter Preview",
    autoApply: "Automation",
    analytics: "Views",
    myServices: "My Services",
    library: "Library",
    shortlists: "Shortlists",
    gmail: "Gmail",
    zapier: "Zapier",
    make: "Make",
    calendar: "Calendar",
    memoryMcp: "Memory + MCP",
    // Service descriptions
    cvScoreProDesc:
      "Score your CV against a GCC role, ATS checks, missing skills, and recruiter risks…",
    tailoredCvsDesc:
      "Create customized CVs tailored to specific job descriptions using AI analysis…",
    coverLettersDesc:
      "Generate personalized cover letters that highlight your strengths and match job requirements…",
    applicationPackDesc:
      "Build a complete job-specific pack with CV notes, letter, answers, prep, salary, and checklist…",
    interviewPrepDesc:
      "Practice interviews with AI and get real-time feedback on your answers…",
    salaryInsightsDesc:
      "Estimate a GCC salary range, confidence level, and negotiation script for your target role…",
    recruiterPreviewDesc:
      "See how employers may read your profile, evidence, verification, and match risks…",
    autoApplyDesc:
      "Schedule job-search automations from your profile and review every match before applying…",
    analyticsDesc:
      "Track your job search progress, application insights, and profile views…",
    myServicesDesc:
      "Manage your active services, subscriptions, and usage history…",
    libraryDesc:
      "Manage candidate library, role pipelines, and profile actions in one place…",
    shortlistsDesc:
      "Create shortlist links, collect reviewer decisions, and finalize hiring picks…",
    gmailDesc:
      "Sync career emails, recruiter replies, and application updates into your Tabbio memory graph.",
    zapierDesc:
      "Send Tabbio events into Zapier workflows for follow-ups, alerts, and handoffs.",
    makeDesc:
      "Connect Tabbio signals to Make scenarios for scheduled workflow automation.",
    calendarDesc:
      "Coordinate interviews, reminders, and application follow-ups with your calendar.",
    memoryMcpDesc:
      "Manage connected memory, CV context, and MCP access used by the agent.",
    byTabbio: "By Tabbio",
    manageConnection: "Manage",
    comingSoon: "Coming soon",
    noServices: "No connections found",
    noServicesSubtitle:
      "Connected apps and automation providers will appear here",
  },

  library: {
    title: "Library",
    candidateProfileTitle: "Candidate Profile",
    sections: {
      database: "Database",
      roles: "Roles",
      activeJobs: "Active Jobs Posted",
      collections: "Collections",
      savedSearches: "Saved Searches",
    },
    database: {
      allCandidates: "All Candidates",
      talentPool: "Talent Pool",
      offlineCvs: "Offline CVs",
    },
    actions: {
      newRole: "New Role",
      newCollection: "New Collection",
      saveSearch: "Save Search",
      viewFullCv: "View Full CV",
      addToShortlist: "Add to Shortlist",
      selectShortlist: "Select Shortlist",
      createShortlist: "Create Shortlist",
      manageCollections: "Manage Collections",
      star: "Star Candidate",
      unstar: "Remove Star",
      reject: "Reject",
    },
    roleCandidates: "{{count}} candidates",
    searchPlaceholder: "Search candidates...",
    loading: "Loading library...",
    errorTitle: "Could not load library data",
    setup: {
      title: "Library setup in progress",
      subtitle:
        "The latest library tables are not available yet. Apply the latest library migration, then refresh this screen.",
      blockedTitle: "Library setup required",
      blockedMessage:
        "This action is unavailable until the latest library setup finishes.",
    },
    match: "MATCH",
    matchScore: "MATCH SCORE",
    addedOn: "ADDED",
    stage: {
      inbox: "Inbox",
      screening: "Screening",
      interview: "Interview",
      offer: "Offer",
      talent_pool: "Talent Pool",
    },
    source: {
      page: "Page",
      email: "Email",
      upload: "Upload",
      scan: "Scan",
    },
    kind: {
      applicant: "Applicant",
      external: "External Upload",
    },
    origin: {
      applied: "Applied",
      saved: "Saved",
      uploaded: "Uploaded",
      email: "Email",
    },
    sort: {
      title: "Sort By",
      options: {
        newest: "Newest First",
        match_desc: "Match % (High to Low)",
        name_asc: "Name (A-Z)",
      },
    },
    filters: {
      title: "Filters",
      location: "Location",
      stage: "Stage",
      source: "Source",
      decision: "Decision",
      candidateType: "Candidate Type",
      collection: "Collection",
      locationPlaceholder: "Add a city or country",
      starredOnly: "Starred Only",
      apply: "Apply Filters",
      allFilters: "All Filters",
    },
    addSheet: {
      title: "Add to Library",
      featuredTitle: "Tabbio Database",
      featured: "Featured",
      featuredSubtitle: "Search candidate profiles on Tabbio",
      uploadCv: "Upload CV",
      uploadCvSubtitle: "Add a PDF or DOCX from your device",
      emailForward: "Email Forward",
      emailUnavailable: "Forwarding address unavailable",
      emailUnavailableTitle: "Forwarding address unavailable",
      emailNotConfigured: "Forwarding setup required",
      emailNotConfiguredTitle: "Email forwarding needs setup",
      emailNotConfiguredMessage:
        "Configure the inbound resumes domain and SendGrid Parse webhook before sharing this address.",
      emailCopiedTitle: "Address copied",
      emailCopiedMessage: "Share this address to receive resumes in Library.",
      emailSharedTitle: "Address shared",
      emailSharedMessage: "The forwarding address is ready to send.",
    },
    jobs: {
      emptyTitle: "No active jobs yet",
      emptySubtitle:
        "Post your first live role and its applicants will appear here.",
    },
    roleActions: {
      title: "Role Actions",
      editRole: "Edit Role Details",
      pauseRole: "Toggle Status (Pause)",
      activateRole: "Toggle Status (Activate)",
      deleteRole: "Delete Role",
      updatedTitle: "Role updated",
      errorTitle: "Could not update role",
      nameLabel: "Role name",
      namePlaceholder: "Update role name",
    },
    upload: {
      successTitle: "CV uploaded",
      successMessage: "The candidate is now available in your library.",
      errorTitle: "Could not upload CV",
      uploading: "Uploading...",
      processingTitle: "Uploading CV to Library",
      processingMessage:
        "Please wait while we parse the resume and create the candidate profile.",
      parsingPill: "Processing CV…",
      parsingFailedPill: "Could not parse CV",
    },
    collections: {
      createTitle: "Create Collection",
      createAction: "Create Collection",
      nameLabel: "Collection Name",
      namePlaceholder: "e.g. Final Interview Loop",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Optional notes for your team...",
      candidateCount: "{{count}} candidates",
      emptyTitle: "No collections yet",
      emptySubtitle: "Create a collection to group candidates privately.",
      emptyCandidatesTitle: "No candidates in this collection",
      emptyCandidatesSubtitle:
        "Add candidates to this collection to keep the shortlist organized.",
      requiredTitle: "Collection name is required.",
      createdTitle: "Collection created",
      createdMessage: "You can start adding candidates to it now.",
      errorTitle: "Could not update collections",
      manageTitle: "Manage Collections",
      manageDescription:
        "Choose which private collections should include this candidate.",
      manageCandidatesTitle: "Manage Candidates",
      manageCandidatesDescription:
        "Choose which library candidates should belong to {{name}}.",
      manageCandidatesAction: "Manage Candidates",
      savedTitle: "Collections updated",
    },
    roleOverview: {
      sectionTitle: "Role Overview",
      lastApplicantAt: "Last applicant",
      lastApplicantNone: "No applicants yet",
      tiles: {
        applicants: "Applicants",
        savedCandidates: "Saved candidates",
        shortlisted: "Shortlisted",
        contacted: "Contacted",
        highMatchNotContacted: "High match, not contacted",
        recentActivity: "Recent activity",
        savedSearches: "Saved searches",
      },
    },
    savedSearches: {
      sectionTitle: "Saved Searches",
      emptyTitle: "No saved searches yet",
      emptySubtitle:
        "Save a filter combo from the candidates page to recall it from here.",
      savingState: "Saving…",
      savedTitle: "Search saved",
      savedAsMessage: 'Saved as "{{name}}". Open it from the Library home.',
      saveErrorTitle: "Could not save search",
      deleteAction: "Delete",
      deletedTitle: "Saved search deleted",
      filtersSummary: {
        empty: "No filters",
        location: "{{count}} locations",
        location_one: "1 location",
        skill: "{{count}} skills",
        skill_one: "1 skill",
        company: "{{count}} companies",
        company_one: "1 company",
      },
    },
    status: {
      shortlisted: "Shortlisted",
      rejected: "Rejected",
    },
    star: {
      savedTitle: "Candidate starred",
      removedTitle: "Star removed",
    },
    reject: {
      confirmTitle: "Reject candidate?",
      confirmMessage:
        "This candidate will be marked as rejected in your library.",
      successTitle: "Candidate rejected",
    },
    shortlists: {
      savedTitle: "Candidate added to shortlist",
      errorTitle: "Could not update shortlist",
      addedState: "Added",
    },
    profile: {
      summary: "Summary",
      roles: "Roles",
      collections: "Collections",
      experience: "Experience",
      education: "Education",
      documents: "Documents",
      noSummary: "No summary available yet.",
      noRoles: "This candidate is not linked to a role yet.",
      noCollections: "This candidate is not part of any collection yet.",
      noExperience: "No experience was parsed yet.",
      noEducation: "No education was parsed yet.",
      noDocuments: "No uploaded documents are available yet.",
      roleActive: "Active role",
      rolePaused: "Paused role",
      locationUnknown: "Location unavailable",
    },
    cvSnippet: {
      title: "CV Preview Snippet",
      mostRecent: "Most Recent",
    },
    cvSheet: {
      title: "Full CV Preview",
      placeholder:
        "Static CV preview for now. Full CV rendering and download actions will be connected in the next phase.",
      noDocumentPreview: "No extracted preview is available for this file yet.",
    },
    empty: {
      title: "No candidates found",
      subtitle: "Try changing search, filters, or selected role.",
    },
    projects: {
      title: "Projects",
      subtitle: "Hiring workspaces for roles you're actively recruiting for.",
      sectionLabel: "Projects",
      newProject: "New Project",
      empty: {
        title: "No projects yet",
        subtitle:
          "Create a project to manage candidates through a hiring pipeline.",
      },
      detailEmpty: {
        title: "No candidates in this project yet",
        subtitle: "Add candidates from your Library to start a pipeline.",
      },
      error: {
        title: "Couldn't load projects",
        subtitle: "Something went wrong. Try again.",
        retry: "Retry",
      },
      candidateCount: "{{count}} candidates",
      stageCount: "{{stage}}: {{count}}",
      tabs: {
        overview: "Overview",
        talentPool: "Talent Pool",
        pipeline: "Pipeline",
        jobPost: "Job Post",
        settings: "Settings",
      },
      overview: {
        totalCandidates: "Candidates",
        stageBreakdown: "Pipeline Breakdown",
        linkedJob: "Linked Job",
        noLinkedJob: "No job linked",
      },
      jobPost: {
        empty: {
          title: "No job linked to this project",
          subtitle: "Link an existing job post to surface applicants here.",
        },
        viewJob: "View Job",
      },
      settings: {
        nameLabel: "Project Name",
        namePlaceholder: "e.g. Frontend Engineer Q3 Hire",
        descriptionLabel: "Description",
        descriptionPlaceholder:
          "Why this hire, success criteria, must-haves...",
        statusLabel: "Status",
        statusActive: "Active",
        statusArchived: "Archived",
        save: "Save Changes",
        deleteProject: "Delete Project",
        deleteConfirm:
          "Delete this project? Candidates remain in your Library.",
      },
      create: {
        title: "Create Project",
        submit: "Create Project",
        nameLabel: "Project Name",
        namePlaceholder: "e.g. Senior Engineer Hire",
        descriptionLabel: "Description (Optional)",
        descriptionPlaceholder: "Context for this hire...",
        requiredName: "Project name is required.",
      },
      pipeline: {
        moveTo: "Move To",
        stages: {
          inbox: "Inbox",
          screening: "Screening",
          interview: "Interview",
          offer: "Offer",
          hired: "Hired",
          rejected: "Rejected",
        },
      },
    },
    onboarding: {
      title: "Get Started",
      subtitle: "Set up your recruiter workspace in 4 quick steps.",
      steps: {
        importCvs: {
          title: "Import CVs",
          subtitle: "Upload candidate resumes to your Library.",
        },
        setForwarding: {
          title: "Set up email forwarding",
          subtitle: "Auto-import candidates from your inbox.",
        },
        createProject: {
          title: "Create a project",
          subtitle: "Organize candidates by role.",
        },
        shortlist: {
          title: "Build a shortlist",
          subtitle: "Share candidates with stakeholders for feedback.",
        },
      },
      progressLabel: "{{done}} of {{total}} complete",
    },
    forwarding: {
      readiness: {
        not_set: {
          title: "Set up email forwarding",
          subtitle: "Auto-import candidate CVs from your inbox.",
        },
        partial: {
          title: "Forwarding configured",
          subtitle: "Waiting for the first forwarded email to arrive.",
        },
        active: {
          title: "Forwarding active",
          subtitle:
            "Candidates from forwarded emails are being added to your Library.",
        },
      },
    },
    spotlights: {
      open_to_work: "Open to work",
      past_applicant: "Past applicant",
      strong_skills: "Strong skills",
      recently_active: "Recently active",
      needs_review: "Needs review",
    },
    boolFilters: {
      skill: "Skill",
      jobTitle: "Title",
      company: "Company",
      location: "Location",
      experience: "Experience",
      education: "Education",
      language: "Language",
      openToWork: "Open to work",
      pastApplicant: "Past applicant",
    },
  },

  shortlists: {
    title: "Shortlists",
    searchPlaceholder: "Search shortlists...",
    meta: {
      candidates: "{{count}} candidates",
    },
    empty: {
      title: "No shortlists yet",
      subtitle: "Create your first shortlist to start collecting feedback.",
    },
    actions: {
      add: "Add",
      addCandidates: "Add Candidates",
      save: "Save",
      deleteShortlist: "Delete Shortlist",
      startReviewing: "Start Reviewing",
      loginWithTabbio: "Login with Tabbio",
      pass: "Pass",
      shortlist: "Shortlist",
      submitReview: "Submit Review",
    },
    create: {
      title: "Create Shortlist",
      nameLabel: "Shortlist Name",
      namePlaceholder: "e.g. Frontend Q3 Candidates",
      descriptionLabel: "Description (Optional)",
      descriptionPlaceholder: "Context for the hiring manager...",
      submit: "Create Shortlist",
      requiredName: "Shortlist name is required.",
    },
    edit: {
      title: "Edit Shortlist",
      nameLabel: "Name",
      descriptionLabel: "Description",
      jobDescriptionLabel: "Job Description",
      jobDescriptionHint: "Optional · enables match %",
      pickRolePlaceholder: "Pick from active role...",
      pastePlaceholder: "Or paste manually...",
      deleteTitle: "Delete shortlist?",
      deleteMessage: "This action cannot be undone.",
    },
    addCandidates: {
      title: "Add Candidates",
      searchPlaceholder: "Search candidates...",
      apply: "Apply ({{count}})",
      empty: "No candidates found",
    },
    detail: {
      linkCopied: "Link copied",
      linkCopyFailed: "Could not copy link",
      feedback: "Feedback",
      feedbackSummary: "{{reviewers}} reviewers · {{reviews}} reviews",
      yesCount: "{{count}} yes",
      noCount: "{{count}} no",
      yes: "Yes",
      no: "No",
      candidatesCount: "Candidates ({{count}})",
      noCandidates: "No candidates yet",
      noCandidatesSubtitle: "Add candidates from your library to get started",
    },
    public: {
      by: "by",
      namePlaceholder: "Enter your name to join...",
      or: "OR",
      poweredBy: "Powered by",
      enterNameRequired: "Please enter your name to continue.",
      reviewUnavailable:
        "This shortlist is not available for reviewing right now.",
      loginPlaceholder: "Login flow will be connected in the next phase.",
    },
    review: {
      notePlaceholder: "Add a note...",
      sections: {
        experience: "Experience",
        education: "Education",
        skills: "Skills",
      },
    },
    confirm: {
      title: "Confirm Review",
      shortlisted: "Shortlisted",
      passed: "Passed",
      sectionShortlisted: "Shortlisted",
      noNote: "No note",
      noShortlisted: "No shortlisted candidates yet",
      reviewingAs: "Reviewing as {{name}}",
    },
    time: {
      justNow: "just now",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      weeksAgo: "{{count}}w ago",
    },
  },

  // Automation
  autoApply: {
    title: "Automation",
    subtitle:
      "Review scheduled job-search automations, cron-style checks, and the signals your agent uses. Applications stay in your control.",
    lastRun: "Last checked:",
    never: "Never",
    loading: "Loading automation...",
    loadFailed: "Could not load automation",
    summaryTitle: "Automation overview",
    summaryBody:
      "{{enabledCount}} of {{totalCount}} match signals are watching. Latest check {{latestRun}}. Every opportunity stays review-first.",
    rulesTitle: "Match signals",
    rulesBody:
      "These preferences guide what Tabbio surfaces. We are not submitting applications automatically.",
    metrics: {
      enabled: "Signals on",
      queue: "To review",
      recent: "Recent matches",
    },
    queueTitle: "Review queue",
    queueBody: "{{count}} matched jobs waiting for your review",
    queueCount: "Queue: {{count}}",
    enabledCount: "Enabled: {{count}}",
    enabled: "Signal watching",
    disabled: "Signal paused",
    runNow: "Refresh matches",
    runStarted: "Match refresh started",
    updateFailed: "Could not update this signal",
    runFailed: "Could not refresh matches",
    matchWatching: "Watching",
    matchPaused: "Paused",
    reviewFirst: "Review-first",
    activityTitle: "Recent activity",
    activityBody: "Latest matches, review status changes, and alerts",
    activityEmptyTitle: "No recent activity",
    activityEmptyBody:
      "New matched jobs will appear here when Automation finds a strong fit.",
    activityCount: "{{count}} recent",
    activityMatchScore: "{{score}} match",
    activityLastUpdated: "Updated {{time}}",
    activityReasons: "Why it matched: {{reasons}}",
    activityChannels: "Notified via {{channels}}",
    activityDismissed: "Dismissed",
    activityDismissFailed: "Could not dismiss this activity",
    openJob: "Open job",
    dismiss: "Dismiss",
    // Frequency badges
    daily: "DAILY",
    weekly: "WEEKLY",
    instant: "INSTANT",
    monthly: "MONTHLY",
    counts: {
      matched: "Matched",
      eligibleAutoApply: "Ready",
      needsReview: "Needs review",
      autoApplied: "Reviewed",
      skipped: "Skipped",
      failed: "Failed",
      dismissed: "Dismissed",
    },
    activityStatuses: {
      matched: "Matched",
      eligibleAutoApply: "Ready to review",
      needsReview: "Needs review",
      autoApplied: "Reviewed",
      skipped: "Skipped",
      failed: "Failed",
      dismissed: "Dismissed",
    },
    settings: {
      minMatchScore: "{{count}}% match score",
      sources: "{{count}} sources",
      locations: "{{count}} locations",
      workModes: "{{count}} work modes",
      employmentTypes: "{{count}} employment types",
      pushEnabled: "Push alerts",
      emailDigestEnabled: "Email digest",
      digestHourLocal: "Digest at {{time}}",
    },
    // Automation items
    jobAlerts: "New job alerts",
    jobAlertsDesc: "Get notified when new jobs match your profile",
    jobAlertsResult: "3 new jobs found",
    autoApplyMatches: "Automation",
    autoApplyMatchesDesc:
      "Finds high-fit jobs and keeps each match ready for review before you apply",
    autoApplyMatchesResult: "2 matches ready",
    marketReport: "Weekly market report",
    marketReportDesc: "Summary of job market trends in your field",
    marketReportResult: "Report delivered",
    profileViewAlerts: "Profile view alerts",
    profileViewAlertsDesc: "Get notified when employers view your CV",
    profileViewAlertsResult: "ADNOC viewed your profile",
    salaryInsights: "Salary insights",
    salaryInsightsDesc: "Monthly salary benchmark for your target roles",
  },

  // Analytics
  analytics: {
    title: "Views",
    personalSubtitle:
      "Track how your public profile performs and where companies discover you.",
    employerTitle: "Company views",
    employerSubtitle:
      "Measure company page interest, hiring funnel health, and talent discovery performance.",
    period7d: "7d",
    period30d: "30d",
    periodAll: "All time",
    heroSummary7d: "Last 7 days",
    heroSummary30d: "Last 30 days",
    heroSummaryAll: "All time",
    retry: "Try again",
    errorTitle: "Views couldn't load",
    errorSubtitle: "Refresh to fetch the latest view data from the backend.",
    overviewTab: "Overview",
    talentTab: "Talent",
    jobsTab: "Jobs",
    publicProfileSection: "Public profile",
    publicProfileSectionSubtitle:
      "These numbers come from backend-tracked views and CTA activity on your public page.",
    publicProfile: "Public profile",
    publicCv: "Public CV",
    privateShareLink: "Private share link",
    publicEngagementSection: "Engagement",
    recentPublicActivity: "Recent public activity",
    discoverabilitySection: "Discoverability",
    discoverabilitySectionSubtitle:
      "See how often employers matched your profile in search and opened it after searching.",
    discoverability: "Discoverability",
    companyPageSection: "Company page",
    companyPageSectionSubtitle:
      "Track how your public company presence converts into job clicks and talent interest.",
    companyPage: "Company page",
    talentSection: "Talent discovery",
    talentSectionSubtitle:
      "Understand what employers searched, who they opened, and which candidates moved forward.",
    jobsSection: "Jobs performance",
    jobsSectionSubtitle:
      "Prioritize the roles that are attracting attention and converting into applications.",
    jobPerformanceSection: "Job performance",
    jobPerformanceSectionSubtitle:
      "This role's funnel is calculated on backend endpoint hits and application events.",
    views: "Views",
    clicks: "Clicks",
    ctr: "CTR",
    pageViews: "Page views",
    cvViews: "CV views",
    shareLinkViews: "Share-link views",
    uniqueVisitors: "Unique visitors",
    uniqueViewers: "Unique viewers",
    ctaClicks: "CTA clicks",
    contactClicks: "Contact clicks",
    downloads: "Downloads",
    shares: "Shares",
    publicCtaClicks: "Public CTA clicks",
    publicJobClicks: "Public job clicks",
    jobViews: "Job views",
    applyIntents: "Apply intents",
    applications: "Applications",
    searches: "Searches",
    searchMatches: "Search matches",
    profileViews: "Profile views",
    uniqueCompanies: "Unique companies",
    conversionRate: "Conversion rate",
    shortlisted: "Shortlisted",
    starred: "Starred",
    rejected: "Rejected",
    pageViewsHelper: "Every backend-tracked view on your profile.",
    uniqueVisitorsHelper: "Deduplicated visitors in the selected range.",
    uniqueViewersHelper:
      "Deduplicated viewers of this job in the selected range.",
    ctaClicksHelper: "Clicks on trackable calls to action from your profile.",
    publicCtaClicksHelper: "Clicks on company profile CTA links.",
    publicJobClicksHelper: "Clicks from the company page into open roles.",
    companyPageViewsHelper: "Views captured through the backend view pixel.",
    jobViewsHelper: "Job detail openings recorded on the backend.",
    applyIntentsHelper: "Preparation or apply-open events before submission.",
    applicationsHelper: "Completed native job applications.",
    searchesHelper: "Employer searches recorded in talent surfaces.",
    searchMatchesHelper:
      "Times your profile appeared in employer search results.",
    profileViewsHelper: "Employer profile opens attributed to discoverability.",
    uniqueCompaniesHelper: "Distinct employers who searched or viewed you.",
    shortlistedHelper: "Candidates moved into shortlists during this range.",
    starredHelper: "Candidates marked for follow-up during this range.",
    rejectedHelper:
      "Candidates dismissed from consideration during this range.",
    conversionRateHelper:
      "Applications divided by apply intents, or by views if intents are unavailable.",
    viewsOverTime: "Views Over Time",
    profileViewsChart: "Profile views over time",
    profileViewsChartSubtitle:
      "Backend-tracked public profile views for the selected range.",
    companyPageViewsChart: "Company page views over time",
    companyPageViewsChartSubtitle: "Backend-tracked public company page views.",
    searchMatchesChart: "Search matches over time",
    searchMatchesChartSubtitle:
      "When your profile appeared in employer searches.",
    candidateViewsChart: "Candidate views over time",
    candidateViewsChartSubtitle:
      "Profile opens after employer discovery and search.",
    jobViewsChart: "Job views over time",
    jobViewsChartSubtitle: "Role detail views across the selected range.",
    applyIntentsChart: "Apply intents over time",
    applyIntentsChartSubtitle: "Backend prepare/open steps before submission.",
    applicationsChart: "Applications over time",
    applicationsChartSubtitle: "Submitted native applications for this role.",
    topLinks: "Top Links",
    trafficSources: "Traffic Sources",
    devices: "Devices",
    topCountries: "Top Countries",
    topSources: "Top sources",
    topLocations: "Top locations",
    topCvTargets: "Top CVs",
    topKeywords: "Top keywords",
    topKeywordsSubtitle:
      "The search terms most often associated with discovery and profile opens.",
    recentActivity: "Recent company activity",
    recentActivitySubtitle:
      "The latest companies that matched your profile in search and then opened it.",
    topApplicants: "Top applicants",
    topApplicantsSubtitle:
      "Ranked using application stage, shortlist status, match score, and recency.",
    topCandidates: "Top candidates",
    topCandidatesSubtitle:
      "The candidates employers interacted with most in this range.",
    topJobs: "Top jobs",
    topJobsSubtitle:
      "Roles with the strongest mix of views, apply intent, and completed applications.",
    jobsPerformanceTitle: "Hiring pipeline",
    jobsPerformanceSubtitle:
      "Overall job funnel metrics for the current range.",
    jobFunnelTitle: "Job funnel",
    jobFunnelSubtitle: "How visitors progressed from view to application.",
    talentFunnelTitle: "Talent funnel",
    talentFunnelSubtitle:
      "How employer discovery moved candidates through the funnel.",
    candidateFeedTitle: "Candidate discoverability feed",
    candidateFeedSubtitle:
      "Reserved for candidate-side discoverability rollups exposed to company analytics.",
    jobApplicantsSubtitle:
      "Applicants ranked for this job using the backend analytics ordering rules.",
    emptyKeywordsTitle: "No keyword data yet",
    emptyKeywordsSubtitle:
      "Keyword analytics will appear after employers search for you or your candidates.",
    emptyPublicSourcesTitle: "No source data yet",
    emptyPublicSourcesSubtitle:
      "Sources appear once public profile, CV, or share-link visits are tracked.",
    emptyPublicLocationsTitle: "No location data yet",
    emptyPublicLocationsSubtitle:
      "Location breakdowns appear when the hosting provider sends visitor geography.",
    emptyPublicCvTargetsTitle: "No CV target data yet",
    emptyPublicCvTargetsSubtitle:
      "CV-specific reach appears after visitors open public CV or private share links.",
    emptyPublicActivityTitle: "No public activity yet",
    emptyPublicActivitySubtitle:
      "Views, contact clicks, downloads, and shares will appear here once visitors interact.",
    anonymousVisitor: "Anonymous visitor",
    publicEventView: "Viewed",
    publicEventShare: "Shared",
    publicEventCta: "CTA click",
    publicEventBlock: "Block click",
    publicEventJobApply: "Job click",
    publicEventContact: "Contact click",
    publicEventDownload: "Downloaded",
    emptyActivityTitle: "No company activity yet",
    emptyActivitySubtitle:
      "Employer search and profile-view activity will appear here once it happens.",
    emptyApplicantsTitle: "No applicants yet",
    emptyApplicantsSubtitle:
      "Applicants will appear here once this job starts receiving submissions.",
    emptyCandidatesTitle: "No candidate interactions yet",
    emptyCandidatesSubtitle:
      "Candidate views, stars, rejections, and shortlist actions will populate this list.",
    emptyCandidateFeedTitle: "No candidate-side discoverability feed yet",
    emptyCandidateFeedSubtitle:
      "This feed will fill when candidate discoverability rollups are exposed to company analytics.",
    emptyJobsTitle: "No tracked jobs yet",
    emptyJobsSubtitle:
      "Job analytics will appear once your published roles receive views or applications.",
    emptyJobFunnelTitle: "No job funnel data yet",
    emptyJobFunnelSubtitle:
      "Publish roles and drive traffic to start filling the job funnel.",
    emptyTalentFunnelTitle: "No talent funnel data yet",
    emptyTalentFunnelSubtitle:
      "Search, shortlist, or view candidates to populate the talent funnel.",
    candidateVisibilityTitle: "Visibility is tracked on the backend",
    candidateVisibilitySubtitle:
      "Search appearances, company opens, and profile CTA clicks all stay tied to endpoint-level analytics.",
    noRecentActivity: "No recent activity",
    unknownCompany: "Unknown company",
    viewedProfile: "Viewed profile",
    matchedOnly: "Matched only",
    noApplicantTitle: "No title added",
    noKeywordsYet: "No keywords yet",
    latestCompanyFallback: "Latest company unavailable",
    rejectedLabel: "Rejected",
    starredLabel: "Starred",
    shortlistedLabel: "Shortlisted",
    viewedLabel: "Viewed",
    jobDetailTitle: "Job analytics",
    jobDetailSubtitle:
      "Inspect this role's view, intent, and application funnel.",
    totalPoints: "{{count}} total points",
    totalViewsLabel: "{{count}} views",
    totalMatchesLabel: "{{count}} matches",
    totalSearchesLabel: "{{count}} searches",
    totalApplicationsLabel: "{{count}} applications",
    totalApplyIntentsLabel: "{{count}} apply intents",
    keywordRow: "{{searches}} searches · {{views}} profile views",
    activityRow: "Matched for “{{keyword}}” on {{matchedAt}}",
    funnelCountLabel: "{{count}} events",
    conversionValue: "{{value}} conversion",
    topApplicantMeta: "{{job}} · {{updatedAt}}",
    topCandidateMeta: "{{views}} views · {{shortlists}} shortlist actions",
    topJobMeta:
      "{{views}} views · {{applyIntents}} intents · {{applications}} applications",
    candidateFeedMeta:
      "{{matches}} matches · {{views}} views · {{companies}} companies",
    matchScoreValue: "{{value}} match",
    jobDetailMeta: "Current status: {{status}}",
    mobile: "Mobile",
    desktop: "Desktop",
    direct: "Direct",
    linkedin: "LinkedIn",
    instagram: "Instagram",
    whatsapp: "WhatsApp",
    other: "Other",
  },

  // Upgrade / Paywall
  entitlementGate: {
    eyebrowPlus: "Plus plan required",
    eyebrowPro: "Pro plan required",
    titlePlus: "Plus feature",
    titlePro: "Pro feature",
    descriptionPlus:
      "Available on Plus and above. Upgrade to unlock this feature.",
    descriptionPro:
      "Available on Pro and above. Upgrade to unlock this feature.",
    ctaUpgradeIndividualPlus: "Upgrade to Plus",
    ctaUpgradeIndividualPro: "Upgrade to Pro",
    ctaUpgradeEmployerPlus: "Upgrade workspace to Plus",
    ctaUpgradeEmployerPro: "Upgrade workspace to Pro",
    loading: "Checking subscription…",
    resourcesTitle: "What you unlock",
    resourcesSubtitle:
      "Every paid action returns a saved artifact, not just a chat answer.",
    benefits: {
      careerArtifacts: {
        title: "Structured career reports",
        body: "CV fit scores, missing skills, recruiter risks, and next actions saved in your app.",
      },
      applicationPack: {
        title: "Application-ready materials",
        body: "Cover letters, screening answers, interview prep, salary guidance, and apply checklists.",
      },
      marketPrep: {
        title: "GCC-specific context",
        body: "Role, country, seniority, and market signals shape the output before generation.",
      },
      employerReach: {
        title: "Private hiring tools",
        body: "Unlock recruiter workflows, candidate evidence, and workspace hiring actions.",
      },
      employerRanking: {
        title: "Better candidate decisions",
        body: "Use AI ranking, richer profile signals, and shortlist context to move faster.",
      },
      employerWorkflow: {
        title: "Saved team workflow",
        body: "Keep hiring work organized across library, shortlists, outreach, and reviews.",
      },
    },
  },
  upgrade: {
    screenTitle: "Upgrade",
    tabPlus: "Plus",
    tabPro: "Pro",
    // The pill beside a locked row names the LOWEST plan that unlocks it
    // (`getFeatureLockTier`), so it never contradicts the paywall it opens.
    lockBadge: {
      plus: "PLUS",
      pro: "PRO",
    },
    iconNote: "",
    featuresHeader: "Features",
    columnFree: "Free",
    columnPlus: "Plus",
    columnPro: "Pro",
    billingMonthly: "Monthly",
    billingYearly: "Yearly",
    billingYearlyBonus: "2 months free",
    disclaimer: "Auto-renews. Cancel anytime.",
    legalPrefix: "By subscribing, you agree to Tabbio's",
    legalAnd: "and",
    privacyPolicy: "Privacy Policy",
    termsOfUse: "Terms of Use (EULA)",
    legalSuffix: ".",
    ctaWord: "Upgrade",
    continueFree: "Continue with Free",
    purchaseSuccess: "Subscription updated",
    purchaseFailed: "Could not start checkout",
    checkoutCanceled: "Checkout canceled",
    companyRequired: "A company workspace is required for employer billing.",
    individual: {
      titlePlus: "Tailored for every job",
      titlePro: "Hand the hunt to your agent",
      subtitlePlus: "",
      subtitlePro: "",
      ctaPlusMonthly: "Upgrade for $14.99/mo",
      ctaPlusYearly: "Upgrade for $9.99/mo",
      ctaProMonthly: "Upgrade for $29.99/mo",
      ctaProYearly: "Upgrade for $24.99/mo",
    },
    employer: {
      titlePlus: "Hire smarter, not harder",
      titlePro: "Hire smarter, not harder",
      subtitlePlus:
        "More AI actions every day, stronger branding, and richer visibility.",
      subtitlePro:
        "More AI actions every day, stronger branding, and richer visibility.",
      ctaPlusMonthly: "Upgrade for $49.99/seat/mo",
      ctaPlusYearly: "Upgrade for $39.99/seat/mo",
      ctaProMonthly: "Upgrade for $99.99/seat/mo",
      ctaProYearly: "Upgrade for $79.99/seat/mo",
    },
    features: {
      moreAiUsage: "More AI usage",
      smarterAiModel: "Smarter AI model",
      downloadAnyFormat: "Download any format",
      customBranding: "Custom branding",
      fullAnalytics: "Full analytics",
      higherVisibility: "Higher visibility to employers",
      everythingInPlus: "Everything in Plus",
      mostPowerfulAi: "Most capable AI model",
      extendedAgentMemory: "Extended agent memory",
      doubleVisibility: "2\u00d7 visibility to employers",
      autopilotJobMatching: "Autopilot job matching",
      prioritySearchResults: "Priority in search results",
      moreHiringPages: "More hiring pages & job posts",
      aiCandidateRanking: "AI candidate ranking",
      teamAnalytics: "Team analytics",
      autopilotHiring: "Autopilot hiring",
      doubleJobVisibility: "2\u00d7 job post visibility",
      prioritySupport: "Priority support",
      // Paywall feature-table rows (UpgradeContent via upgrade-data.ts)
      unlimitedCvs: "Unlimited CVs",
      smarterAiWriting: "Guided AI writing and tailoring",
      coverLetters: "Job-specific cover letters",
      applicationPackets: "Complete application packets",
      unlimitedAutofill: "Unlimited SmartApply autofill",
      premiumSkills: "Premium and custom Skills",
      connectedApps: "Connect your apps (Gmail, Slack, Zapier)",
      betterJobMatching: "Better job matching",
      careerAgent: "Your career agent",
      novaAutomations: "Automation",
      interviewPrep: "Interview preparation",
      salaryInsights: "Salary intelligence",
      recruiterPreview: "Recruiter preview",
      sourcesAnalytics: "Views and sources analytics",
      hiringPages: "Hiring pages",
      jobPosts: "Job posts",
      privateContactVisibility: "Private contact visibility",
      recruiterOutreach: "Recruiter outreach",
      jdGenerator: "JD generator",
      bulkScreening: "Bulk screening",
      talentInsights: "Talent insights",
      careerFairQrAnalytics: "Career fair QR analytics",
    },
    // Individual paywall rows, authored to the pricing spec.
    rows: {
      everythingInFree: "Everything in Free",
      smarterAi: "Smarter AI ({{model}})",
      tailoredCvs: "Tailored CVs for every job",
      unlimitedAutofill: "SmartApply autofill",
      autofill_free: "10/month",
      autofill_plus: "Unlimited",
      coverLetters: "Cover letters and messages",
      skillsHub: "Install and run Agent Skills",
      seeWhoViewed: "See who viewed you",
      anyDownloadFormat: "Any download format",
      customBranding: "Custom branding",
      connectApps: "Connect your apps (Gmail, Zapier)",
      everythingInPlus: "Everything in Plus",
      findsJobs: "Finds jobs and preps your interviews",
      automations: "Automations (scheduled tasks)",
      extendedAgentMemory: "Extended agent memory",
      credits: "Credits",
      credits_free: "70/day",
      credits_plus: "10,000/mo",
      credits_pro: "100,000/mo",
      creditsDaily_free: "resets daily",
      creditsDaily_plus: "+ 300/day",
      creditsDaily_pro: "+ 3,000/day",
    },
    // Pro-tier Plus-vs-Pro comparison rows (text cells, not checkmarks)
    comparison: {
      topAi: "AI",
      // Plus highlights Gemini; Pro highlights Claude at the highest capacity.
      topAiFreeSubtitle: "Premium model",
      topAiPaidSubtitle: "Highest capacity",
      usage: "Usage",
      usageFree: "Standard",
      usagePaid: "Higher limits",
      visibility: "Visibility",
      visibilityFree: "Higher",
      visibilityPaid: "Max",
    },
    context: {
      allowanceRemaining:
        "{{remaining}} of {{limit}} included {{unit}} remaining",
      allowanceUsed: "All {{limit}} included {{unit}} used",
      resetsOn: "Resets {{date}}",
      // Why the paywall opened (fair use): one line above the plans.
      reason: {
        credits:
          "You've used today's free credits. They refresh tomorrow, or upgrade to keep going now. Your chat and drafts are saved.",
        image:
          "You've used today's free image. Another one comes tomorrow, or get more with {{plan}}.",
        research:
          "You've used today's free web searches. More come tomorrow, or get more with {{plan}}.",
        document:
          "You've used today's free files (documents, slides and pages). More come tomorrow, or get more with {{plan}}.",
        edit:
          "You've used today's free file edits. More come tomorrow, or get more with {{plan}}.",
        automations:
          "Automations run on a schedule with Pro. Your draft is saved.",
      },
      units: {
        reply: "replies",
        run: "runs",
        preview: "previews",
        report: "reports",
        generation: "generations",
        autofill: "autofills",
        action: "actions",
      },
      generic: {
        title: "Unlock this next step",
        subtitle:
          "Your work is saved. Choose the plan that includes this action and continue where you left off.",
      },
      chatLimit: {
        title: "Keep the conversation going",
        subtitle:
          "You've used today's free credits. Plus adds a 10,000-credit monthly pool for chat, CVs, and more.",
      },
      skillRun: {
        title: "Keep running skills",
        subtitle:
          "Your Skill selection is saved. You've used today's free credits. Plus adds a 10,000-credit monthly pool so skills keep running.",
      },
      studioGenerate: {
        title: "Generate this career asset",
        subtitle:
          "Your project is saved. Upgrade to create the selected report, letter, or application material.",
      },
      applicationAutofill: {
        title: "Keep autofilling applications",
        subtitle:
          "Plus and Pro include unlimited SmartApply autofill. Your application stays open so you can continue after upgrading.",
      },
      novaPreview: {
        title: "Create another Career agent plan",
        subtitle:
          "Your instructions are saved. Upgrade for more Career agent previews and continue with the same draft.",
      },
      novaActivate: {
        title: "Activate your Career agent automation",
        subtitle:
          "Your automation draft is saved. Pro activates scheduled runs while keeping approvals and safety controls in place.",
      },
      export: {
        title: "Export in every format",
        subtitle:
          "Plus adds editable DOCX, text, and Markdown exports while PDF stays available on Free.",
      },
      analytics: {
        title: "Understand what is working",
        subtitle:
          "Plus unlocks your complete visibility, source, and engagement analytics.",
      },
      branding: {
        title: "Make your profile unmistakably yours",
        subtitle:
          "Plus unlocks custom branding while your existing profile and content stay intact.",
      },
      connectors: {
        title: "Connect more of your workflow",
        subtitle:
          "Upgrade when you need more connected tools. You can always review and disconnect existing connections.",
      },
      voice: {
        title: "Keep using voice",
        subtitle:
          "You reached this month's included voice time. Upgrade for more voice conversations while text chat stays available.",
      },
      onboardingChoice: {
        title: "Choose how Tabbio should help",
        subtitle:
          "Start free, create stronger applications with Plus, or automate repetitive work with Pro.",
      },
      onboardingEnd: {
        title: "Your career workspace is ready",
        subtitle:
          "Continue free or unlock the tools matched to the goals and CV you just set up.",
      },
    },
  },

  // Voice (mic / push-to-talk / hands-free): Voice AI Epic SCRUM-96
  voice: {
    permission: {
      rationale:
        "Tabbio uses your microphone for voice mode: typing always works.",
      deniedToast: "Microphone access denied. You can still type to chat.",
      deniedTitle: "Microphone access needed",
      deniedSettings: "Enable the microphone in Settings to use voice.",
      openSettings: "Open Settings",
    },
    session: {
      startError: "Voice mode couldn't start. Try again or type your message.",
    },
    ptt: {
      hint: "Tap to talk, tap again to finish",
      recording: "Listening…",
      transcribing: "Transcribing…",
      cancel: "Release to send",
    },
    transcribe: {
      offlineTitle: "Voice unavailable offline",
      offlineMessage: "Type to chat: voice will come back when you're online.",
      errorTitle: "Couldn't transcribe",
      errorMessage: "Voice failed. Try again or type your message.",
    },
    cap: {
      title: "Voice minutes used",
      message: "You've used your monthly voice allowance. Upgrade for more.",
      remainingMinutes: "{{count}} min left this month",
      capLabel: "{{used}} of {{cap}} min",
      upgradeCta: "Upgrade to Premium",
    },
    handsfree: {
      title: "Voice mode",
      tapToStart: "Tap to start talking",
      tapToInterrupt: "Tap to interrupt",
      idle: "Hold to talk",
      ready: "Hi, I'm Nova. What's on your mind?",
      lookingUp: "Looking that up…",
      planLimit: "You've reached your plan's chat limit. Upgrade to keep talking.",
      thinking: "Thinking…",
      speaking: "Speaking…",
      // Web voice mode (components/voice/live): hands-free, with a status
      // capsule (status + what a tap does), captions and a keyboard legend.
      live: {
        ready: "Ready",
        starting: "Starting…",
        listening: "Listening…",
        micOff: "Mic off",
        paused: "Paused",
        send: "Tap to send",
        unmute: "Tap to unmute",
        resume: "Tap to resume",
        retry: "Tap to try again",
        continue: "Tap to keep talking",
        stuck: "No sound",
        stuckDetail: "Your browser didn't play Nova's voice.",
        saving: "Saving…",
        noMic: "No mic access",
        offline: "You're offline",
        planReached: "Chat limit reached",
        error: "Something went wrong",
        microphone: "Microphone",
        end: "End",
        captions: "Captions",
        notCaught: "Didn't catch that",
        noMetering: "Can't tell when you stop talking. Tap to send when you're done.",
        micBlocked: "Allow microphone access in your browser to talk to Nova.",
        slow: "This is taking longer than usual.",
        approval: "Nova needs your approval in the chat to go ahead.",
        continueInChat: "Continue in chat",
        interrupted: "Interrupted",
        failed: "Nova couldn't answer that",
        jumpToLatest: "Jump to latest",
        spaceKey: "Space",
        escKey: "Esc",
      },
    },
  },

  // Misc
  chatInputDisclaimer: "AI can make mistakes. Double-check important details.",
  assistantName: "Career agent",
  disclaimer:
    "Your Career agent is an AI assistant. It can make mistakes-check important information.",
  messagePlaceholder: "Ask Anything...",
  userAccount: "User Account",
  workspaceTypePersonal: "Individual account",
  workspaceTypeCompany: "Company",
  closeSidebar: "Close sidebar",
  openSidebar: "Open sidebar",
  tabbioForWeb: "Tabbio for Web",
  memoryFields: "fields",
  memoryEditHelp:
    "Structured JSON only. Save merges the edited fields into working memory.",

  taxonomy: {
    sectionTitle: "Skills",
    otherSkillsMentioned: "Other skills mentioned",
    confidence: {
      high: "High confidence match",
      medium: "Possible match",
      low: "Low confidence",
    },
    source: {
      alias: "Alias match",
      key: "Canonical match",
      family: "Family match",
      fuzzy: "Fuzzy match",
      none: "No canonical match",
    },
  },

  msgPrefs: {
    title: "Messaging",
    modes: {
      seeker: "Seeker mode",
      employer: "Employer · {{workspace}}",
    },
    seeker: {
      sectionTitle: "Who can message me",
      sectionHelp:
        "You're in Seeker mode. Anyone you message first can always reply, regardless of these settings.",
      candidate: {
        label: "Other job seekers",
        desc: "Anyone else looking for work on Tabbio",
      },
      company: {
        label: "Companies",
        desc: "In-house recruiters and hiring managers",
      },
      recruiter: {
        label: "Recruiters",
        desc: "Independent recruiters & talent partners",
      },
      founder: { label: "Founders", desc: "Founders & CEOs of small teams" },
      agency: { label: "Agencies", desc: "Staffing & placement agencies" },
      freelancer: { label: "Freelancers", desc: "Project-based contractors" },
    },
    employer: {
      sectionTitle: "Who can message this workspace",
      sectionHelp:
        "You're in Employer mode. Settings apply when people DM the workspace inbox.",
      master: {
        label: "Allow all platform users",
        desc: "Open inbox: anyone can DM the workspace",
      },
      applied: {
        label: "Candidates who applied",
        desc: "Default; keeps inbox tied to your pipeline",
      },
      cold: {
        label: "All candidates (cold DMs)",
        desc: "Open inbound sourcing from any candidate",
      },
      freelancers: {
        label: "Freelancers only",
        desc: "Project-based hires: independent contractors",
      },
      agency: {
        label: "Agencies",
        desc: "Staffing partners pitching candidates",
      },
      blockSpam: {
        sectionTitle: "Block profiling messages",
        sectionHelp:
          "Filter generic outreach where the sender clearly hasn't read your job listing.",
        label: "Auto-block likely-spam outreach",
        desc: '"We have candidates for you" pitches are filtered',
      },
    },
  },

  msgGated: {
    headline: "{{firstName}} only accepts messages from these roles",
    applyViaJob: "Apply for {{jobTitle}}",
    notifyWhenOpen: "Notify me when {{firstName}} opens DMs",
    explainer:
      "You're signed in as a Candidate, so direct messages aren't an option right now: but you can still get on {{firstName}}'s radar.",
  },

  msgSheet: {
    editProfile: "Edit your profile",
    muteNotifications: "Mute notifications",
    unmuteNotifications: "Unmute notifications",
    blockPerson: "Block {{name}}",
    report: "Report",
    menuLabel: "Conversation options",
    blockedTitle: "Blocked",
    blockedBody: "{{name}} can no longer message you.",
    blockFailedTitle: "Couldn't block",
    mutedTitle: "Muted",
    mutedBody: "Notifications are off for this conversation.",
    muteFailedTitle: "Couldn't mute",
    unmutedTitle: "Unmuted",
    unmutedBody: "Notifications are on for this conversation.",
    unmuteFailedTitle: "Couldn't unmute",
    reportedTitle: "Report received",
    reportedBody:
      "Thanks: your report is received. Track it in Settings under My reports.",
    reportFailedTitle: "Couldn't file report",
  },

  userReports: {
    title: "My reports",
    subtitle:
      "Track reports you submitted. We will also notify you when an outcome is available.",
    viewStatus: "View report status",
    loading: "Loading your reports...",
    loadFailed: "We couldn't load your reports.",
    retry: "Try again",
    emptyTitle: "No reports submitted",
    emptyBody: "Reports you submit about another user will appear here.",
    reference: "Reference {{id}}",
    status: {
      pending: {
        label: "Received",
        description:
          "Your report was submitted successfully and is waiting for review.",
      },
      reviewing: {
        label: "Under review",
        description: "Our safety team is reviewing the report.",
      },
      actioned: {
        label: "Action taken",
        description:
          "We reviewed the report and took action under our policies. Private enforcement details are not shared.",
      },
      dismissed: {
        label: "No violation found",
        description:
          "We reviewed the report and did not find a policy violation based on the available information.",
      },
    },
  },

  // Landing screens for the public CV overflow menu ("Open in Tabbio" /
  // "Report"), reached as universal links from <username>.tabbio.com.
  profileHandoff: {
    back: "Back",
    message: "Message",
    report: "Report",
    loading: "Loading profile...",
    notFoundTitle: "This profile is unavailable",
    notFoundBody: "It may have been made private or removed.",
  },

  reportProfile: {
    title: "Report {{name}}",
    subtitle: "Tell us what is wrong. Our team reviews every report.",
    signInRequired: "Sign in to file a report...",
    loading: "Loading...",
    notFoundTitle: "This profile is unavailable",
    notFoundBody: "We could not find the person you are reporting.",
    detailsPlaceholder: "Add any detail that helps us review (optional)",
    submit: "Submit report",
    done: "Done",
    categories: {
      spam: "Spam or scam",
      harassment: "Harassment or hate",
      impersonation: "Pretending to be someone else",
      inappropriate_content: "Inappropriate content",
      other: "Something else",
    },
  },

  roles: {
    candidate: "Job seeker",
    company: "Company",
    recruiter: "Recruiter",
    founder: "Founder",
    agency: "Agency",
    freelancer: "Freelancer",
    master: "Anyone",
    candidate_who_applied: "Candidates who applied",
    all_candidates: "All candidates",
    freelancers: "Freelancers",
  },

  profile: {
    editTitle: "Edit profile",
    savingLabel: "Saving…",
    savedTitle: "Saved",
    savedBody: "Your profile is up to date.",
    saveFailedTitle: "Couldn't save",
    photoPermissionTitle: "Photo permission needed",
    photoPermissionBody:
      "Allow Tabbio to read your photo library so you can pick an avatar.",
    photoUpdatedTitle: "Photo updated",
    photoUpdatedBody: "Your new avatar is live.",
    photoFailedTitle: "Couldn't update photo",
    fields: {
      name: "Name",
      headline: "Headline",
      summary: "Summary",
      location: "Location",
    },
  },
  // Subscription journeys (A-J, L). Copy lifted from the design reference;
  // see lib/subscription-journey/COPY.md for the screen -> key map.
  subscription: {
    tierFree: 'Free',
    providers: {
      appStore: 'App Store',
      googlePlay: 'Google Play',
      web: 'Web billing',
    },
    cycles: {
      label: 'Billing period',
      monthly: 'Monthly',
      yearly: 'Yearly',
    },
    perMonth: '/ month',
    actions: {
      back: 'Back',
      close: 'Close',
      continue: 'Continue',
      done: 'Done',
      notNow: 'Not now',
      keepMyPlan: 'Keep my plan',
      backToMyPlan: 'Back to my plan',
      continueCancellation: 'Continue cancellation',
    },
    legal: {
      restore: 'Restore',
      terms: 'Terms',
      privacy: 'Privacy',
    },
    paywall: {
      screenTitle: 'Upgrade',
      heading: 'Get Tabbio {{tier}}',
      subtitle: {
        plus: 'Do more with enhanced CV writing.',
        pro: 'Do more with your job-search assistant.',
      },
      tierGroupLabel: 'Plan tier',
      compareCaption: 'Compare Free and Tabbio {{tier}}',
      columns: {
        features: 'Features',
        free: 'Free',
      },
      rows: {
        cvWriting: 'CV writing',
        moreAi: 'More AI usage',
        higherLimits: 'Higher AI limits',
        nova: 'Nova, your job-search assistant',
        scheduled: 'Scheduled job searches',
        tools: 'Connect your tools',
        insights: 'Insights',
        customization: 'More customization',
      },
      details: {
        tools: {
          everyday: 'Your everyday tools',
          ai: 'AI connections',
          developer: 'Developer tools',
          note: 'Model access depends on your connected account.',
        },
        insights: 'See views, downloads and where visitors come from.',
        customization: 'Remove Tabbio branding, customize your link preview, and choose from more download formats.',
      },
      included: 'Included',
      notIncluded: 'Not included',
      upgradeFor: 'Upgrade for {{price}}',
      disclosureMonthly: 'Auto-renews monthly. Cancel anytime.',
      disclosureYearly: '{{price}} billed yearly. Renews until canceled.',
    },
    compare: {
      title: 'Compare plans',
      pricePerMonth: '{{price}} / month',
      backToChoice: 'Back to my choice',
      features: {
        free: {
          basicWriting: 'Basic CV writing',
          oneCv: 'One CV and your public link',
        },
        plus: {
          enhancedWriting: 'Enhanced CV writing with {{model}}',
          moreAi: 'More AI usage',
          connectTools: 'Connect your tools',
          insights: 'Insights',
          customization: 'More customization',
        },
        pro: {
          everythingInPlus: 'Everything in Plus',
          advancedWriting: 'Advanced CV writing with {{model}}',
          nova: 'Nova, your job-search agent',
          scheduled: 'Scheduled job searches',
          higherLimits: 'Higher AI limits',
        },
      },
    },
    checkout: {
      title: 'Review your plan',
      heading: 'One last look.',
      subtitle: 'You’ll confirm securely with your billing provider.',
      planRow: 'Tabbio {{tier}}',
      monthlySubscription: 'Monthly subscription',
      yearlySubscription: 'Yearly subscription',
      totalToday: 'Total today',
      renewsAutomatically: 'Renews automatically',
      everyMonth: 'Every month, unless canceled',
      everyYear: 'Every year, unless canceled',
      billedThrough: 'Billed through',
      accessNote: 'Your paid access starts after the purchase is confirmed.',
      fine: 'Manage or cancel renewal in your billing provider’s subscription settings.',
      continueTo: 'Continue to {{provider}}',
      restoreExisting: 'Restore an existing purchase',
      checking: 'We\'re checking your subscription.',
      delayed: 'Your payment may be complete. Let\'s check your access.',
      checkAgain: 'Check again',
      backToTabbio: 'Back to Tabbio',
      cancelled: 'No purchase was completed.',
      unchanged: 'Your plan hasn’t changed.',
      // Why a purchase could not start, one short reason each (the generic
      // "Something didn’t load" stays for everything else). Never "charged".
      errors: {
        unavailable: {
          title: 'This plan isn’t available right now.',
          body: 'The store couldn’t offer it just now. Your plan hasn’t changed; try again in a moment.',
        },
        network: {
          title: 'You seem to be offline.',
          body: 'Check your connection and try again. Your plan hasn’t changed.',
        },
        store: {
          title: '{{provider}} couldn’t complete the purchase.',
          body: 'Your plan hasn’t changed. Try again, or check your payment method in {{provider}}.',
        },
        notAllowed: {
          title: 'Purchases aren’t allowed on this device.',
          body: 'Check Screen Time or purchase restrictions, then try again.',
        },
        signIn: {
          title: 'Sign in to continue.',
          body: 'Your session ended before the purchase started. Sign in again and try once more.',
        },
      },
    },
    welcome: {
      title: 'You’re all set',
      heading: 'Welcome to {{tier}}.',
      subtitle: 'Let’s make your next step a good one.',
      cardTitle: 'Start with a stronger CV',
      cardBody: 'Nova can suggest a clearer introduction. You decide what to keep.',
      improveCv: 'Improve my CV',
      improvePrompt: 'Improve my CV introduction',
    },
    billing: {
      title: 'Plan & billing',
      tagline: {
        pro: 'Advanced writing + Nova',
        plus: 'Enhanced CV writing',
        free: 'Essential CV writing',
      },
      pricePerMonth: '{{price}} / month',
      pricePerYear: '{{price}} / year',
      renews: 'Renews {{date}}',
      accessUntil: 'Access until {{date}}',
      pauseStarts: 'Pause starts {{date}}',
      accessUnknown: 'Check when your access ends',
      metrics: {
        label: 'Your activity',
        applicationsSent: 'Applications sent',
        cvsTailored: 'CVs tailored',
        profileViews: 'Profile views',
      },
      improveCv: 'Improve my CV',
      improveCvSub: 'Make your next application stronger',
      viewProfile: 'View my profile',
      viewProfileSub: 'See what recruiters see',
      planDetails: 'Plan details',
      planPricePerMonth: '{{price}} per month',
      planPricePerYear: '{{price}} per year',
      change: 'Change',
      nextPayment: 'Next payment',
      accessUntilLabel: 'Access until',
      pauseStartsLabel: 'Pause starts',
      dateAndPrice: '{{date}} · {{price}}',
      manageSubscription: 'Manage subscription',
      cancelSubscription: 'Cancel subscription',
      turnRenewalOn: 'Turn renewal back on',
      renewalOff: 'Renewal is off',
      viewScheduledPause: 'View scheduled pause',
      billingHelp: 'Billing help',
      receipts: 'Billing history & receipts',
      restorePurchases: 'Restore purchases',
      refreshSubscription: 'Refresh subscription',
      requestRefund: 'Request a refund',
    },
    info: {
      receipts: {
        title: 'Billing history & receipts',
        body: 'Your receipts are held by {{provider}}, where this subscription was purchased.',
      },
      refund: {
        title: 'Request a refund',
        body: 'Refund requests are reviewed by {{provider}}. Eligibility and timing follow that provider’s rules.',
        note: 'A refund request is separate from canceling renewal.',
      },
      nextPayment: {
        title: 'Your next payment',
        body: 'The provider is the source of truth for the amount, tax, currency and renewal date.',
      },
      provider: {
        body: 'Billing ownership follows the original purchase, including when you switch devices.',
      },
      terms: {
        title: 'Subscription terms',
        body: 'Paid plans renew automatically unless canceled with your provider. The final amount and date must come from the provider’s confirmation screen.',
      },
      profile: {
        title: 'Your public profile',
        body: 'Your single public link stays the same across plans.',
        copy: 'Copy link',
        copied: 'Copied',
      },
      cv: {
        title: 'Your saved CV',
        body: 'Saved in your account. Canceling renewal does not delete it.',
      },
      free: {
        title: 'Continue on Free',
        body: 'Your CV and public link are available.',
      },
    },
    cancel: {
      reasons: {
        title: 'Cancel subscription',
        heading: 'What made you decide to leave?',
        groupLabel: 'Reason for leaving',
        options: {
          found_job: 'I found a job',
          later: 'I’m taking a break',
          price: 'It costs too much',
          value: 'It didn’t meet my expectations',
          confusing: 'I’m not sure how to use it',
          broken: 'I’m having technical issues',
          other: 'I need something else',
        },
      },
      details: {
        value: {
          cv: 'CV writing',
          matches: 'Job matches',
          results: 'Application results',
          other: 'Something else',
        },
        confusing: {
          cv: 'Improve my CV',
          matches: 'Find jobs',
          nova: 'Use Nova',
        },
        broken: {
          crash: 'App froze or crashed',
          loading: 'Something wouldn’t load',
          failed: 'An action failed',
          other: 'Something else',
        },
        other: {
          missing: 'A missing feature',
          switching: 'Another tool',
          other: 'Another reason',
        },
      },
      help: {
        title: 'Before you go',
        titles: {
          foundJob: 'Congratulations on the new job!',
          value: 'What fell short?',
          confusing: 'What were you trying to do?',
          broken: 'What went wrong?',
          price: 'Find a plan that fits.',
          other: 'What’s missing?',
        },
        career: {
          heading: 'How did Tabbio help?',
          groupLabel: 'What helped',
          tellMore: 'Tell us a little more',
          placeholder: 'One sentence is enough',
          note: 'Your feedback helps us improve Tabbio.',
        },
        priceInfo: 'Your annual billing stays unchanged. You can compare plans before deciding.',
        comparePlans: 'Compare plans',
        prompts: {
          missing: 'What would you like Tabbio to do?',
          switching: 'What does the other tool do better?',
          broken: 'What happened, and what were you trying to do?',
          confusing: 'Where did you get stuck?',
          value: 'What were you hoping for?',
          default: 'What would you like us to know?',
        },
        placeholder: 'A few words are enough…',
        note: 'Your feedback helps us decide what to improve next.',
        feedbackReceived: 'Thanks. Your feedback was received.',
        feedbackFailed: 'Feedback wasn’t sent. Your draft is saved; you can still continue.',
      },
      career: {
        title: 'Keep Tabbio with you',
        heading: 'Your new job is only the beginning.',
        subtitle: 'Keep Tabbio ready for your next move.',
        features: {
          current: 'Keep your CV and LinkedIn profile current',
          identity: 'Improve your professional identity',
          opportunities: 'Find better opportunities when you’re ready',
        },
        fine: 'Nova can keep helping in the background, even after you start your new role.',
        attribution: {
          cv: 'CV',
          jobboard: 'Job board',
          recruiter: 'Employer',
          agent: 'Agent',
          elsewhere: 'Not from Tabbio',
          other: 'Other',
        },
      },
      pause: {
        title: 'Take a break',
        unavailable: {
          heading: 'You can turn off renewal.',
          body: 'Pausing isn’t available for this {{provider}} subscription. You can keep your benefits until {{date}} by turning off renewal.',
          bodyNoDate: 'Pausing isn’t available for this {{provider}} subscription. You can keep your benefits until your plan ends by turning off renewal.',
          info: 'Your CV and public link stay available on Free.',
        },
        noThanks: 'No thanks, continue',
        heading: 'A break, not goodbye.',
        subtitle: 'Pause your plan and come back when you’re ready.',
        lengthLabel: 'Pause length',
        months: {
          one: '1 month',
          two: '2 months',
          three: '3 months',
        },
        timelineRow: '{{date}} · {{title}}',
        starts: 'Pause starts',
        startsBody: 'Paid tools pause. Your CV and link stay.',
        reminder: 'A reminder',
        reminderBody: 'Three days before your plan resumes.',
        resumes: 'Your plan resumes',
        resumesBody: '{{price}} / month. Cancel anytime in Google Play.',
        review: 'Review pause in Google Play',
        scheduled: {
          title: 'Your pause',
          heading: 'Your break is scheduled.',
          body: 'Google Play confirmed the pause. Your current benefits stay until it starts.',
          startsLabel: 'Pause starts',
          resumesOn: 'Resumes on {{date}}',
          thenPrice: 'Then {{price}} per month. Manage or cancel in Google Play.',
          info: 'Your saved CV and public link stay available during the break.',
        },
      },
      confirm: {
        title: 'Cancel subscription',
        heading: 'Here’s what happens.',
        subtitle: 'Turn off renewal. Keep what you’ve made.',
        successHeading: 'Congrats on the new role.',
        successSubtitle: 'We’ll keep your CV and public link ready whenever you need them again.',
        benefitsStayUntil: '{{tier}} benefits stay until',
        accessUnknown: 'Check when your access ends',
        staysTitle: 'Stays with you on Free',
        stays: {
          savedCv: 'Your saved CV',
          publicProfile: 'Your public profile and link',
        },
        stopsAfter: 'Stops after {{date}}',
        stopsLater: 'Stops when your plan ends',
        stops: {
          paidAi: 'Paid AI tools and higher limits',
          premium: 'Premium plan features',
        },
        fine: 'You’ll finish in {{provider}}. No change is made until you confirm there.',
        continueToCancel: 'Continue to cancel',
      },
      provider: {
        title: 'Manage subscription',
        heading: 'Managed in {{provider}}',
        subtitle: 'Your subscription belongs to the provider you paid through.',
        pendingHeading: 'Let’s check your plan.',
        pendingSubtitle: 'We haven’t received a confirmed change yet. Your current access stays as it is.',
        steps: {
          open: 'Open {{provider}}',
          find: 'Find Tabbio in subscriptions',
        },
        intents: {
          pause: 'Pause this subscription',
          renew: 'Turn renewal back on',
          manage: 'Review your subscription',
          cancel: 'Turn off renewal',
        },
        pendingInfo: 'Still pending. No change has been confirmed.',
        returnInfo: 'Returning here alone doesn’t confirm a change. We check the subscription status first.',
        checkAgain: 'Check again',
      },
    },
    renewalOff: {
      title: 'Your subscription',
      heading: 'Renewal is off.',
      subtitle: 'No more automatic payments. Make the most of the time you have left.',
      benefitsEnd: 'Your {{tier}} benefits end',
      info: 'After that, your saved CV and public link stay available on Free.',
      keepUsing: 'Keep using my benefits',
      turnRenewalOn: 'Turn renewal back on',
    },
    lapsed: {
      title: 'Your plan',
      heading: 'You’re on Free now.',
      subtitle: 'Your CV and public link are right where you left them.',
      ended: '{{tier}} ended on {{date}}',
      novaNote: 'Nova will be here when you need a hand.',
      savedCv: 'My saved CV',
      stillYours: 'Still yours',
      publicProfile: 'My public profile',
      readyToShare: 'Ready to share',
      explorePlans: 'Explore plans',
      continueFree: 'Continue with Free',
    },
    states: {
      screenTitle: 'Your subscription',
      loading: 'Loading',
      loadingLabel: 'Loading screen',
      error: {
        title: 'Something didn’t load.',
        body: 'Your plan hasn’t changed. Try again when you’re ready.',
      },
      offline: {
        title: 'You’re offline.',
        body: 'Connect to check your latest plan. Nothing changes while you’re offline.',
      },
      unavailable: {
        title: 'This option isn’t available.',
        body: 'Your provider couldn’t offer this right now. Your current plan is unchanged.',
      },
      pending: {
        title: 'Waiting for confirmation.',
        body: 'The provider hasn’t confirmed the change yet. Your current plan stays active.',
      },
      checkStatus: 'Check status',
      tryAgain: 'Try again',
    },
  },
  // Audit E03: what a submission sent, and what a share link shares.
  submissionReceipts: {
    candidateTitle: 'What you sent',
    employerTitle: 'Submission receipts',
    sentCount_one: 'Sent once',
    sentCount_other: 'Sent {{count}} times',
    recipient: 'Sent to',
    fields: {
      cv: 'CV',
      language: 'Language',
      sentAt: 'Sent',
      source: 'Sent from',
      file: 'File',
    },
    heading: {
      original: 'Original submission',
      updated: 'Updated submission',
    },
    current: 'Current',
    cv: {
      none: 'No CV attached',
      notRecorded: 'Not recorded',
    },
    language: {
      en: 'English',
      ar: 'Arabic',
      notRecorded: 'Not recorded',
    },
    source: {
      app: 'Tabbio app',
      companyPage: 'Company page on Tabbio',
      publicApply: 'Public apply form',
      other: 'Other',
    },
    historical: {
      title: 'Sent before receipts were recorded',
      body: 'This was sent before Tabbio kept submission receipts, so its exact details were not recorded.',
    },
    employerSummaryNote:
      'Earlier submissions are listed as summaries. Only the current CV is shared with you.',
  },
  shareLinkDetails: {
    title: 'Link details',
    fields: {
      cv: 'CV',
      access: 'Who can open it',
      contact: 'Phone and email',
      download: 'Downloads',
      expiry: 'Expiry',
      created: 'Created',
      opened: 'Opened',
    },
    mainCv: 'Main CV',
    access: {
      anyone: 'Anyone with the link',
    },
    contact: {
      included: 'Included',
      partial: 'Partly included',
      hidden: 'Hidden',
    },
    download: {
      allowed: 'Allowed',
      off: 'Not allowed',
    },
    expiry: {
      never: 'Never',
      expired: 'Expired {{date}}',
    },
    opened: {
      never: 'Not yet',
      count_one: 'Once',
      count_other: '{{count}} times',
    },
  },
} as const;
